/**
 * Dev mode: skip API calls and simulate a 30s preview timer instead.
 * Toggle via browser console: CustomAlertButton.devMode = true/false
 */
const STARTALES_DEV_MODE = localStorage.getItem('startales_dev_mode') === 'true';

/**
 * Master switch for the whole character session-save construct
 * (localStorage persistence + customer sync + restore-on-open).
 * Set to `false` to fully disable saving AND restoring characters;
 * set to `true` to re-enable. This is the single toggle for the feature.
 */
const STARTALES_SESSION_SAVE_ENABLED = false;

/**
 * Master switch for the binding step (5) — the Hardcover/Softcover cover choice.
 * Set to `false` to skip the step entirely and force Hardcover; set to `true`
 * to let the customer pick. This is the single toggle for the feature.
 * (Scene generation, step 4, is disabled separately via `sceneGenDisabled`.)
 */
const STARTALES_BINDING_ENABLED = true;

/**
 * Master switch for the outfit step between photo upload (2) and character
 * generation (3): the customer picks the book's predefined outfit (included)
 * or the clothes from their own photo (paid add-on). `false` skips the step and
 * always generates the book outfit.
 */
const STARTALES_OUTFIT_ENABLED = true;

/**
 * Internal number of the outfit step. Deliberately between 2 and 3 instead of
 * renumbering every later step - those numbers are part of the tracking
 * history (funnel_step) and of the navigation special cases below.
 */
const STEP_OUTFIT = 2.5;

/**
 * Add-on line for "Outfit vom Foto" (Shopify product `outfit-vom-foto`, see
 * snippets/button.liquid). Fallback price only applies while the product is
 * missing; the real price always comes from Liquid.
 */
const OUTFIT_PHOTO_PRICE_FALLBACK = 1.99;

/** Book outfit image used when the product has no custom.charakter_bild. */
const OUTFIT_IMAGE_FALLBACK = 'https://cdn.shopify.com/s/files/1/1018/1073/3381/files/preview_1782333813434_du6dxkc_1.png?v=1782507925&width=600';

/**
 * Text, falls die API PHOTO_OUTFIT_REJECTED ohne `error` liefert. Normalerweise
 * kommt die Meldung von der API.
 */
const OUTFIT_REJECTED_FALLBACK = 'Die Kleidung auf diesem Foto können wir leider nicht ins Buch übernehmen. Bitte lade ein anderes Foto hoch oder wähle das Outfit aus dem Buch.';

/**
 * Beschriftung der Trigger-Buttons (Produktseite + Sticky), sobald ein
 * angefangener Durchlauf im Speicher liegt. Der Kunde hat den Flow nur
 * geschlossen, nicht verworfen - "Jetzt personalisieren" waere dann falsch.
 */
const STARTALES_RESUME_LABEL = 'Bestellung abschließen';

/**
 * Gespeicherter Durchlauf (Schritt, Angaben, fertiger Charakter, Extras) je Buch
 * in localStorage - uebersteht Neuladen, App-Wechsel und Tab-Kill. Das Foto des
 * Kindes wird bewusst nicht gespeichert (siehe _persistFlow).
 */
const STARTALES_FLOW_KEY_PREFIX = 'startales_flow_v1_';
const STARTALES_FLOW_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Die Instanz, die gerade einen angefangenen Durchlauf haelt. Produktseite und
 * Sticky-Leiste rendern je ein eigenes <custom-alert-button>-Element mit
 * eigenem State; ohne diesen Verweis wuerde ein Klick auf den jeweils anderen
 * Button bei Schritt 1 neu anfangen. Der Verweis lebt nur im Speicher der
 * Seite - ein Reload startet den Flow bewusst frisch.
 * @type {any}
 */
let _stFlowOwner = null;

/**
 * Setzt die Beschriftung ALLER Trigger-Buttons der Seite. Der Originaltext
 * kommt aus Liquid und wird beim ersten Umschalten am Element gesichert.
 * @param {boolean} inProgress
 */
function _stSyncTriggerLabels(inProgress) {
  document.querySelectorAll('custom-alert-button .custom-alert-button__text').forEach((el) => {
    const span = /** @type {HTMLElement} */ (el);
    if (!span.dataset['originalText']) span.dataset['originalText'] = span.textContent || '';
    span.textContent = inProgress
      ? STARTALES_RESUME_LABEL
      : (span.dataset['originalText'] || span.textContent || '');
  });
}

/**
 * Loest auf, sobald die Seite sichtbar ist (sofort, wenn sie es schon ist).
 * @returns {Promise<void>}
 */
function _stWhenVisible() {
  if (document.visibilityState !== 'hidden') return Promise.resolve();
  return new Promise((resolve) => {
    const onChange = () => {
      if (document.visibilityState === 'hidden') return;
      document.removeEventListener('visibilitychange', onChange);
      resolve();
    };
    document.addEventListener('visibilitychange', onChange);
  });
}

/**
 * Elemente mit data-st-open-personalizer ausserhalb des Buttons (Schritt 1 in
 * sections/process-steps.liquid) oeffnen denselben Flow wie der Haupt-CTA. In
 * Clarity waren sie tote Klicks: die Grafik wirbt fuer den Upload, tat aber
 * nichts. Einmal pro Seite und delegiert, damit auch ein Section-Re-Render im
 * Theme-Editor greift. Das data-Attribut am <html> schaltet erst jetzt den
 * Zeiger-Cursor frei - ohne geladenes Script bleibt die Grafik ehrlich statisch.
 */
function _stWireExternalTriggers() {
  const root = document.documentElement;
  if (root.dataset['stPersonalizer'] === 'ready') return;
  root.dataset['stPersonalizer'] = 'ready';
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-st-open-personalizer]') : null;
    if (!target) return;
    /** @type {any} */
    const owner = (_stFlowOwner && _stFlowOwner.isConnected)
      ? _stFlowOwner
      : (document.querySelector('#personalize-button-original custom-alert-button') || document.querySelector('custom-alert-button'));
    if (!owner) return;
    owner.handleClick(event);
  });
}

/**
 * Uebergabe an ein anderes Buch, wenn das Geschlecht nicht zum aktuellen passt
 * (Schritt 1, siehe _genderHintHtml). Name, Geschlecht und Augenfarbe reisen
 * per sessionStorage mit, das Ziel-Buch oeffnet sein Overlay damit sofort.
 */
const STARTALES_HANDOFF_KEY = 'startales_handoff';
const STARTALES_HANDOFF_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * Produktseite und Sticky-Leiste verbinden je ein <custom-alert-button>; die
 * Uebergabe darf nur eines davon oeffnen.
 */
let _stHandoffChecked = false;

/** Die Bundle-Auswahl der Produktseite meldet ihre Aenderungen nur einmal. */
let _stBundlePickWired = false;

const STARTALES_API_BASE = 'https://api.star-tales.com';
// Fuer snippets/funnel-tracking.liquid: das Snippet schickt dieselben Funnel-
// Events an die eigene API und soll die Basis-URL nicht ein zweites Mal
// hartkodieren muessen.
window.STARTALES_API_BASE = STARTALES_API_BASE;

// Die Einband-Vorschauen laufen in eine 108-px-Box. Ohne width-Parameter liefert
// die Shopify-CDN das Original (347 KB / 182 KB) - mit width=324 (3x fuer Retina)
// sind es 11 KB / 8 KB bei identischer Darstellung.
// Der Foto-Rahmen ist ~150 px breit, die Vorschaukarte in Schritt 3 ~300 px.
// 900 px deckt beides bis DPR 3 ab und ist in Millisekunden dekodiert.
const PHOTO_DISPLAY_MAX_PX = 900;

const HARDCOVER_IMAGE_FALLBACK = 'https://cdn.shopify.com/s/files/1/1018/1073/3381/files/bild-EDQYE3.jpg?v=1791975652&width=324';
const SOFTCOVER_IMAGE_FALLBACK = 'https://cdn.shopify.com/s/files/1/1018/1073/3381/files/bild-XRQFVR.jpg?v=1791975651&width=324';

// Hard ceiling for the mascot typewriter. Long bubbles would otherwise run
// 85 chars * 28 ms = 2.4 s before a single slow frame is counted; capping the
// total keeps the copy readable on slow devices instead of dribbling in.
const TYPEWRITER_MAX_MS = 1500;

// Luft zwischen Zielelement und Kante des Scroll-Containers, damit ein
// angesprungener Block nicht bündig an der Oberkante klebt.
const ST_SCROLL_GAP = 12;

function _stGetSessionId() {
  try {
    let id = sessionStorage.getItem('startales_session_id');
    if (!id) {
      id = `st_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem('startales_session_id', id);
    }
    return id;
  } catch {
    return `st_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

function _stGetShop() {
  try {
    const w = /** @type {any} */ (window);
    return w.Shopify?.shop || w.STARTALES_SHOP || 'startales';
  } catch {
    return 'startales';
  }
}

/**
 * Broadcasts a funnel event as a DOM event; listeners subscribe without this
 * file having to know about them (see snippets/funnel-tracking.liquid, which
 * maps a subset onto GA4).
 *
 * @param {string} event
 * @param {Record<string, any>} [props]
 */
function _stTrack(event, props = {}) {
  try {
    document.dispatchEvent(new CustomEvent('startales:track', { detail: { event, props } }));
  } catch {
    /* never throw from analytics */
  }
}

/**
 * Escapes merchant-authored copy (product titles, theme-editor text) for innerHTML.
 *
 * @param {string} text
 * @returns {string}
 */
function _stEscape(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Escapes merchant-authored copy for innerHTML and turns *stars* into bold —
 * the review quote is editable in the theme editor, so no raw HTML gets through.
 *
 * @param {string} text
 * @returns {string}
 */
function _stEmphasis(text) {
  return _stEscape(text).replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
}

// Reused inline SVG for the Star Tales mascot (star with face). The body follows
// the book color via --st-orange; the attribute fill is the fallback.
const STAR_MASCOT_SVG = `<svg width="42" height="42" viewBox="0 0 2256 2373" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M711.993 241.608L711.28 243.529C658.876 408.357 617.224 572.751 600.988 742.098C594.838 806.252 570.643 846.606 511.813 877.29C415.38 927.589 324.843 988.944 236.254 1052.51C170.886 1099.42 106.207 1146.97 52.6343 1208.05C-21.3009 1292.35 -17.2516 1386.06 64.1999 1462.16C118.205 1512.62 183.111 1545.06 249.131 1575.43C337.484 1616.07 428.225 1651.04 519.781 1683.71C544.034 1692.37 555.014 1704.83 553.896 1731.71C550.824 1805.62 555.016 1879.49 562.215 1953.03C571.937 2052.34 583.909 2151.49 620.232 2245.49C670.565 2375.75 760.38 2406.21 882.407 2336.44C966.931 2288.12 1034.16 2219.73 1101.44 2151.24C1196.07 2054.91 1280.3 1949.5 1366.1 1845.54C1387.42 1819.7 1408.25 1809.95 1442.16 1817.88C1601.02 1855.04 1760.85 1887.43 1923.98 1899.55C1999.06 1905.13 2074.48 1909.65 2147.76 1886.54C2219.97 1863.77 2254.89 1813.98 2255.97 1738.8C2256.72 1687.06 2241.69 1638.98 2224.01 1591.64C2177.4 1466.83 2105.6 1355.56 2035.08 1243.65C2025.1 1227.82 2017.07 1212.66 2009.28 1189.86C2001.5 1167.06 2005.68 1108.56 2031.98 1056.84C2058.28 1005.11 2082.52 963.49 2106.32 916.578C2149.11 832.216 2192.62 748.161 2212.54 654.361C2237.11 538.677 2196.19 474.597 2081.41 451.255C2052.13 445.301 2022.71 442.648 1992.71 442.276C1826.84 440.221 1665.61 472.59 1504.92 507.896C1475.39 514.386 1459.29 505.923 1443.32 482.859C1383.5 396.433 1319.67 312.925 1250.44 233.831C1183.78 157.675 1116.18 81.6811 1027.29 30.1647C930.182 -26.1177 838.961 -2.60137 779.382 92.0244C750.538 137.835 731.766 188.376 711.993 241.608Z" fill="#FF6620" style="fill: var(--st-orange, #FF6620)"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M1307.71 1101.58C1352.02 1066.84 1391.31 1051.7 1424.69 1062.24C1458.1 1072.79 1481.31 1107.7 1497.08 1161.59L1498.96 1168.26C1537.2 1308.58 1491.53 1466.57 1388.75 1539.23C1309.4 1595.33 1208.45 1598.61 1128.15 1543.59C1046.34 1487.53 986.552 1413.21 964.579 1313.25C958.608 1286.09 958.573 1264.28 968.055 1245.45C977.457 1226.77 995.423 1212.63 1021.45 1198.81L1022.66 1198.18L1024.02 1197.95C1056.98 1192.54 1088.31 1185.51 1119.57 1183.36C1191.68 1178.41 1251.53 1145.63 1307.71 1101.58ZM1395.8 1318.92C1318.04 1314.86 1262.43 1352.13 1220.56 1413.46C1204.76 1436.6 1182.31 1464.49 1194.42 1490.63C1208.3 1520.61 1244.67 1507.3 1278.65 1508.02C1358.71 1490.43 1398.46 1433.29 1424.19 1361.38C1434.53 1332.46 1425.12 1320.46 1395.8 1318.92Z" fill="#151515"/>
  <path d="M1446.23 757.477C1507.62 723.785 1575.29 738.016 1632.36 794.022C1637.24 798.81 1641.77 804.25 1647.71 810.932L1647.79 811.022L1647.86 811.112C1665.52 832.113 1674.23 854.142 1674.05 879.282C1673.91 898.983 1667.06 919.132 1645.42 928.273C1624.79 936.988 1602.75 934.465 1585.92 917.092C1579.82 910.796 1574.25 904.092 1568.92 897.571C1563.53 890.973 1558.42 884.614 1552.97 878.572C1537.81 861.762 1519.28 855.977 1498.23 862.184C1480.41 867.437 1473.28 882.476 1469.51 903.063C1466.91 917.278 1464.18 930.808 1462.93 944.465C1461.83 956.502 1459.16 968.656 1452.14 977.884C1444.7 987.665 1433.37 992.935 1417.93 992.934C1402.95 992.932 1390.83 988.197 1381.69 979.549C1372.73 971.064 1367.29 959.438 1364.24 946.709C1346.69 873.621 1381.84 792.817 1446.23 757.477Z" fill="#151515"/>
  <path d="M710.016 1028.08C764.558 983.566 837.995 984.756 895.128 1032.15C912.157 1046.28 929.277 1063.05 941.222 1082.48C949.014 1095.16 956.433 1110.25 957.226 1125.6C958.064 1141.81 951.452 1157.26 933.649 1169.49C917.214 1180.77 901.463 1182.52 886.885 1176.41C873.302 1170.71 861.955 1158.67 852.159 1145.04C841.506 1133.89 832.714 1124.1 822.788 1117.95C813.471 1112.17 803.469 1109.85 790.135 1114.33C775.275 1119.33 767.455 1128.12 762.704 1139.04C757.698 1150.55 756.084 1164.4 754.43 1179.66C753.862 1184.9 753.813 1190.31 753.866 1196.1C753.917 1201.75 754.07 1207.94 753.792 1214.05C753.243 1226.11 750.089 1237.32 743.363 1246.24C736.535 1255.3 726.554 1261.31 713.804 1263.74C700.552 1266.26 689.119 1263.61 679.632 1257.34C670.396 1251.24 663.488 1242.05 658.226 1232.16C642.386 1202.41 641.529 1162.73 651.117 1125.45C660.733 1088.06 681.221 1051.58 710.016 1028.08Z" fill="#151515"/>
</svg>`;

const ICON_BACK = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_CLOSE = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
const ICON_CAMERA = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M4 7h3l2-2h6l2 2h3a1 1 0 011 1v11a1 1 0 01-1 1H4a1 1 0 01-1-1V8a1 1 0 011-1z" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="13" r="4" stroke="currentColor" stroke-width="1.8"/></svg>`;
const ICON_CHEVRON_RIGHT = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
const ICON_ARROW_RIGHT = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M5 12h13M13 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_SHIELD_CHECK = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 3l8 3v5c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-3z" stroke="currentColor" stroke-width="1.8"/><path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_CHECK_SM = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M5 12l4 4 10-10" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_CHECK_WHITE = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 12l4 4 10-10" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_CHECK_BADGE = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M5 12l4 4 10-10" stroke="#fff" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_X_BADGE = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#fff" stroke-width="3.4" stroke-linecap="round"/></svg>`;
const ICON_CART = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 7h13l-1.5 9H8L6 7zm0 0L5 4H3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="20" r="1.5" fill="currentColor"/><circle cx="17" cy="20" r="1.5" fill="currentColor"/></svg>`;
const ICON_SHIELD_TRUST = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 3l8 3v5c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-3z" stroke="currentColor" stroke-width="1.8" fill="none"/><path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_REFRESH = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 3v5h-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 21v-5h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_UPLOAD_ARROW = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 16V4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><path d="M7 9l5-5 5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

// --- Step 7 (review) trust icons -------------------------------------------
/**
 * Filled star; `size` keeps the social-proof strip (17px) and review card (13px) in sync.
 * @param {number} size
 */
const ICON_STAR = (size) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.9 6.3 6.8.8-5 4.7 1.3 6.8L12 17.8 5.9 21l1.3-6.8-5-4.7 6.8-.8z"/></svg>`;
/** @param {number} size */
const ICON_STARS = (size) => ICON_STAR(size).repeat(5);
const ICON_CALENDAR_CHECK = `<svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="17" rx="3"/><path d="M3 9h18M8 2v4M16 2v4"/><path d="M8.5 14.5l2 2 4-4.5"/></svg>`;
const ICON_TRUCK = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 3h13v10H1zM14 8h4l3 3v2h-7z"/><circle cx="5.5" cy="16.5" r="1.8"/><circle cx="17.5" cy="16.5" r="1.8"/></svg>`;
const ICON_SHIELD_PILLAR = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2l7 3v6c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V5z"/><path d="M9 12l2 2 4-4.5"/></svg>`;
/**
 * Padlock, sized by the caller (pillar 24px, payment row 15px, footer note 13px).
 * @param {number} size
 */
const ICON_LOCK = (size) => `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2.5"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>`;
/**
 * Geschenkpaket für die Extras-Überschrift, Größe vom Aufrufer.
 * @param {number} size
 */
const ICON_TRASH = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16"/><path d="M9.5 7V5.2A1.2 1.2 0 0 1 10.7 4h2.6a1.2 1.2 0 0 1 1.2 1.2V7"/><path d="M6.5 7l.9 12a1.8 1.8 0 0 0 1.8 1.7h5.6a1.8 1.8 0 0 0 1.8-1.7l.9-12"/><path d="M10.5 11v6M13.5 11v6"/></svg>`;
const ICON_HEART = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.8 5.1a5.2 5.2 0 0 0-7.4 0L12 6.5l-1.4-1.4a5.2 5.2 0 1 0-7.4 7.4L12 21l8.8-8.5a5.2 5.2 0 0 0 0-7.4z"/></svg>`;
const ICON_BOOK = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 5.5A1.5 1.5 0 0 1 4.5 4H9a3 3 0 0 1 3 3 3 3 0 0 1 3-3h4.5A1.5 1.5 0 0 1 21 5.5v12a1.5 1.5 0 0 1-1.5 1.5H15a3 3 0 0 0-3 3 3 3 0 0 0-3-3H4.5A1.5 1.5 0 0 1 3 17.5z"/><path d="M12 7v14"/></svg>`;
const ICON_INFO = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 7.6v.1"/></svg>`;

// --- Star burst -------------------------------------------------------------
// Die Aussenkontur des Star-Tales-Logos, direkt aus dem Maskottchen gezogen, damit
// es nur eine Quelle fuer die Form gibt. Die Partikel sind damit unsere Marke und
// nicht irgendein Konfetti.
const STAR_SILHOUETTE_D = (STAR_MASCOT_SVG.match(/<path d="([^"]+)"/) || [])[1] || '';

// Das Maskottchen steht auf sechs Screens. Sechs Kopien der vier langen Pfade
// sind ~25 KB - fast die Haelfte des gesamten Overlay-Markups, das beim Oeffnen
// in einem Rutsch geparst wird. Also genauso wie beim Logo (siehe
// snippets/logo-svg-sprite.liquid): einmal als <symbol>, sechsmal als <use>.
const STAR_MASCOT_SYMBOL = STAR_MASCOT_SVG
  .replace(/^<svg[^>]*>/, '<symbol id="st-mascot-star" viewBox="0 0 2256 2373" fill="none">')
  .replace(/<\/svg>\s*$/, '</symbol>');

const STAR_MASCOT_SPRITE = `<svg width="0" height="0" aria-hidden="true" focusable="false" style="position:absolute;width:0;height:0;overflow:hidden">${STAR_MASCOT_SYMBOL}</svg>`;

const STAR_MASCOT_USE = `<svg width="42" height="42" viewBox="0 0 2256 2373" fill="none" aria-hidden="true" focusable="false"><use href="#st-mascot-star"/></svg>`;
const STAR_BURST_COLORS = ['#FF6620', '#FFB020', '#FFD37E', '#FFFFFF'];
/** @type {Map<string, HTMLCanvasElement>} */
const _starSpriteCache = new Map();

/**
 * Rasterisiert den Logo-Stern einmal pro Farbe. Ein fertiges Bitmap pro Frame zu
 * blitten ist deutlich billiger, als den komplexen Pfad je Partikel zu fuellen.
 * @param {string} color
 * @param {number} px
 */
function _stStarSprite(color, px) {
  const key = `${color}@${px}`;
  const cached = _starSpriteCache.get(key);
  if (cached) return cached;
  const c = document.createElement('canvas');
  c.width = px;
  c.height = px;
  const ctx = c.getContext('2d');
  if (ctx && typeof Path2D === 'function' && STAR_SILHOUETTE_D) {
    // Das Logo-Viewbox ist 2256x2373 - auf die Spritekante normieren.
    const scale = px / 2373;
    ctx.translate((px - 2256 * scale) / 2, 0);
    ctx.scale(scale, scale);
    ctx.fillStyle = color;
    ctx.fill(new Path2D(STAR_SILHOUETTE_D));
  }
  _starSpriteCache.set(key, c);
  return c;
}

/**
 * Einmaliger Sternen-Burst, der hinter dem fertigen Buch hervorschiesst: die
 * Partikel starten auf dem Rand des Covers und fliegen nach aussen, damit es
 * aussieht, als kaeme das Licht von hinter dem Buch - und nicht, als lege sich
 * Deko darueber. Laeuft genau einmal und raeumt seinen Canvas danach weg.
 * @param {Element} anchor
 */
function _stStarBurst(anchor) {
  const rect = anchor.getBoundingClientRect();
  if (!rect.width || !rect.height) return;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const canvas = document.createElement('canvas');
  canvas.className = 'st-burst-canvas';
  canvas.width = Math.round(window.innerWidth * dpr);
  canvas.height = Math.round(window.innerHeight * dpr);
  canvas.setAttribute(
    'style',
    'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:10000;'
  );
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  document.body.appendChild(canvas);

  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const halfW = rect.width / 2;
  const halfH = rect.height / 2;

  // Erste Farbe ist die Buchfarbe (custom.primary_color, vom Overlay als
  // --overlay-primary-color gesetzt); die Gold-/Weisstoene bleiben fuer alle Buecher.
  const bookColor = getComputedStyle(anchor).getPropertyValue('--overlay-primary-color').trim();
  const burstColors = bookColor ? [bookColor, ...STAR_BURST_COLORS.slice(1)] : STAR_BURST_COLORS;

  /** @type {{x:number,y:number,vx:number,vy:number,rot:number,vr:number,size:number,sprite:HTMLCanvasElement,life:number,maxLife:number}[]} */
  const particles = [];
  for (let i = 0; i < 26; i++) {
    // Gleichmaessig verteilte Winkel mit etwas Streuung - reiner Zufall klumpt.
    const angle = (i / 26) * Math.PI * 2 + (Math.random() - 0.5) * 0.22;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    // Startpunkt auf dem Coverrand in Blickrichtung des Winkels.
    const edge = Math.min(
      Math.abs(cos) > 0.001 ? halfW / Math.abs(cos) : Infinity,
      Math.abs(sin) > 0.001 ? halfH / Math.abs(sin) : Infinity
    ) * 0.94;
    const speed = 3 + Math.random() * 3;
    const size = 9 + Math.random() * 11;
    particles.push({
      x: cx + cos * edge,
      y: cy + sin * edge,
      vx: cos * speed,
      vy: sin * speed - 1.2, // leichter Auftrieb, sonst faellt alles nur nach unten
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() - 0.5) * 0.22,
      size,
      sprite: _stStarSprite(burstColors[i % burstColors.length] || '#FFFFFF', 48),
      life: 0,
      maxLife: 1000 + Math.random() * 500,
    });
  }

  let last = performance.now();
  let raf = 0;
  /** @param {number} now */
  const step = (now) => {
    // In 60fps-Schritten rechnen, damit 120-Hz-Displays den Burst nicht doppelt
    // so schnell abspielen.
    const elapsed = Math.min(now - last, 50);
    const dt = elapsed / 16.667;
    last = now;
    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
    let alive = 0;
    for (const p of particles) {
      p.life += elapsed;
      if (p.life >= p.maxLife) continue;
      alive++;
      p.vy += 0.13 * dt; // Schwerkraft
      // Kraeftiger Luftwiderstand: die Sterne schiessen kurz raus, bremsen
      // sichtbar ab und schweben aus - statt einfach aus dem Bild zu fliegen.
      p.vx *= Math.pow(0.965, dt);
      p.vy *= Math.pow(0.965, dt);
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vr * dt;
      const t = p.life / p.maxLife;
      ctx.globalAlpha = t < 0.55 ? 1 : 1 - (t - 0.55) / 0.45;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.drawImage(p.sprite, -p.size / 2, -p.size / 2, p.size, p.size);
      ctx.restore();
    }
    if (alive > 0) {
      raf = requestAnimationFrame(step);
    } else {
      cancelAnimationFrame(raf);
      canvas.remove();
    }
  };
  raf = requestAnimationFrame(step);
}

/**
 * Beispielfotos für die "So sieht ein gutes Foto aus"-Karte in Schritt 2.
 * Reihenfolge = Anzeigereihenfolge im 2x2-Raster. Zum Austauschen der Bilder
 * nur die `src`-URLs (Shopify Files) ersetzen.
 */
const PHOTO_GUIDE_EXAMPLES = [
  { ok: true,  label: 'Klar & gut erkennbar',    src: 'https://cdn.shopify.com/s/files/1/1018/1073/3381/files/photo_example_good.jpg?v=1786186501' },
  { ok: false, label: 'Gesicht nicht verdeckt',  src: 'https://cdn.shopify.com/s/files/1/1018/1073/3381/files/photo_example_glasses.jpg?v=1786186502' },
  { ok: false, label: 'Keine seitlichen Bilder', src: 'https://cdn.shopify.com/s/files/1/1018/1073/3381/files/photo_example_side.jpg?v=1786186501' },
  { ok: false, label: 'Nur ein Kind im Bild',    src: 'https://cdn.shopify.com/s/files/1/1018/1073/3381/files/photo_example_multiple.jpg?v=1786186501' },
];

/**
 * Rezensionen, die während der Charakter-Generierung (Schritt 3) rotieren.
 * Merchant-überschreibbar via `data-wait-reviews` (eine Zeile pro Rezension,
 * Format `Name | Zitat`, siehe snippets/button.liquid).
 */
const WAIT_REVIEWS = [
  { name: 'Sarah M.', text: 'Die Ähnlichkeit ist verblüffend - mein Sohn hat sich sofort erkannt!' },
  { name: 'Daniel K.', text: 'Das schönste Geschenk. Wird jeden Abend vorgelesen.' },
  { name: 'Lena B.', text: 'Qualität top, Lieferung schnell. Absolute Empfehlung!' },
];

/**
 * Foto-Sicherheit/Garantie-Chips unter der Rezension (Schritt 3, Ladezustand).
 * ACHTUNG: Diese Claims müssen faktisch mit Datenschutzerklärung und
 * Garantiebedingungen übereinstimmen - vor allem die Löschfrist.
 */
const WAIT_TRUST_CHIPS = [
  { icon: ICON_LOCK(14), label: 'Foto<br>verschlüsselt' },
  { icon: ICON_TRASH, label: 'Gelöscht<br>nach 30 Tagen' },
  { icon: ICON_SHIELD_CHECK, label: '30 Tage<br>Garantie' },
];

/**
 * Gruende im Feedback-Modal nach Daumen runter (Schritt 3). Die `id` geht als
 * `reasons` an /preview/feedback und wird dort ausgewertet - IDs also nie
 * umbenennen, nur neue anhaengen. `other` klappt das Freitextfeld auf.
 */
const CHARACTER_FEEDBACK_REASONS = [
  { id: 'face', label: 'Gesicht' },
  { id: 'hair', label: 'Haare / Frisur' },
  { id: 'skin', label: 'Hautfarbe' },
  { id: 'eyes', label: 'Augen' },
  { id: 'age', label: 'Alter wirkt falsch' },
  { id: 'outfit', label: 'Outfit' },
  { id: 'style', label: 'Zeichenstil' },
  { id: 'other', label: 'Etwas anderes' },
];

/**
 * Sprechender Name je Schritt. Die internen Nummern (1..7, mit Luecken durch
 * SCENE_GEN_DISABLED und STARTALES_BINDING_ENABLED) sind fuer Analytics
 * unbrauchbar - ein Listener draussen soll "extras" lesen, nicht raten muessen,
 * ob 6 gerade der Extras- oder der Review-Screen ist.
 * @type {Record<number, string>}
 */
const STEP_SLUGS = {
  1: 'details',
  2: 'photo',
  [STEP_OUTFIT]: 'outfit',
  3: 'character',
  4: 'book_preview',
  5: 'binding',
  6: 'extras',
  7: 'review',
};

/**
 * A custom element that opens a 5-step personalization overlay.
 */
class CustomAlertButton extends HTMLElement {
  static get devMode() { return localStorage.getItem('startales_dev_mode') === 'true'; }
  static set devMode(v) { localStorage.setItem('startales_dev_mode', v ? 'true' : 'false'); console.log(`⚙️ Dev mode ${v ? 'ON - API calls skipped, 30s timer used' : 'OFF - real API calls'}`); }

  constructor() {
    super();
    // SCENE_GEN_DISABLED: book preview step (4) is skipped. Restore to 7 to re-enable.
    // Binding step (5) is gated by STARTALES_BINDING_ENABLED.
    // Step 6 is the extras/upsell screen, step 7 the review screen.
    // The outfit step (STEP_OUTFIT) is gated by STARTALES_OUTFIT_ENABLED.
    this.totalSteps = this._stepOrder().length;
    this.currentStep = 1; // 1..7 (step 4 is skipped while scene gen is disabled)

    /** @type {Record<number, { title: string, bubble: string }>} */
    this.stepInfo = {
      1: { title: 'Dein Kind', bubble: 'Hey! 👋 Erzähl uns kurz von deinem Kind - Vorname, Geschlecht und Augenfarbe.' },
      2: { title: 'Foto hochladen', bubble: 'Ein klares Foto mit freiem Blick aufs Gesicht funktioniert am besten. 📸' },
      // Bubble is personalised with the child's name, see _outfitBubble.
      [STEP_OUTFIT]: { title: 'Outfit wählen', bubble: 'Was soll dein Kind im Buch tragen? 👕' },
      // Ehrliche Dauer statt "nur einen Moment": in Clarity brachen Kunden nach
      // ~20s ab, weil das versprochene "gleich" vorbei war.
      3: { title: 'Dein Charakter', bubble: 'Jetzt zaubern wir deinen Helden ✨ Dauert ca. 1 Minute – du kannst kurz die App wechseln, wir speichern alles.' },
      // SCENE_GEN_DISABLED: book preview step. Uncomment to re-enable.
      // 4: { title: 'Buchvorschau', bubble: 'Jetzt zaubern wir eine erste Vorschau deines Buches. Das dauert nur einen Moment! 📚' },
      5: { title: 'Einband wählen', bubble: 'Welcher Einband passt besser zu dir? Beide sind wunderschön gedruckt. 📖' },
      6: { title: 'Dein Buch veredeln', bubble: 'Füge deinem Buch den letzten Touch hinzu 🎁 Optionale Extras - wähle aus, was du ergänzen möchtest.' },
      7: { title: 'Bestellung prüfen', bubble: 'Fast geschafft! Prüfe kurz, ob alles passt - dann geht\'s los. 🚀' },
    };

    this.childName = '';
    this.gender = '';
    this.eyeColor = '';
    this.photoConfirmed = false;
    this._setUploadedImageUrl('');
    this.uploadedImageBlob = null;
    this.previewImageUrl = '';
    /** Cache-busted variant of previewImageUrl, set after a details re-render. */
    this.previewDisplayUrl = '';
    this.previewUniqid = '';
    this.bookPreviewImageUrl = '';
    this._detailsSubmittedFor = '';
    // Generation id of the preview already reported as `preview_generated`.
    this._previewTrackedFor = '';
    this.retryCount = 1;
    this.retryMax = 2;
    // '' | 'up' | 'down' - Bewertung der aktuell gezeigten Vorschau.
    this.characterRating = '';
    // Empty until the customer picks a binding in step 5 - nothing is preselected
    // and the step's continue button stays disabled while this is empty.
    this.bookCoverType = STARTALES_BINDING_ENABLED ? 'softcover' : 'hardcover';
    this.coverFinish = 'glaenzend';
    // 'book' = the book's predefined outfit (included), 'photo' = the clothes
    // from the uploaded photo (paid add-on). The free option is preselected so
    // the step never blocks; the card states make the choice visible.
    this.outfitChoice = 'book';
    this.dedicationEnabled = false;
    this.dedicationText = '';
    // The "please enter your dedication" hint only appears once the customer
    // actually tried to continue with an empty field - not the moment the card
    // is selected.
    this._dedicationErrorShown = false;
    /** Handy-Tastatur sichtbar (nur dort messbar, wo sie das Visual Viewport verkleinert). */
    this._keyboardOpen = false;
    this.mattCoverEnabled = false;

    /** @type {{ promise: Promise<string> | null, progress: number, done: boolean, error: string | null, startTime: number }} */
    this._previewGen ={ promise: null, progress: 0, done: false, error: null, startTime: 0 };
    /** @type {{ promise: Promise<string> | null, progress: number, done: boolean, error: string | null, startTime: number }} */
    this._bookPreviewGen = { promise: null, progress: 0, done: false, error: null, startTime: 0 };
    /** Timer fuer den Sternen-Burst nach dem Reveal. @type {ReturnType<typeof setTimeout> | undefined} */
    this._celebrateTimer = undefined;

    /** @type {{ interval: ReturnType<typeof setInterval> | null, timeout: ReturnType<typeof setTimeout> | null } | null} */
    this._waitRotation = null;
    /** @type {Array<{ name: string, text: string }> | null} */
    this._waitReviewCache = null;
    /** @type {Array<{ src: string, alt: string }> | null} */
    this._waitPageCache = null;
    /** "Weiter, wo du aufgehoert hast?" ist offen - solange nichts speichern. */
    this._resumePending = false;
    /** Buch liegt im Warenkorb - der Durchlauf wird nicht mehr gespeichert. */
    this._flowCompleted = false;

    /** @type {number} */
    this._lastViewedStep = 0;
    /** @type {ReturnType<typeof setTimeout> | null} */
    this._hideOverlayTimeoutId = null;
    /** @type {Array<Record<string, any>>} */
    this._savedChars = [];
    /** @type {boolean} Guards against double-submit while a cart/checkout request is in flight. */
    this._submittingOrder = false;
    /**
     * Setzt den Kauf-Button zurueck, solange ein Submit laeuft. Wird gebraucht,
     * wenn der Kunde per Zurueck-Button aus dem Checkout kommt: dann laeuft kein
     * `finally` mehr, das den Spinner aufloest.
     * @type {(() => void) | null}
     */
    this._restoreSubmitBtn = null;
    /**
     * @type {boolean} Die Buch-Zeile liegt bereits im Warenkorb. Ein Retry darf
     * sie dann nicht ein zweites Mal hinzufuegen.
     */
    this._bookLineAdded = false;
    /**
     * @type {boolean} Der Kauf wurde vollstaendig abgeschickt und der Browser
     * navigiert(e) zum Checkout. Ein weiterer Klick fuehrt zurueck zur Kasse
     * statt ein zweites Buch in den Warenkorb zu legen.
     */
    this._cartSubmitted = false;
    /** @type {boolean} Extras nach einem fehlgeschlagenen Add-on-Call bewusst auslassen. */
    this._skipAddons = false;
    /** @type {string} Verbindet Buch- und Add-on-Zeilen (_BuchID); ueber Retries hinweg stabil. */
    this._pairId = '';
    /** @type {(() => void) | null} */
    this._pageShowHandler = null;
    /**
     * Personalisierte Buecher, die schon im Warenkorb liegen (aus /cart.js).
     * null = noch nicht geladen; entscheidet ueber Zweitbuch-Angebot und -Rabatt.
     * @type {Array<{ name: string, price: number }> | null}
     */
    this._cartBooks = null;
    /** @type {Array<{ title: string, url: string, image: string, color: string, current: boolean }> | null} */
    this._nextBooksCache = null;
    /** Index in _nextBooks(): das zweite Buch, das in der Bundle-Uebersicht gewaehlt ist. */
    this._bundlePick = 0;
    /** @type {((e: KeyboardEvent) => void) | null} */
    this._escHandler = null;
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick.bind(this));
    this._armOverlayPreload();
    this._wireHeroTrigger();
    _stWireExternalTriggers();
    // Gespeicherter Durchlauf (z. B. vor dem Neuladen): die Buttons sagen das.
    if (!_stFlowOwner && this._readStoredFlow()) _stSyncTriggerLabels(true);
    this._tryRestoreFromStorage();
    this._consumeHandoff();
    this._wireBundlePick();

    // Der Submit-Guard wird auf dem Checkout-Pfad bewusst nie zurueckgesetzt
    // (siehe addToCart). Kommt der Kunde per Zurueck-Button aus dem Checkout,
    // stellt Safari die Seite aus dem bfcache wieder her - dieselbe Instanz lebt
    // weiter, der Guard bleibt auf true und "Jetzt kaufen" waere fuer den Rest
    // der Session ein Klick ins Leere. pageshow feuert auch beim normalen Load,
    // wo die Flags ohnehin frisch sind, deshalb ist das Zuruecksetzen bedingungslos.
    if (!this._pageShowHandler) {
      this._pageShowHandler = () => this._releaseSubmitGuard();
      window.addEventListener('pageshow', this._pageShowHandler);
    }
  }

  /**
   * Macht den Hero-Banner ueber dem Button klickbar - er wirbt fuer dieselbe
   * Personalisierung, also soll er sie auch oeffnen.
   *
   * Rolle, tabindex und Klasse setzt bewusst erst das JS: ein role="button" im
   * Markup waere ohne geladenes Script ein Versprechen, das niemand einloest.
   */
  _wireHeroTrigger() {
    const wrapper = this.closest('.custom-alert-wrapper');
    const hero = /** @type {HTMLElement | null} */ (wrapper ? wrapper.querySelector('.custom-alert-hero') : null);
    if (!hero || hero.dataset.stHeroWired === 'true') return;
    hero.dataset.stHeroWired = 'true';

    hero.setAttribute('role', 'button');
    hero.setAttribute('tabindex', '0');
    hero.classList.add('custom-alert-hero--clickable');

    hero.addEventListener('click', (event) => this.handleClick(event));
    hero.addEventListener('keydown', (event) => {
      const key = /** @type {KeyboardEvent} */ (event).key;
      if (key !== 'Enter' && key !== ' ') return;
      event.preventDefault();
      this.handleClick(event);
    });

    // Dieselbe Absichts-Erkennung wie am Button: beim ersten Hover/Touch/Fokus
    // laufen die Overlay-Bilder warm (siehe _armOverlayPreload).
    ['pointerenter', 'touchstart', 'focusin'].forEach((type) => {
      hero.addEventListener(type, () => this._preloadOverlayImages(), { once: true, passive: true });
    });
  }

  /**
   * Loest einen haengengebliebenen Submit-Zustand auf: Guard frei, Spinner weg.
   * Liegt die Bestellung schon im Warenkorb, wird der Button zum Checkout-Link,
   * damit ein zweiter Klick kein zweites Buch bestellt.
   */
  _releaseSubmitGuard() {
    this._submittingOrder = false;
    const restore = this._restoreSubmitBtn;
    this._restoreSubmitBtn = null;
    if (restore) restore();
    if (this._cartSubmitted) this._markCheckoutPending();
  }

  /**
   * Beschriftet den Kauf-Button um, sobald die Positionen im Warenkorb liegen.
   * @param {Element | null} [overlay]
   */
  _markCheckoutPending(overlay = null) {
    const root = overlay || document.querySelector('.custom-overlay');
    const btn = /** @type {HTMLButtonElement | null} */ (
      root ? root.querySelector('.st-screen--review .st-btn--buy') : null
    );
    if (!btn) return;
    btn.disabled = false;
    btn.removeAttribute('aria-busy');
    btn.innerHTML = `${ICON_CART} Zur Kasse`;
  }

  /**
   * Das Vorladen hing frueher direkt am connectedCallback: rund 950 KB Overlay-
   * Bilder auf JEDEM Produktseiten-Aufruf, also auch fuer die grosse Mehrheit,
   * die die Personalisierung nie oeffnet - auf alten Geraeten konkurriert das
   * mit dem LCP-Bild der Seite. Jetzt erst bei erkennbarer Absicht: Hover,
   * Touch-Start oder Fokus liegen einige hundert ms vor dem echten Klick, das
   * reicht, damit die Bilder beim Oeffnen warm sind.
   */
  _armOverlayPreload() {
    const conn = /** @type {any} */ (navigator).connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
    const warm = () => this._preloadOverlayImages();
    ['pointerenter', 'touchstart', 'focusin'].forEach(type => {
      this.addEventListener(type, warm, { once: true, passive: true });
    });
  }

  _preloadOverlayImages() {
    if (this._imagesPreloaded) return;
    this._imagesPreloaded = true;
    const urls = [
      this.getAttribute('data-hardcover-image') || HARDCOVER_IMAGE_FALLBACK,
      this.getAttribute('data-softcover-image') || SOFTCOVER_IMAGE_FALLBACK,
      this._extraImage('dedication'),
      this._extraImage('matt'),
      STARTALES_OUTFIT_ENABLED ? this._outfitImage() : '',
      ...PHOTO_GUIDE_EXAMPLES.map(e => e.src),
    ];
    urls.forEach(url => { if (url) { const img = new Image(); img.src = url; } });
  }

  /**
   * Steps in the order the customer walks through them. Single source for
   * next/back navigation, the stepper dots and "Schritt x von y".
   * SCENE_GEN_DISABLED: step 4 is left out; add it after 3 to re-enable.
   * @returns {number[]}
   */
  _stepOrder() {
    const order = [1, 2];
    if (STARTALES_OUTFIT_ENABLED) order.push(STEP_OUTFIT);
    order.push(3);
    if (STARTALES_BINDING_ENABLED) order.push(5);
    order.push(6, 7);
    return order;
  }

  /**
   * The book's predefined outfit for the outfit step: the hero in the book's
   * clothes with a grey placeholder head (resolved in snippets/button.liquid:
   * custom.outfit_bild, theme asset, custom.charakter_bild).
   */
  _outfitImage() {
    return (this.getAttribute('data-outfit-image') || '').trim() || OUTFIT_IMAGE_FALLBACK;
  }

  /** True when the photo outfit is chosen and the step is actually offered. */
  _outfitIsPhoto() {
    return STARTALES_OUTFIT_ENABLED && this.outfitChoice === 'photo';
  }

  /**
   * Preview image for one of the two extras on step 6. Merchant-overridable via
   * Liquid; the fallbacks are the mockups the design references.
   * @param {'dedication' | 'matt'} key
   */
  _extraImage(key) {
    const fallback = key === 'dedication'
      ? 'https://cdn.shopify.com/s/files/1/1018/1073/3381/files/firstpage_1.jpg?v=1787646436&width=600'
      : 'https://cdn.shopify.com/s/files/1/1018/1073/3381/files/matt_cover.jpg?v=1787650892&width=600';
    return (this.getAttribute(`data-${key}-image`) || '').trim() || fallback;
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    if (this._pageShowHandler) {
      window.removeEventListener('pageshow', this._pageShowHandler);
      this._pageShowHandler = null;
    }
  }

  /** @param {Event} event */
  handleClick(event) {
    event.preventDefault();
    // Produktseiten- und Sticky-Button sind zwei Instanzen mit getrenntem
    // State. Haelt die andere einen angefangenen Durchlauf, oeffnet sie ihn -
    // sonst faenge der Kunde je nach angeklicktem Button wieder bei 1 an.
    if (_stFlowOwner && _stFlowOwner !== this && _stFlowOwner.isConnected && _stFlowOwner._hasProgress()) {
      _stFlowOwner.showOverlay();
      return;
    }
    this.showOverlay();
  }

  // ---------------------------------------------------------------------------
  // Overlay construction
  // ---------------------------------------------------------------------------

  /**
   * @param {{ keepState?: boolean }} [opts] keepState: State wurde vorab gesetzt
   *   (Uebergabe von einem anderen Buch) und darf nicht zurueckgesetzt werden,
   *   auch wenn _hasProgress() ihn noch nicht als Fortschritt zaehlt.
   */
  showOverlay({ keepState = false } = {}) {
    if (this._hideOverlayTimeoutId) {
      clearTimeout(this._hideOverlayTimeoutId);
      this._hideOverlayTimeoutId = null;
    }
    // Dieselbe Bedingung, die der Schliessen-Dialog dem Kunden zusichert:
    // was _hasProgress() als Fortschritt zaehlt, wird auch wiederhergestellt.
    const hasExistingState = keepState || this._hasProgress();

    if (!hasExistingState) {
      this.resetState();
    }

    _stTrack('flow_opened', {
      product_id: this.getAttribute('data-product-id') || undefined,
      meta: { resumed: hasExistingState, step: this.currentStep },
    });

    const existingOverlay = document.querySelector('.custom-overlay');
    if (existingOverlay) existingOverlay.remove();

    const hardcoverImage = this.getAttribute('data-hardcover-image') || HARDCOVER_IMAGE_FALLBACK;
    const softcoverImage = this.getAttribute('data-softcover-image') || SOFTCOVER_IMAGE_FALLBACK;

    const hardcoverPrice = parseFloat(this.getAttribute('data-hardcover-price') || '') || 39.99;
    const hardcoverComparePrice = parseFloat(this.getAttribute('data-hardcover-compare-price') || '') || 0;
    const softcoverPrice = parseFloat(this.getAttribute('data-softcover-price') || '') || 29.99;
    const softcoverComparePrice = parseFloat(this.getAttribute('data-softcover-compare-price') || '') || 0;

    this.hardcoverPrice = hardcoverPrice;
    this.hardcoverComparePrice = hardcoverComparePrice;
    this.softcoverPrice = softcoverPrice;
    this.softcoverComparePrice = softcoverComparePrice;

    // Upsell prices come from the Shopify products via Liquid data attributes;
    // fall back to the previous hardcoded values if the attribute is missing.
    this.dedicationPrice = parseFloat(this.getAttribute('data-dedication-price') || '') || 3.99;
    this.mattPrice = parseFloat(this.getAttribute('data-matt-price') || '') || 1.99;
    // Anchor ("was") prices for the extras step. Only the products' real
    // compare-at prices are shown - without one the strike-through is omitted
    // rather than invented.
    this.dedicationComparePrice = parseFloat(this.getAttribute('data-dedication-compare-price') || '') || 0;
    this.mattComparePrice = parseFloat(this.getAttribute('data-matt-compare-price') || '') || 0;
    this.outfitPrice = parseFloat(this.getAttribute('data-outfit-price') || '') || OUTFIT_PHOTO_PRICE_FALLBACK;

    const overlay = document.createElement('div');
    overlay.className = 'custom-overlay custom-overlay--wave-enter';
    overlay.innerHTML = this._buildOverlayHtml({
      hardcoverImage, softcoverImage,
      hardcoverPrice, hardcoverComparePrice,
      softcoverPrice, softcoverComparePrice,
    });

    // Primary color mapping (metafield) → --overlay-primary-color used by --st-orange
    const primaryColor = this.dataset.primaryColor;
    if (primaryColor && primaryColor.trim()) {
      overlay.style.setProperty('--overlay-primary-color', primaryColor.trim());
    }

    this._wireEvents(overlay);
    document.body.appendChild(overlay);

    // Initial state. Ohne Stand im Speicher der Seite fragt der Flow nach einem
    // gespeicherten Durchlauf (Neuladen, anderer Tab, Rueckkehr aus der App).
    const storedFlow = hasExistingState ? null : this._readStoredFlow();
    if (storedFlow) this._resumePending = true;
    if (hasExistingState) {
      this._restoreStateToOverlay(overlay);
    } else {
      this._updateScreen(overlay, { initial: true });
    }
    if (storedFlow) this._offerResume(overlay, storedFlow);

    // Das Markup wird bei jedem Oeffnen neu gebaut - liegt die Bestellung schon
    // im Warenkorb, muesste der frische "Jetzt kaufen"-Button sonst wieder wie
    // ein Hinzufuegen wirken.
    if (this._cartSubmitted) this._markCheckoutPending(overlay);

    // Lock body scroll — overflow:hidden preserves scroll position so no
    // restoration is needed on close (avoids the jump-to-top flash).
    overlay.dataset.scrollY = window.scrollY.toString();
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    // overflow:hidden verhindert nur das Wischen, nicht das programmatische
    // Scrollen: ein einziges scrollIntoView() (oder ein Fokus) auf ein Element
    // tief im Screen genuegt, damit der Browser das Overlay selbst verschiebt.
    // Der absolut am Screen-Boden haengende Footer faehrt dann sichtbar in die
    // Bildschirmmitte. Das Overlay ist eine feste Buehne - es scrollt nie.
    overlay.addEventListener('scroll', () => {
      if (overlay.scrollTop !== 0) overlay.scrollTop = 0;
      if (overlay.scrollLeft !== 0) overlay.scrollLeft = 0;
    });

    // Prevent mobile zoom on input focus
    const inputs = overlay.querySelectorAll('input[type="text"], textarea');
    inputs.forEach(input => {
      input.addEventListener('focus', () => {
        const vp = document.querySelector('meta[name="viewport"]');
        if (vp) {
          this.originalViewport = vp.getAttribute('content');
          vp.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
        }
      });
      input.addEventListener('blur', () => {
        const vp = document.querySelector('meta[name="viewport"]');
        if (vp && this.originalViewport) vp.setAttribute('content', this.originalViewport);
      });
    });

    // Visual viewport keyboard handling
    // Das Overlay kann ohne hideOverlay neu gebaut werden (zweites Buch), dann
    // haengen die Listener des alten noch.
    if (this._vvResizeHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this._vvResizeHandler);
      this._vvResizeHandler = null;
    }
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
      this._escHandler = null;
    }

    if (window.visualViewport) {
      const handle = () => {
        const vv = window.visualViewport;
        if (!vv) return;
        const kh = window.innerHeight - vv.height;
        this._keyboardOpen = kh > 50;
        overlay.querySelectorAll('.st-body').forEach(body => {
          body.style.paddingBottom = kh > 50 ? `${kh + 120}px` : '';
        });
      };
      window.visualViewport.addEventListener('resize', handle);
      this._vvResizeHandler = handle;
    }

    // ESC close — routed through the same abort confirmation as the X button.
    // The confirm modal registers its own (later) Escape handler, so a single
    // Escape press with the modal open only dismisses the modal.
    const escHandler = (e) => {
      if (e.key === 'Escape') {
        if (overlay.querySelector('.st-confirm')) return;
        // Die Leseprobe schliesst sich mit demselben Tastendruck selbst.
        if (overlay.querySelector('.st-bookpeek')) return;
        this._requestClose(overlay, () => document.removeEventListener('keydown', escHandler));
      }
    };
    document.addEventListener('keydown', escHandler);
    this._escHandler = escHandler;

    this._renderNextBookState(overlay);
    this._refreshCartBooks(overlay);

    // Trigger wave animation
    requestAnimationFrame(() => {
      overlay.classList.remove('custom-overlay--wave-enter');
      overlay.classList.add('custom-overlay--wave-open');
    });
  }

  /**
   * Trust/social-proof content for the review step. Everything is supplied by
   * Liquid (see snippets/button.liquid) so the merchant edits it in the theme
   * editor; the fallbacks only matter when the snippet is out of sync.
   * @returns {{ rating: string, familyCount: string, deliveryRange: string, reviewAuthor: string, reviewInitial: string, reviewQuote: string, paymentIcons: string }}
   */
  _trustContent() {
    const attr = (/** @type {string} */ name, /** @type {string} */ fallback) =>
      (this.getAttribute(name) || '').trim() || fallback;

    const reviewAuthor = attr('data-review-author', 'Sarah M.');
    const quote = attr(
      'data-review-quote',
      'Mein Sohn will jetzt jeden Abend sein eigenes Buch vorgelesen bekommen. Die Ähnlichkeit ist verblüffend - jeden Cent wert!'
    );

    // Payment badges are the official Shopify provider SVGs, rendered once per
    // page into a template by the Liquid snippet.
    const tpl = /** @type {HTMLTemplateElement | null} */ (document.getElementById('st-overlay-payment-icons'));

    return {
      rating: attr('data-rating', '4,9'),
      familyCount: attr('data-family-count', '2.000'),
      deliveryRange: attr('data-delivery-range', this._fallbackDeliveryRange()),
      reviewAuthor,
      reviewInitial: reviewAuthor.charAt(0).toUpperCase() || 'S',
      reviewQuote: `&bdquo;${_stEmphasis(quote)}&ldquo;`,
      paymentIcons: tpl ? tpl.innerHTML : '',
    };
  }

  /**
   * Delivery window used when Liquid did not pass one: today + 8 to today + 12
   * days, matching the product page's info card.
   */
  _fallbackDeliveryRange() {
    const days = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];
    const months = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];
    const at = (/** @type {number} */ offset) => {
      const d = new Date();
      d.setDate(d.getDate() + offset);
      return d;
    };
    const from = at(8);
    const to = at(12);
    const head = from.getMonth() === to.getMonth()
      ? `${days[from.getDay()]} ${from.getDate()}.`
      : `${days[from.getDay()]} ${from.getDate()}. ${months[from.getMonth()]}`;
    return `${head} - ${days[to.getDay()]} ${to.getDate()}. ${months[to.getMonth()]}`;
  }

  _buildOverlayHtml({
    hardcoverImage, softcoverImage,
    hardcoverPrice, hardcoverComparePrice,
    softcoverPrice, softcoverComparePrice,
  }) {
    const fmt = (p) => `€${p.toFixed(2).replace('.', ',')}`;
    // Trust content comes from Liquid data attributes (see snippets/button.liquid)
    // so rating, review and payment icons stay editable in the theme editor.
    // Used by the waiting screen (step 3) and the review step (7).
    const trust = this._trustContent();

    // The dedication and matt-cover add-ons are offered on the extras step (6);
    // the review step (7) only lists what was picked there.

    // "Blick ins Buch": die Leseprobe der Produktseite laesst sich auf der
    // Uebersicht noch einmal aufschlagen. Der Knopf erscheint nur, wenn die
    // Section auf der Seite steht (siehe _bookPeekSource).
    const bookPeek = this._bookPeekSource()
      ? `<button type="button" class="st-peek" data-st-book-peek>${ICON_BOOK}<span>Blick ins Buch</span></button>`
      : '';


    // Fixed pre-header bar mirroring the site announcement bar (orange, white,
    // two rotating messages). Sits above every screen inside the overlay.
    const preheader = `
      <div class="st-preheader" aria-hidden="true">
        <div class="st-preheader__slides">
          <span class="st-preheader__slide">🍂 HERBST SALE · BIS ZU 40% RABATT</span>
          <span class="st-preheader__slide">📦 KOSTENLOSER Versand nach DE</span>
        </div>
      </div>`;

    const header = ({ hideBack = false } = {}) => `
      <div class="st-header">
        <div class="st-header__title"></div>
        <button type="button" class="st-header__icon-btn st-header__close" aria-label="Schließen">${ICON_CLOSE}</button>
        <div class="st-stepper">
          <!-- SCENE_GEN_DISABLED: step 4 removed. Dot count follows STARTALES_BINDING_ENABLED. -->
          ${'<span></span>'.repeat(this.totalSteps)}
        </div>
        <div class="st-stepper-caption">
          <span class="st-stepper-caption__left">
            <span class="st-stepper-caption__book" hidden></span>
            <span class="st-stepper-caption__step">Schritt 1 von ${this.totalSteps}</span>
          </span>
          <span class="st-stepper-caption__pct">${Math.round((1 / this.totalSteps) * 100)}%</span>
        </div>
      </div>`;

    const mascot = `
      <div class="st-mascot">
        <div class="st-mascot__star">${STAR_MASCOT_USE}</div>
        <div class="st-mascot__bubble"><p class="st-mascot__text"></p></div>
      </div>`;

    // ===== Step 1: Child details (name, gender, eye color) =====
    const eyeOptions = [
      { color: 'dark-brown', label: 'Dunkelbraun', hex: '#2D1815' },
      { color: 'brown',      label: 'Braun',       hex: '#5A3620' },
      { color: 'light-brown',label: 'Hellbraun',   hex: '#886B33' },
      { color: 'green',      label: 'Grün',        hex: '#3F7D4C' },
      { color: 'blue',       label: 'Blau',        hex: '#2D6BA4' },
    ];
    const eyeOptionsHtml = eyeOptions.map(o => `
      <button type="button" class="st-eye-option" data-color="${o.color}" data-color-label="${o.label}">
        <div class="st-eye-swatch" style="background-color:${o.hex}">
          <div class="st-eye-check">${ICON_CHECK_SM}</div>
        </div>
        <div class="st-eye-label">${o.label}</div>
      </button>
    `).join('');

    const screen1 = `
      <section class="st-screen st-screen--details" data-step="1">
        ${header()}
        <div class="st-body">
          ${mascot}
          <div class="st-bundle" role="status" hidden></div>

          <div class="st-field st-field--name" data-error="false">
            <label class="st-field__label" for="st-name-input">Vorname des Kindes</label>
            <input id="st-name-input" type="text" class="st-field__input st-name-input" placeholder="z. B. Max" maxlength="20" />
            <div class="st-field__error" role="alert">Bitte gib den Vornamen des Kindes ein (nur Buchstaben).</div>
          </div>

          <div class="st-field st-field--gender" data-error="false">
            <div class="st-field__label">Geschlecht</div>
            <div class="st-gender-grid">
              <button type="button" class="st-gender" data-value="Junge" data-selected="false"><span>👦</span>Junge</button>
              <button type="button" class="st-gender" data-value="Mädchen" data-selected="false"><span>👧</span>Mädchen</button>
            </div>
            <div class="st-field__error" role="alert">Bitte wähle ein Geschlecht aus, um fortzufahren.</div>
            ${this._genderHintHtml()}
          </div>

          <div class="st-field st-field--eyes" data-error="false">
            <div class="st-field__label">Augenfarbe</div>
            <div class="st-eye-grid">${eyeOptionsHtml}</div>
            <div class="st-field__error" role="alert">Bitte wähle eine Augenfarbe aus, um fortzufahren.</div>
          </div>
        </div>
        <div class="st-footer">
          <!-- Wie in Schritt 2 nur optisch gesperrt: ein echtes disabled-Attribut
               verschluckt den Klick, statt zu zeigen, was noch fehlt. -->
          <button type="button" class="st-btn st-btn--orange st-btn--continue" data-soft-disabled="true" aria-disabled="true">Weiter ${ICON_ARROW_RIGHT}</button>
        </div>
      </section>`;

    // ===== Step 2: Upload =====
    const photoGuideHtml = PHOTO_GUIDE_EXAMPLES.map(e => `
      <figure class="st-guide__item" data-ok="${e.ok}">
        <div class="st-guide__thumb">
          <img src="${e.src}" alt="" loading="lazy" decoding="async" />
          <span class="st-guide__badge">${e.ok ? ICON_CHECK_BADGE : ICON_X_BADGE}</span>
        </div>
        <figcaption class="st-guide__caption">${e.label}</figcaption>
      </figure>
    `).join('');

    // Die Guide-Box ist gleichzeitig ein zweiter Einstieg in den Datei-Dialog -
    // wer hier tippt, will ohnehin ein Foto auswählen.
    const photoGuide = `
      <div class="st-guide" role="button" tabindex="0" aria-label="Foto hochladen">
        <div class="st-guide__title">✨ So sieht ein gutes Foto aus</div>
        <p class="st-guide__sub">Wichtig ist nur ein gut erkennbares Gesicht. Perfekt muss dein Foto nicht sein: Du siehst das Ergebnis kostenlos.</p>
        <div class="st-guide__grid">${photoGuideHtml}</div>
      </div>`;

    const screen2 = `
      <section class="st-screen st-screen--upload" data-step="2" data-has-photo="false">
        ${header()}
        <div class="st-body">
          ${mascot}

          <!-- Beispielkarte vorerst ausgeblendet - zum Reaktivieren die Kommentarklammern entfernen.
          <div class="st-example-card" role="button" tabindex="0" aria-label="Foto hochladen">
            <div class="st-example-card__eyebrow">So wird aus deinem Foto …</div>
            <div class="st-example-card__row">
              <div class="st-example-photo st-example-photo--real">
                <img src="https://cdn.shopify.com/s/files/1/1018/1073/3381/files/Gemini_Generated_Image_m6hy0xm6hy0xm6hy_1.png?v=1779891091" alt="Dein Foto" />
              </div>
              <div class="st-example-card__arrow">${ICON_ARROW_RIGHT}</div>
              <div class="st-example-photo st-example-photo--char">
                <img src="https://cdn.shopify.com/s/files/1/1018/1073/3381/files/preview_1782333813434_du6dxkc_1.png?v=1782507925" alt="Dein Charakter" />
              </div>
            </div>
            <div class="st-example-card__labels">
              <span>Dein Foto</span>
              <span style="width:22px"></span>
              <span>Dein Charakter</span>
            </div>
          </div>
          -->

          <!-- Guide steht bewusst VOR der Upload-Card: auf Mobil sieht man so
               vor dem Upload, was ein gutes Foto ausmacht. -->
          ${photoGuide}

          <div class="st-photo-frame">
            <div class="st-photo-frame__inner">
              <img class="st-photo-frame__img" src="" alt="" />
            </div>
          </div>

          <div class="st-privacy">
            <div class="st-privacy__icon">${ICON_SHIELD_CHECK}</div>
            <div class="st-privacy__text">Dein hochgeladenes Foto wird nur zur Erstellung verwendet und danach automatisch gelöscht.</div>
          </div>

          <label class="st-upload-cta">
            <input type="file" class="st-upload-input" accept="image/*" />
            <div class="st-upload-cta__icon">${ICON_CAMERA}</div>
            <div class="st-upload-cta__body">
              <div class="st-upload-cta__title">Foto hochladen</div>
              <div class="st-upload-cta__sub">Kamera oder Galerie</div>
            </div>
            <div class="st-upload-cta__chevron">${ICON_CHEVRON_RIGHT}</div>
          </label>

          <!-- Ein abgelehntes oder fehlgeschlagenes Foto muss sichtbar werden:
               vorher wurde es kommentarlos verworfen und der Kunde stand vor
               einem Kasten, der scheinbar nichts tut. -->
          <p class="st-upload-error" role="alert" hidden></p>

          <label class="st-consent">
            <input type="checkbox" class="st-consent__input" />
            <div class="st-consent__box">${ICON_CHECK_WHITE}</div>
            <!-- Kurzfassung im Label, vollstaendige Erklaerung eine Antippdistanz
                 entfernt: die Einwilligung muss laut Art. 7 Abs. 2 DSGVO in klarer,
                 einfacher Sprache stehen - der Wortlaut im Lightbox-Text traegt
                 weiterhin alle Zusicherungen (18+, Nutzungsrecht, Erziehungsberechtigung). -->
            <div class="st-consent__text">Ich bin volljährig und darf dieses Foto nutzen. <a href="/policies/privacy-policy" class="st-consent__more" rel="noopener">Details &amp; Datenschutz</a></div>
          </label>
          <!-- Erscheint, wenn "Weiter" ohne Haken getippt wird (_flagConsentMissing),
               und bleibt, bis der Haken gesetzt ist. -->
          <p class="st-consent-hint" id="st-consent-hint" role="alert" hidden>Bitte bestätige kurz diesen Haken</p>
        </div>
        <div class="st-footer">
          <button type="button" class="st-footer__back" aria-label="Zurück">${ICON_BACK}</button>
          <!-- Immer aktiv dargestellt: der graue "Soft disabled"-Look liess Kunden
               glauben, es gehe nicht weiter (Clarity). Der Klick fuehrt zum
               Datei-Dialog bzw. zur fehlenden Checkbox. -->
          <button type="button" class="st-btn st-btn--orange st-btn--continue">Weiter ${ICON_ARROW_RIGHT}</button>
        </div>
      </section>`;

    // ===== Step 2.5: Outfit =====
    // Two cards side by side: what the book draws vs. the customer's own photo.
    // Native radios carry the choice (keyboard + screen reader for free); the
    // note below states what the choice does NOT touch - the face always comes
    // from the photo - because that is the misunderstanding to expect here.
    const outfitPrice = this.outfitPrice || OUTFIT_PHOTO_PRICE_FALLBACK;
    const screenOutfit = `
      <section class="st-screen st-screen--outfit" data-step="${STEP_OUTFIT}">
        ${header()}
        <div class="st-body">
          ${mascot}

          <fieldset class="st-outfit">
            <legend class="st-outfit__legend">Outfit für den Charakter wählen</legend>
            <div class="st-outfit__grid">
              <label class="st-outfit__option" data-outfit="book">
                <input type="radio" class="st-outfit__input" name="st-outfit" value="book" />
                <span class="st-outfit__media">
                  <img class="st-outfit__img" src="${this._outfitImage()}" alt="" loading="lazy" decoding="async" />
                  <span class="st-outfit__tag">Aus dem Buch</span>
                  <span class="st-outfit__check" aria-hidden="true">${ICON_CHECK_WHITE}</span>
                </span>
                <span class="st-outfit__body">
                  <span class="st-outfit__title">Buch-Outfit</span>
                  <span class="st-outfit__desc">So, wie die Geschichte gezeichnet ist</span>
                  <span class="st-outfit__price" data-tone="included">Inklusive</span>
                </span>
              </label>

              <label class="st-outfit__option" data-outfit="photo">
                <input type="radio" class="st-outfit__input" name="st-outfit" value="photo" />
                <span class="st-outfit__media">
                  <img class="st-outfit__img st-outfit__img--photo" src="" alt="" decoding="async" />
                  <span class="st-outfit__tag">Von deinem Foto</span>
                  <span class="st-outfit__check" aria-hidden="true">${ICON_CHECK_WHITE}</span>
                </span>
                <span class="st-outfit__body">
                  <span class="st-outfit__title">Eigenes Outfit</span>
                  <span class="st-outfit__desc">Die Kleidung von deinem Foto</span>
                  <span class="st-outfit__price" data-tone="addon">+${fmt(outfitPrice)}</span>
                </span>
              </label>
            </div>
          </fieldset>

          <div class="st-outfit-note">
            <span class="st-outfit-note__icon" aria-hidden="true">${ICON_INFO}</span>
            <p class="st-outfit-note__text">Gesicht, Frisur und Augenfarbe übernehmen wir <strong>immer</strong> von deinem Foto. Hier wählst du nur die Kleidung.</p>
          </div>

          <p class="st-outfit-tip" hidden>📸 Am besten klappt es, wenn die Kleidung auf deinem Foto gut zu sehen ist.</p>
        </div>
        <div class="st-footer">
          <button type="button" class="st-footer__back" aria-label="Zurück">${ICON_BACK}</button>
          <button type="button" class="st-btn st-btn--orange st-btn--continue">Charakter erstellen ${ICON_ARROW_RIGHT}</button>
        </div>
      </section>`;

    // ===== Step 3: Character =====
    const screen3 = `
      <section class="st-screen st-screen--character" data-step="3" data-state="idle">
        ${header({ hideBack: true })}
        <div class="st-body">
          ${mascot}

          <div class="st-preview-card">
            <div class="st-preview-card__row">
              <div class="st-preview-card__photo">
                <img class="st-preview-card__img" src="" alt="" />
                <div class="st-preview-card__spinner"></div>
              </div>
              <div class="st-preview-card__info">
                <div class="st-preview-card__eyebrow">Bereit zum Start</div>
                <div class="st-preview-card__heading">Dein Held wartet</div>
                <div class="st-preview-card__bar"><div class="st-preview-card__fill"></div></div>
                <div class="st-preview-card__meta-row">
                  <span class="st-preview-card__meta">Starte gleich …</span>
                  <span class="st-preview-card__pct">0%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Wartezeit-Inhalte: nur im Ladezustand sichtbar (CSS in snippets/button.liquid) -->
          <div class="st-wait">
            <div class="st-wait__rating">
              <span class="st-wait__rating-pill">
                <span class="st-wait__stars" aria-hidden="true">${ICON_STARS(15)}</span>
                <span class="st-wait__rating-text">${trust.rating}/5</span>
              </span>
            </div>

            <div class="st-wait__review" aria-live="polite">
              <div class="st-wait__review-head">
                <div class="st-wait__avatar" aria-hidden="true"></div>
                <div class="st-wait__ident">
                  <div class="st-wait__name"></div>
                  <span class="st-wait__stars-sm" aria-hidden="true">${ICON_STARS(11)}</span>
                </div>
              </div>
              <p class="st-wait__quote"></p>
            </div>

            <!-- Buchseiten aus "Blick ins Buch" (_waitPageSources), rotieren mit
                 den Bewertungen. Bleibt verborgen, wenn die Section fehlt. -->
            <figure class="st-wait__page" hidden>
              <div class="st-wait__page-frame">
                <img class="st-wait__page-img" src="" alt="" decoding="async" />
              </div>
              <figcaption class="st-wait__page-caption">${ICON_BOOK}<span>So sieht dein Buch aus</span></figcaption>
            </figure>

            <div class="st-wait__chips">
              ${WAIT_TRUST_CHIPS.map(c => `<div class="st-wait__chip"><span class="st-wait__chip-icon">${c.icon}</span>${c.label}</div>`).join('')}
            </div>
          </div>

          <!-- Qualitaets-Feedback zu JEDER fertigen Vorschau. Vorher gab es nur
               den Link "Gefaellt dir das Ergebnis nicht?", der erst nach einem
               zweiten Versuch auftauchte - damit war messbar, wer sich beschwert,
               aber nie, wie oft die Aehnlichkeit einfach sitzt. -->
          <div class="st-rate" data-answered="false">
            <div class="st-rate__question">Trifft es dein Kind?</div>
            <div class="st-rate__row">
              <button type="button" class="st-rate__btn" data-rating="up">
                <span class="st-rate__emoji" aria-hidden="true">👍</span><span class="st-rate__btn-label">Ja, erkenne ich!</span>
              </button>
              <button type="button" class="st-rate__btn" data-rating="down">
                <span class="st-rate__emoji" aria-hidden="true">👎</span>Noch nicht
              </button>
            </div>
            <div class="st-rate__thanks" role="status"></div>
          </div>

          <div class="st-preview-actions">
            <button type="button" class="st-action-btn st-action-btn--retry">
              ${ICON_REFRESH}
              <span class="st-action-btn__label">Nochmal</span>
            </button>
            <label class="st-action-btn st-action-btn--restart">
              <input type="file" class="st-restart-input" accept="image/*" />
              ${ICON_UPLOAD_ARROW}
              <span class="st-action-btn__label">Neues Foto</span>
            </label>
          </div>

          <!-- Nur bei PHOTO_OUTFIT_REJECTED sichtbar (data-error-code="outfit-rejected"
               am Screen, siehe _showOutfitRejected). Ersetzt dann Aktionsleiste
               und Footer-Button. Das Datei-Feld bleibt fokussierbar, damit der
               Upload auch per Tastatur erreichbar ist. -->
          <div class="st-outfit-reject">
            <p class="st-outfit-reject__text" role="alert"></p>
            <div class="st-outfit-reject__actions">
              <label class="st-btn st-btn--orange st-outfit-reject__btn st-outfit-reject__btn--photo">
                <input type="file" class="st-outfit-reject__input" accept="image/*" />
                <span class="st-outfit-reject__label">Anderes Foto hochladen</span>
              </label>
              <button type="button" class="st-btn st-btn--secondary st-outfit-reject__btn st-outfit-reject__btn--book">Outfit aus dem Buch nehmen</button>
            </div>
          </div>

          <!-- Erscheint erst, wenn "Nochmal" aufgebraucht ist: vorher stand hier
               ein "· 1/2"-Zaehler, der schon beim ersten Blick Knappheit
               kommuniziert hat. Der Hinweis zeigt stattdessen den Weg weiter. -->
          <p class="st-retry-hint" hidden></p>

          <p class="st-upload-error" role="alert" hidden></p>
        </div>
        <div class="st-footer">
          <button type="button" class="st-btn st-btn--orange st-btn--continue" disabled>Charakter erstellen</button>
        </div>
      </section>`;

    // ===== Step 4: Book preview generation =====
    const screen4 = `
      <section class="st-screen st-screen--book-preview" data-step="4" data-state="idle">
        ${header({ hideBack: true })}
        <div class="st-body">
          ${mascot}

          <div class="st-preview-card">
            <div class="st-preview-card__row">
              <div class="st-preview-card__photo">
                <img class="st-preview-card__img" src="" alt="" />
                <div class="st-preview-card__spinner"></div>
              </div>
              <div class="st-preview-card__info">
                <div class="st-preview-card__eyebrow">Bereit zum Start</div>
                <div class="st-preview-card__heading">Dein Buch wartet</div>
                <div class="st-preview-card__bar"><div class="st-preview-card__fill"></div></div>
                <div class="st-preview-card__meta-row">
                  <span class="st-preview-card__meta">Starte gleich …</span>
                  <span class="st-preview-card__pct">0%</span>
                </div>
              </div>
            </div>
          </div>

          <div class="st-preview-result">
            <img class="st-preview-result__img" src="" alt="" />
            <div class="st-preview-result__overlay">
              <div class="st-preview-result__title"></div>
              <div class="st-preview-result__subtitle">Schnellvorschau &bull; Dein gedrucktes Buch wird wunderschön detailliert</div>
            </div>
          </div>
        </div>
        <div class="st-footer">
          <button type="button" class="st-btn st-btn--orange st-btn--continue" disabled>Weiter ${ICON_ARROW_RIGHT}</button>
        </div>
      </section>`;

    // ===== Step 5: Binding =====
    const screen5 = `
      <section class="st-screen st-screen--binding" data-step="5">
        ${header({ hideBack: true })}
        <div class="st-body">
          ${mascot}

          <!-- Softcover ist vorausgewaehlt (Einstiegspreis, Clarity: der "ab"-Preis
               passte nicht zum vorher oben stehenden Hardcover). Hardcover steht
               gleichwertig daneben und nennt nur den Aufpreis. -->
          <div class="st-binding-grid">
            <button type="button" class="st-binding" data-variant="softcover"
              data-selected="${this.bookCoverType === 'softcover'}" aria-pressed="${this.bookCoverType === 'softcover'}">
              <div class="st-binding__image">
                <img src="${softcoverImage}" alt="" loading="lazy" decoding="async" />
              </div>
              <div class="st-binding__body">
                <div class="st-binding__title">Softcover</div>
                <div class="st-binding__price">${this._bindingPriceHtml(softcoverPrice, softcoverComparePrice)}</div>
                <div class="st-binding__desc">Leicht &amp; handlich - ideal für unterwegs und kleine Kinderhände.</div>
              </div>
            </button>

            <button type="button" class="st-binding" data-variant="hardcover"
              data-selected="${this.bookCoverType === 'hardcover'}" aria-pressed="${this.bookCoverType === 'hardcover'}">
              <div class="st-binding__badge">Meistgewählt</div>
              <div class="st-binding__image">
                <img src="${hardcoverImage}" alt="" loading="lazy" decoding="async" />
              </div>
              <div class="st-binding__body">
                <div class="st-binding__title">Hardcover</div>
                <div class="st-binding__price">${this._bindingUpgradeHtml()}</div>
                <div class="st-binding__desc">Gemacht für die hundertste Gutenachtgeschichte - zum immer wieder Lesen und für immer Behalten.</div>
              </div>
            </button>
          </div>
        </div>
        <div class="st-footer">
          <button type="button" class="st-footer__back" aria-label="Zurück">${ICON_BACK}</button>
          <button type="button" class="st-btn st-btn--orange st-btn--continue" disabled>Weiter ${ICON_ARROW_RIGHT}</button>
        </div>
      </section>`;

    // ===== Step 6: Extras / upsell =====
    // Both add-ons are offered here instead of on the review step, so the review
    // step only has to confirm what was chosen.
    const dedicationPrice = this.dedicationPrice || 3.99;
    const mattPrice = this.mattPrice || 1.99;
    const dedicationCompare = this.dedicationComparePrice || 0;
    const mattCompare = this.mattComparePrice || 0;

    /**
     * @param {{ key: string, image: string, badge: string, title: string, desc: string,
     *           price: number, comparePrice: number, proof: string, proofIcon: string,
     *           tone: string, extra?: string }} o
     */
    const extraCard = (o) => `
      <article class="st-extra" data-extra="${o.key}" data-selected="false">
        <div class="st-extra__media">
          <span class="st-extra__badge">${o.badge}</span>
          <img src="${o.image}" alt="" loading="lazy" decoding="async" />
        </div>
        <div class="st-extra__head">
          <div class="st-extra__copy">
            <div class="st-extra__title">${o.title}</div>
            <div class="st-extra__desc">${o.desc}</div>
          </div>
          <div class="st-extra__prices">
            ${o.comparePrice > o.price ? `<span class="st-extra__was">${fmt(o.comparePrice)}</span>` : ''}
            <span class="st-extra__now">+${fmt(o.price)}</span>
          </div>
        </div>
        <span class="st-extra__proof" data-tone="${o.tone}">${o.proofIcon}${o.proof}</span>
        ${o.extra || ''}
        <button type="button" class="st-extra__toggle" aria-pressed="false">
          <span class="st-extra__check">${ICON_CHECK_WHITE}</span>
          <span class="st-extra__toggle-label">Hinzufügen</span>
        </button>
      </article>`;

    // The dedication needs its wording before it can become a line item, so the
    // card reveals a textarea once it is selected.
    const dedicationField = `
      <div class="st-dedication">
        <label class="st-dedication__label" for="st-dedication-text">Deine Widmung</label>
        <textarea id="st-dedication-text" class="st-dedication__input" rows="3" maxlength="200"
          placeholder="z. B. Für Max - unseren größten Helden. Mama &amp; Papa"></textarea>
        <div class="st-dedication__meta">
          <span class="st-dedication__hint" data-error="false">Erscheint auf der ersten Seite deines Buches.</span>
          <span class="st-dedication__count">0/200</span>
        </div>
      </div>`;

    const screen6 = `
      <section class="st-screen st-screen--extras" data-step="6">
        ${header()}
        <div class="st-body">
          ${mascot}

          ${extraCard({
            key: 'dedication',
            image: this._extraImage('dedication'),
            badge: 'Die erste Seite im Buch',
            title: 'Persönliche Widmung',
            desc: 'Deine Worte auf der ersten Seite - dein Kind liest sie noch in Jahren.',
            price: dedicationPrice,
            comparePrice: dedicationCompare,
            proof: '80% nehmen diese Option',
            proofIcon: ICON_HEART,
            tone: 'orange',
            extra: dedicationField,
          })}

          ${extraCard({
            key: 'matt',
            image: this._extraImage('matt'),
            badge: 'Premium-Cover-Upgrade',
            title: 'Matt &amp; Kratzfest',
            desc: 'Samtmattes, kratzfestes Cover - fühlt sich edel an und übersteht das hundertste Vorlesen.',
            price: mattPrice,
            comparePrice: mattCompare,
            proof: 'Hält ein Kinderleben lang',
            proofIcon: ICON_SHIELD_CHECK,
            tone: 'green',
          })}

          <p class="st-extras-skip">Kein Extra? Kein Problem - tippe einfach auf Weiter.</p>

          <!-- Scrolls with the cards instead of sitting in a sticky footer, so
               nothing covers the second extra. -->
          <div class="st-extras-actions">
            <button type="button" class="st-footer__back" aria-label="Zurück">${ICON_BACK}</button>
            <button type="button" class="st-btn st-btn--orange st-btn--continue">Weiter ${ICON_ARROW_RIGHT}</button>
          </div>
        </div>
      </section>`;

    // ===== Step 7: Review =====
    const screen7 = `
      <section class="st-screen st-screen--review" data-step="7">
        ${header()}
        <div class="st-body">
          ${mascot}

          <div class="st-summary">
            <div class="st-summary__top">
              <div class="st-summary__cover">
                <img class="st-summary__cover-img st-summary__cover-img--character" src="" alt="" />
                ${bookPeek}
              </div>
              <div class="st-summary__meta">
                <div class="st-summary__title"></div>
                <div class="st-summary__rating">
                  <span class="st-summary__stars" aria-hidden="true">${ICON_STARS(12)}</span>
                  <span class="st-summary__rating-value">${trust.rating}/5</span>
                </div>
                <div class="st-summary__row"><span>Name</span><strong class="st-summary__name">-</strong></div>
                <div class="st-summary__row"><span>Geschlecht</span><strong class="st-summary__gender">-</strong></div>
                <div class="st-summary__row"><span>Augenfarbe</span><strong class="st-summary__eye">-</strong></div>
                <div class="st-summary__row"><span>Einband</span><strong class="st-summary__binding">Hardcover</strong></div>
                ${STARTALES_OUTFIT_ENABLED ? '<div class="st-summary__row"><span>Outfit</span><strong class="st-summary__outfit">Buch-Outfit</strong></div>' : ''}
              </div>
            </div>
            <hr class="st-divider" />
            <div class="st-summary__lines">
              <div class="st-line">
                <div>
                  <div class="st-line__label st-summary__product-label">Personalisiertes Buch</div>
                  <div class="st-line__sub st-summary__binding-sub">Hardcover</div>
                </div>
                <div class="st-line__right">
                  <div class="st-line__price st-summary__book-price"></div>
                  <div class="st-line__compare st-summary__book-compare" style="display:none"></div>
                </div>
              </div>
              <div class="st-line st-summary__outfit-line" style="display:none">
                <div>
                  <div class="st-line__label">Eigenes Outfit</div>
                  <div class="st-line__sub">Kleidung von deinem Foto</div>
                </div>
                <div class="st-line__right">
                  <div class="st-line__price">+${fmt(outfitPrice)}</div>
                </div>
              </div>
              <div class="st-line st-summary__dedication-line" style="display:none">
                <div>
                  <div class="st-line__label">Persönliche Widmung</div>
                  <div class="st-line__sub">Extra</div>
                </div>
                <div class="st-line__right">
                  <div class="st-line__price">+${fmt(dedicationPrice)}</div>
                </div>
              </div>
              <div class="st-line st-summary__matt-line" style="display:none">
                <div>
                  <div class="st-line__label">Matt &amp; Kratzfest</div>
                  <div class="st-line__sub">Extra</div>
                </div>
                <div class="st-line__right">
                  <div class="st-line__price">+${fmt(mattPrice)}</div>
                </div>
              </div>
              <div class="st-line st-summary__next-line" style="display:none">
                <div>
                  <div class="st-line__label">Rabatt 2. Buch</div>
                  <div class="st-line__sub st-summary__next-sub"></div>
                </div>
                <div class="st-line__right">
                  <div class="st-line__price st-line__price--green st-summary__next-price"></div>
                </div>
              </div>
              <div class="st-line">
                <div>
                  <div class="st-line__label">Versand</div>
                  <div class="st-line__sub">Innerhalb Deutschlands</div>
                </div>
                <div class="st-line__right">
                  <div class="st-line__price st-line__price--green">Kostenlos</div>
                </div>
              </div>
              <div class="st-delivery">
                <span class="st-delivery__icon" aria-hidden="true">${ICON_CALENDAR_CHECK}</span>
                <span class="st-delivery__text">Lieferung <strong>${trust.deliveryRange}</strong></span>
              </div>
            </div>
            <hr class="st-divider" />
            <div class="st-total">
              <div>
                <div class="st-total__label">Gesamt</div>
                <div class="st-total__save" style="display:none"></div>
              </div>
              <div class="st-total__amount"></div>
            </div>
          </div>

          ${this._nextBookUpsellHtml()}

          <div class="st-review-card">
            <div class="st-review-card__head">
              <div class="st-review-card__avatar" aria-hidden="true">${trust.reviewInitial}</div>
              <div class="st-review-card__ident">
                <div class="st-review-card__line">
                  <span class="st-review-card__name">${trust.reviewAuthor}</span>
                  <span class="st-review-card__verified">${ICON_CHECK_SM}Verifiziert</span>
                </div>
                <span class="st-review-card__stars" aria-hidden="true">${ICON_STARS(13)}</span>
              </div>
            </div>
            <p class="st-review-card__quote">${trust.reviewQuote}</p>
          </div>

          <div class="st-trust">
            <div class="st-trust__icon">${ICON_SHIELD_TRUST}</div>
            <div>
              <div class="st-trust__title">30 Tage Geld-zurück-Garantie</div>
              <div class="st-trust__text">Gefällt dir das Buch nicht? Geld zurück - ohne Wenn und Aber.</div>
            </div>
          </div>

          <div class="st-pillars">
            <div class="st-pillar"><span class="st-pillar__icon">${ICON_TRUCK}</span><span class="st-pillar__label">Kostenloser<br>Versand</span></div>
            <div class="st-pillar"><span class="st-pillar__icon">${ICON_SHIELD_PILLAR}</span><span class="st-pillar__label">30 Tage<br>Garantie</span></div>
            <div class="st-pillar"><span class="st-pillar__icon">${ICON_LOCK(24)}</span><span class="st-pillar__label">Sichere<br>Zahlung</span></div>
          </div>

          <div class="st-pay">
            <p class="st-pay__title"><span class="st-pay__lock">${ICON_LOCK(15)}</span>Sichere &amp; SSL-verschlüsselte Zahlung</p>
            <ul class="st-pay__list" role="list">${trust.paymentIcons}</ul>
          </div>
        </div>
        <div class="st-footer st-footer--review">
          <!-- Kauf-Fehler werden hier sichtbar statt in einem nativen alert():
               Session-Recordings (Clarity) zeichnen native Dialoge nicht auf, ein
               harter Fehler sah dort aus wie ein Button, der nichts tut. -->
          <div class="st-cart-error" role="alert" hidden>
            <p class="st-cart-error__text"></p>
            <div class="st-cart-error__actions">
              <button type="button" class="st-cart-error__btn st-cart-error__retry">Nochmal versuchen</button>
              <button type="button" class="st-cart-error__btn st-cart-error__skip" hidden>Ohne Extras zur Kasse</button>
            </div>
          </div>
          <div class="st-footer__row">
            <button type="button" class="st-footer__back" aria-label="Zurück">${ICON_BACK}</button>
            <button type="button" class="st-btn st-btn--orange st-btn--buy">${ICON_CART} Sicher zur Kasse</button>
          </div>
          <button type="button" class="st-footer__solo" hidden>Nur dieses Buch kaufen</button>
          <p class="st-footer__note">${ICON_LOCK(13)}SSL-gesicherte Zahlung &middot; 30 Tage Geld-zurück</p>
        </div>
      </section>`;

    // SCENE_GEN_DISABLED: screen 4 is excluded. Re-add to restore.
    // Das Maskottchen-Sprite muss vor dem ersten <use> stehen.
    return STAR_MASCOT_SPRITE + preheader + screen1 + screen2
      + (STARTALES_OUTFIT_ENABLED ? screenOutfit : '')
      + screen3 + /* screen4 + */ screen5 + screen6 + screen7;
  }

  // ---------------------------------------------------------------------------
  // Event wiring
  // ---------------------------------------------------------------------------

  _wireEvents(overlay) {
    // Header close (all screens) — asks for confirmation once there is progress
    overlay.querySelectorAll('.st-header__close').forEach(btn => {
      btn.addEventListener('click', () => this._requestClose(overlay));
    });

    // Header back (all screens)
    overlay.querySelectorAll('.st-header__back').forEach(btn => {
      btn.addEventListener('click', () => this._goBack(overlay));
    });

    // Footer back (steps 2..5)
    overlay.querySelectorAll('.st-footer__back').forEach(btn => {
      btn.addEventListener('click', () => this._goBack(overlay));
    });

    // Step 1: file input → upload
    const fileInput = /** @type {HTMLInputElement | null} */ (overlay.querySelector('.st-upload-input'));
    if (fileInput) {
      fileInput.addEventListener('change', async () => {
        const file = fileInput.files && fileInput.files[0];
        // Der Wert MUSS am Ende geleert werden: sonst loest exakt dasselbe Foto
        // beim zweiten Mal kein change-Event aus und der Kasten wirkt tot.
        try {
          await this._acceptPhoto(file, overlay, (f) => this._handleUpload(f, overlay));
        } finally {
          fileInput.value = '';
        }
      });

      // Step 2: example card and photo guide double as entry points into the
      // file picker - the upload CTA itself is easy to miss on small screens.
      // Die Maskottchen-Blase ("Ein klares Foto ...") ebenso: Clarity zeigte
      // dort gehaeufte tote Klicks. Sie bekommt bewusst keine Button-Rolle,
      // ihr Text bleibt der Hinweis des Schritts; Tastatur nutzt Guide/CTA.
      const uploadMascot = overlay.querySelector('.st-screen--upload .st-mascot');
      if (uploadMascot) {
        uploadMascot.classList.add('st-mascot--picker');
        uploadMascot.addEventListener('click', () => fileInput.click());
      }
      const pickerProxies = overlay.querySelectorAll('.st-screen--upload .st-example-card, .st-screen--upload .st-guide');
      pickerProxies.forEach(/** @param {Element} el */ (el) => {
        el.addEventListener('click', () => fileInput.click());
        el.addEventListener('keydown', (/** @type {Event} */ e) => {
          const key = /** @type {KeyboardEvent} */ (e).key;
          if (key === 'Enter' || key === ' ') {
            e.preventDefault();
            fileInput.click();
          }
        });
      });
    }

    // Step 1: eye color
    overlay.querySelectorAll('.st-screen--details .st-eye-option').forEach(opt => {
      opt.addEventListener('click', () => {
        overlay.querySelectorAll('.st-screen--details .st-eye-option').forEach(o => o.setAttribute('data-selected', 'false'));
        opt.setAttribute('data-selected', 'true');
        this.eyeColor = opt.getAttribute('data-color-label') || '';
        const eyeField = opt.closest('.st-field--eyes');
        if (eyeField) eyeField.setAttribute('data-error', 'false');
        this._validateStep(overlay);
      });
    });

    // Step 2: consent
    const consent = overlay.querySelector('.st-screen--upload .st-consent');
    if (consent) {
      consent.addEventListener('click', (e) => {
        const target = /** @type {Element} */ (e.target);
        if (target.closest('a')) return;
        e.preventDefault();
        this.photoConfirmed = !this.photoConfirmed;
        consent.setAttribute('data-checked', this.photoConfirmed ? 'true' : 'false');
        // Antippen räumt den roten Hinweis sofort weg.
        this._clearConsentError(overlay);
        const input = /** @type {HTMLInputElement | null} */ (consent.querySelector('.st-consent__input'));
        if (input) input.checked = this.photoConfirmed;
        this._validateStep(overlay);
      });

      const policyLink = /** @type {HTMLAnchorElement | null} */ (consent.querySelector('.st-consent__text a'));
      if (policyLink) {
        policyLink.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._openConsentDetailsLightbox(policyLink.getAttribute('href') || '/policies/privacy-policy');
        });
      }
    }

    // Step 2.5: outfit. Native radios, so arrow keys work without extra code.
    overlay.querySelectorAll('.st-screen--outfit .st-outfit__input').forEach(/** @param {Element} el */ (el) => {
      const input = /** @type {HTMLInputElement} */ (el);
      input.addEventListener('change', () => {
        if (!input.checked) return;
        this._setOutfit(overlay, input.value === 'photo' ? 'photo' : 'book');
        _stTrack('outfit_selected', {
          step: String(STEP_OUTFIT),
          product_id: this.getAttribute('data-product-id') || undefined,
          meta: { outfit: this.outfitChoice },
        });
      });
    });

    // Step 1: name input
    const nameInput =/** @type {HTMLInputElement | null} */ (overlay.querySelector('.st-name-input'));
    if (nameInput) {
      nameInput.addEventListener('input', () => {
        this.childName = nameInput.value.trim();
        const nameField = nameInput.closest('.st-field--name');
        if (nameField) nameField.setAttribute('data-error', 'false');
        // Live-update heading once the character preview is ready
        const charScreen = overlay.querySelector('.st-screen--character');
        if (charScreen && charScreen.getAttribute('data-state') === 'done') {
          const heading = charScreen.querySelector('.st-preview-card__heading');
          if (heading) heading.textContent = `Hallo, ${this.childName || '...'}!`;
        }
        this._validateStep(overlay);
      });
      nameInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const btn = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('.st-screen--details .st-btn--continue'));
          if (btn && !btn.disabled) btn.click();
        }
      });
    }

    // Step 3: try again - re-run preview with the same image/eye color/gender
    const retryBtn = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('.st-action-btn--retry'));
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        if (this.retryCount >= this.retryMax) return;
        this.retryCount += 1;
        this._updateRetryState(overlay);
        this._restartCharacterPreview(overlay);
      });
    }

    // Step 3: start over - pick a new photo and re-run the preview
    const restartInput = /** @type {HTMLInputElement | null} */ (overlay.querySelector('.st-restart-input'));
    if (restartInput) {
      restartInput.addEventListener('change', async () => {
        const file = restartInput.files && restartInput.files[0];
        try {
          await this._acceptPhoto(file, overlay, async (f) => {
                await this._handleRestartUpload(f, overlay);
          });
        } finally {
          restartInput.value = '';
        }
      });
    }

    // Step 3, Outfit vom Foto abgelehnt: anderes Foto (Outfit bleibt "photo")
    // oder dasselbe Foto mit dem Buch-Outfit.
    const rejectInput = /** @type {HTMLInputElement | null} */ (overlay.querySelector('.st-outfit-reject__input'));
    if (rejectInput) {
      rejectInput.addEventListener('change', async () => {
        const file = rejectInput.files && rejectInput.files[0];
        try {
          await this._acceptPhoto(file, overlay, async (f) => {
            await this._handleOutfitRejectedUpload(f, overlay);
          });
        } finally {
          rejectInput.value = '';
        }
      });
    }
    const rejectBookBtn = overlay.querySelector('.st-outfit-reject__btn--book');
    if (rejectBookBtn) {
      rejectBookBtn.addEventListener('click', () => this._retryWithBookOutfit(overlay));
    }

    // Step 1: gender
    overlay.querySelectorAll('.st-screen--details .st-gender').forEach(opt => {
      opt.addEventListener('click', () => {
        const newGender = opt.getAttribute('data-value') || '';
        overlay.querySelectorAll('.st-screen--details .st-gender').forEach(o => o.setAttribute('data-selected', 'false'));
        opt.setAttribute('data-selected', 'true');
        this.gender = newGender;
        const field = opt.closest('.st-field--gender');
        if (field) field.setAttribute('data-error', 'false');
        this._syncGenderHint(overlay);
        if (this._genderMismatch()) {
          _stTrack('gender_mismatch_shown', {
            step: '1',
            product_id: this.getAttribute('data-product-id') || undefined,
            meta: { gender: this.gender, target_gender: this._targetGender() },
          });
        }
        this._validateStep(overlay);
      });
    });

    // Step 1: switch to the book that fits the chosen gender. A plain link -
    // the handoff only adds the details already entered.
    overlay.querySelector('.st-gender-hint__switch')?.addEventListener('click', () => {
      _stTrack('gender_mismatch_switch', {
        step: '1',
        product_id: this.getAttribute('data-product-id') || undefined,
        meta: { gender: this.gender, target_gender: this._targetGender() },
      });
      this._saveHandoff();
    });

    // Step 4: binding
    overlay.querySelectorAll('.st-screen--binding .st-binding').forEach(card => {
      card.addEventListener('click', () => {
        const next = card.getAttribute('data-variant') || '';
        if (!next) return;
        const changed = this.bookCoverType !== next;
        overlay.querySelectorAll('.st-screen--binding .st-binding').forEach(c => {
          c.setAttribute('data-selected', 'false');
          c.setAttribute('aria-pressed', 'false');
        });
        card.setAttribute('data-selected', 'true');
        card.setAttribute('aria-pressed', 'true');
        this.bookCoverType = next;
        if (changed) {
          _stTrack('cover_selected', {
            step: '5',
            product_id: this.getAttribute('data-product-id') || undefined,
            meta: { cover: next },
          });
        }
        this._saveCharacterToStorage();
        this._validateStep(overlay);
      });
    });

    // Step 5: tap the character image to open a lightbox preview
    // getAttribute statt .src: ein leeres src-Attribut liefert als .src die
    // Seiten-URL - die Lightbox oeffnete dann ein kaputtes Bild (toter Klick).
    overlay.querySelectorAll('.st-screen--review .st-summary__cover-img').forEach((/** @type {Element} */ img) => {
      img.addEventListener('click', () => {
        const el = /** @type {HTMLImageElement} */ (img);
        const src = el.getAttribute('src');
        if (!src) return;
        this._openLightbox(src, el.alt);
      });
    });

    // Step 7: "Blick ins Buch" - Leseprobe der Produktseite noch einmal zeigen
    overlay.querySelector('[data-st-book-peek]')?.addEventListener('click', () => {
      this._openBookPeek(overlay);
    });

    // Step 3: tap generated character image to open the same lightbox preview
    const charImg = /** @type {HTMLImageElement | null} */ (overlay.querySelector('.st-screen--character .st-preview-card__img'));
    if (charImg) {
      charImg.addEventListener('click', () => {
        const screen = overlay.querySelector('.st-screen--character');
        if (!screen || screen.getAttribute('data-state') !== 'done') return;
        const src = charImg.getAttribute('src');
        if (!src) return;
        this._openLightbox(src, charImg.alt);
      });
    }

    // Step 3: Aehnlichkeit bewerten. Delegiert, weil die Zeile bei jeder neuen
    // Generierung zurueckgesetzt wird - die Buttons selbst bleiben dieselben.
    const rate = overlay.querySelector('.st-screen--character .st-rate');
    if (rate) {
      rate.addEventListener('click', (e) => {
        const btn = /** @type {Element} */ (e.target).closest('.st-rate__btn');
        if (!btn) return;
        this._submitCharacterRating(btn.getAttribute('data-rating') === 'up' ? 'up' : 'down', overlay);
      });
    }

    // Step 6: extras. Each card owns one boolean; the dedication additionally
    // needs its text before it can become a line item.
    overlay.querySelectorAll('.st-screen--extras .st-extra').forEach(/** @param {Element} card */ (card) => {
      const toggle = /** @type {HTMLButtonElement | null} */ (card.querySelector('.st-extra__toggle'));
      if (!toggle) return;
      // Tapping the button while the dedication textarea is focused closes the
      // on-screen keyboard anyway - without this guard that same tap would also
      // deselect the add-on, so the customer loses the dedication they just
      // typed. The state has to be read on pointerdown: by the time the click
      // arrives the field has already lost focus.
      let keyboardTap = false;
      toggle.addEventListener('pointerdown', () => {
        keyboardTap = this._dedicationKeyboardOpen(overlay);
      });
      toggle.addEventListener('click', () => {
        const key = card.getAttribute('data-extra') || '';
        if (keyboardTap) {
          keyboardTap = false;
          const field = /** @type {HTMLTextAreaElement | null} */ (overlay.querySelector('.st-dedication__input'));
          if (field) field.blur();
          return;
        }
        const enabled = card.getAttribute('data-selected') !== 'true';
        this._setExtraSelected(overlay, key, enabled);
        if (enabled && key === 'dedication') {
          const field = /** @type {HTMLTextAreaElement | null} */ (overlay.querySelector('.st-dedication__input'));
          if (field) {
            // iOS only opens the keyboard for a focus() that still runs inside
            // the tap - a setTimeout would focus the field silently. The card
            // was just switched to selected, so read a layout property first to
            // flush the display:none that would make the field unfocusable.
            void field.offsetHeight;
            // preventScroll: sonst zieht der Fokus selbst wieder den
            // Dokument-Scroller mit und schiebt das fixierte Overlay hoch.
            field.focus({ preventScroll: true });
            setTimeout(() => this._scrollInOverlay(field, 'center'), 120);
          }
        }
        _stTrack('customization_changed', {
          step: '6',
          product_id: this.getAttribute('data-product-id') || undefined,
          meta: { field: key === 'dedication' ? 'dedication' : 'matt_cover', enabled },
        });
      });
    });

    const dedicationInput = /** @type {HTMLTextAreaElement | null} */ (overlay.querySelector('.st-dedication__input'));
    if (dedicationInput) {
      dedicationInput.addEventListener('input', () => {
        this.dedicationText = dedicationInput.value.trim();
        // Typing clears the reminder immediately; it comes back only if the
        // customer empties the field and tries to continue again.
        if (this.dedicationText) this._dedicationErrorShown = false;
        this._updateDedicationField(overlay);
        this._validateStep(overlay);
      });
      // Tapping inside the field must not bubble up into the card's toggle.
      dedicationInput.addEventListener('click', (e) => e.stopPropagation());
    }

    // Continue buttons (per screen)
    overlay.querySelectorAll('.st-screen--details .st-btn--continue').forEach(btn => {
      btn.addEventListener('click', () => {
        // Der Button ist nur optisch gesperrt: ein Klick benennt die fehlende
        // Angabe, statt wirkungslos zu verpuffen.
        if (!this._detailsComplete()) {
          this._flagMissingDetails(overlay);
          return;
        }
        this._next(overlay);
      });
    });

    overlay.querySelectorAll('.st-screen--upload .st-btn--continue').forEach(btn => {
      btn.addEventListener('click', () => {
        // Der Button ist nur optisch gesperrt: statt ins Leere zu klicken landet
        // der Kunde im Datei-Dialog bzw. bei der übersehenen Checkbox.
        if (!this.uploadedImageBlob) {
          const input = /** @type {HTMLInputElement | null} */ (overlay.querySelector('.st-upload-input'));
          if (input) input.click();
          return;
        }
        if (!this.photoConfirmed) {
          this._flagConsentMissing(overlay);
          return;
        }
        _stTrack('consent_given', {
          step: '2',
          product_id: this.getAttribute('data-product-id') || undefined,
          meta: { eye_color: this.eyeColor },
        });
        this._next(overlay);
      });
    });

    overlay.querySelectorAll('.st-screen--outfit .st-btn--continue').forEach(/** @param {Element} btn */ (btn) => {
      btn.addEventListener('click', () => this._next(overlay));
    });

    const charContinue = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('.st-screen--character .st-btn--continue'));
    if (charContinue) {
      charContinue.addEventListener('click', () => this._onCharacterContinueClick(overlay));
    }

    overlay.querySelectorAll('.st-screen--book-preview .st-btn--continue').forEach(/** @param {Element} btn */ (btn) => {
      btn.addEventListener('click', () => this._next(overlay));
    });

    overlay.querySelectorAll('.st-screen--binding .st-btn--continue').forEach(btn => {
      btn.addEventListener('click', () => this._next(overlay));
    });

    overlay.querySelectorAll('.st-screen--extras .st-btn--continue').forEach(/** @param {Element} btn */ (btn) => {
      btn.addEventListener('click', () => {
        // An empty dedication would silently drop out of the cart, so it is the
        // one thing that blocks here - and only now does the hint turn red.
        if (this.dedicationEnabled && !this.dedicationText) {
          this._dedicationErrorShown = true;
          this._updateDedicationField(overlay);
          const field = /** @type {HTMLTextAreaElement | null} */ (overlay.querySelector('.st-dedication__input'));
          if (field) {
            this._scrollInOverlay(field, 'center');
            field.focus({ preventScroll: true });
          }
          return;
        }
        this._next(overlay);
      });
    });

    // "Jetzt kaufen": adds the line items and navigates straight to checkout.
    const buyBtn = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('.st-screen--review .st-btn--buy'));
    if (buyBtn) {
      buyBtn.addEventListener('click', () => {
        // Nach einem abgeschlossenen Submit liegt alles schon im Warenkorb -
        // erneutes Hinzufuegen wuerde ein zweites Buch bestellen.
        if (this._cartSubmitted) {
          window.location.href = '/checkout';
          return;
        }
        // Bundle mit weiterem Buch: der Button fuehrt zum gewaehlten naechsten Buch.
        if (this._bundleHasNext()) {
          this._startNextBook(this._bundlePick, buyBtn);
          return;
        }
        this.addToCart({ redirect: true, container: overlay, triggerBtn: buyBtn });
      });
    }

    // Bundle: doch kein weiteres Buch, direkt zur Kasse.
    const soloBtn = overlay.querySelector('.st-screen--review .st-footer__solo');
    if (soloBtn && buyBtn) {
      soloBtn.addEventListener('click', () => {
        _stTrack('bundle_declined', {
          step: '7',
          product_id: this.getAttribute('data-product-id') || undefined,
        });
        this.addToCart({ redirect: true, container: overlay, triggerBtn: buyBtn });
      });
    }

    // Zweites Buch: ohne Bundle legt ein Klick dieses Buch in den Warenkorb und
    // startet das naechste. Im Bundle ist es nur eine Auswahl, weiter geht es
    // ueber den Button unten.
    overlay.querySelectorAll('.st-nextbook__book').forEach((/** @type {Element} */ el) => {
      const btn = /** @type {HTMLButtonElement} */ (el);
      btn.addEventListener('click', () => {
        const index = Number(btn.dataset.index) || 0;
        if (this._bundleHasNext()) {
          this._bundlePick = index;
          this._renderNextBookState(overlay);
          return;
        }
        this._startNextBook(index, btn);
      });
    });

    const addBtn = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('.st-screen--review .st-btn--add'));
    if (addBtn) {
      addBtn.addEventListener('click', () => this.addToCart({ redirect: false, closeOverlay: true, container: overlay, triggerBtn: addBtn }));
    }

    // Fehler-Box: "Nochmal versuchen" wiederholt den Kauf (die Buch-Zeile wird
    // dabei nicht doppelt gesendet, siehe _bookLineAdded), "Ohne Extras" laesst
    // die nicht hinzufuegbaren Add-ons bewusst weg.
    const errRetry = overlay.querySelector('.st-screen--review .st-cart-error__retry');
    if (errRetry) {
      errRetry.addEventListener('click', () => {
        this._hideCartError(overlay);
        if (buyBtn) this.addToCart({ redirect: true, container: overlay, triggerBtn: buyBtn });
      });
    }
    const errSkip = overlay.querySelector('.st-screen--review .st-cart-error__skip');
    if (errSkip) {
      errSkip.addEventListener('click', () => {
        this._skipAddons = true;
        this._hideCartError(overlay);
        if (buyBtn) this.addToCart({ redirect: true, container: overlay, triggerBtn: buyBtn });
      });
    }
  }

  /**
   * Zeigt einen Kauf-Fehler im Overlay an - sichtbar fuer den Kunden und fuer
   * jede Session-Aufzeichnung.
   * @param {Element | null} container
   * @param {string} message
   * @param {{ allowSkip?: boolean }} [opts]
   */
  _showCartError(container, message, { allowSkip = false } = {}) {
    const root = container || document.querySelector('.custom-overlay');
    const box = /** @type {HTMLElement | null} */ (
      root ? root.querySelector('.st-screen--review .st-cart-error') : null
    );
    if (!box) {
      // Kein Overlay (mehr) im DOM: dann ist ein Dialog besser als gar nichts.
      alert(message);
      return;
    }
    const text = box.querySelector('.st-cart-error__text');
    if (text) text.textContent = message;
    const skip = /** @type {HTMLElement | null} */ (box.querySelector('.st-cart-error__skip'));
    if (skip) skip.hidden = !allowSkip;
    box.hidden = false;
    this._scrollInOverlay(box, 'nearest');
  }

  /** @param {Element | null} container */
  _hideCartError(container) {
    const root = container || document.querySelector('.custom-overlay');
    const box = /** @type {HTMLElement | null} */ (
      root ? root.querySelector('.st-screen--review .st-cart-error') : null
    );
    if (box) box.hidden = true;
  }

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------

  _goBack(overlay) {
    if (this.currentStep === 1) {
      this.hideOverlay(overlay);
      this._markFlowInProgress();
      return;
    }
    // Going back from step 4 (book preview) resets book preview generation
    if (this.currentStep === 4) {
      this.bookPreviewImageUrl = '';
      this._bookPreviewGen = { promise: null, progress: 0, done: false, error: null, startTime: 0 };
      const screen = overlay.querySelector('.st-screen--book-preview');
      if (screen) this._setBookPreviewState(screen, 'idle');
    }
    // Going back from step 3 resets preview generation
    if (this.currentStep === 3) {
      this.previewImageUrl = '';
      this.previewDisplayUrl = '';
      this.previewUniqid = '';
      this._detailsSubmittedFor = '';
      this._previewGen = { promise: null, progress: 0, done: false, error: null, startTime: 0 };
      this.retryCount = 1;
      this._updateRetryState(overlay);
      const screen = overlay.querySelector('.st-screen--character');
      if (screen) this._setCharacterState(screen, 'idle');
    }
    // Going back from step 2 (upload) keeps the photo — the customer only
    // returns to correct the details from step 1 and comes right back.
    // Going back from step 3 lands on the outfit step, so the outfit can be
    // changed before the next generation starts.
    const order = this._stepOrder();
    const idx = order.indexOf(this.currentStep);
    this.currentStep = idx > 0 ? order[idx - 1] : Math.max(1, Math.floor(this.currentStep) - 1);
    this._updateScreen(overlay);
  }

  _next(overlay) {
    // Steps that are switched off (book preview 4, binding 5 without
    // STARTALES_BINDING_ENABLED, outfit without STARTALES_OUTFIT_ENABLED) are
    // simply not in _stepOrder, so they are skipped here.
    const order = this._stepOrder();
    const idx = order.indexOf(this.currentStep);
    if (idx === -1 || idx >= order.length - 1) return;
    _stTrack('step_completed', {
      step: String(this.currentStep),
      screen: STEP_SLUGS[this.currentStep] || undefined,
      product_id: this.getAttribute('data-product-id') || undefined,
    });
    this.currentStep = order[idx + 1];
    this._updateScreen(overlay);
  }

  // ---------------------------------------------------------------------------
  // Screen + stepper updates
  // ---------------------------------------------------------------------------

  _updateScreen(overlay, { initial = false } = {}) {
    if (this._lastViewedStep !== this.currentStep) {
      _stTrack('step_viewed', {
        step: String(this.currentStep),
        screen: STEP_SLUGS[this.currentStep] || undefined,
        product_id: this.getAttribute('data-product-id') || undefined,
      });
      this._lastViewedStep = this.currentStep;
    }

    // Activate the right screen
    overlay.querySelectorAll('.st-screen').forEach(screen => {
      // parseFloat: the outfit step is 2.5.
      const stepNum = parseFloat(screen.getAttribute('data-step') || '0');
      screen.classList.toggle('st-screen--active', stepNum === this.currentStep);
    });

    // Contiguous visible position: skipped steps (e.g. 4) are not counted, so
    // the stepper never shows a gap.
    const visibleStep = Math.max(1, this._stepOrder().indexOf(this.currentStep) + 1);

    // Update all headers (stepper, title)
    overlay.querySelectorAll('.st-header').forEach(header => {
      // Stepper dots
      header.querySelectorAll('.st-stepper span').forEach((dot, idx) => {
        dot.classList.remove('is-active', 'is-done');
        const step = idx + 1;
        if (step < visibleStep) dot.classList.add('is-done');
        if (step === visibleStep) dot.classList.add('is-active');
      });

      // Caption
      const stepEl = header.querySelector('.st-stepper-caption__step');
      const pctEl = header.querySelector('.st-stepper-caption__pct');
      if (stepEl) stepEl.textContent = `Schritt ${visibleStep} von ${this.totalSteps}`;

      if (pctEl) pctEl.textContent = `${Math.round((visibleStep / this.totalSteps) * 100)}%`;

      // Title
      const titleEl = header.querySelector('.st-header__title');
      const info = this.stepInfo[this.currentStep];
      if (titleEl && info) titleEl.textContent = info.title;
    });

    this._renderBundleCaption(overlay);

    // Mascot bubble on the active screen
    const activeScreen = overlay.querySelector('.st-screen--active');
    if (activeScreen) {
      const bubbleText = /** @type {HTMLElement | null} */ (activeScreen.querySelector('.st-mascot__text'));
      const info = this.stepInfo[this.currentStep];
      let bubble = this.currentStep === STEP_OUTFIT ? this._outfitBubble() : (info && info.bubble);
      if (this.currentStep === 1) {
        const pos = this._bundlePos();
        if (pos && pos.index === 1) bubble = 'Hey! 👋 Wir starten mit Buch 1. Erzähl uns kurz von deinem Kind - Vorname, Geschlecht und Augenfarbe.';
        if (pos && pos.index > 1) bubble = `Weiter geht's mit Buch ${pos.index}! 🎁 Für wen ist dieses Buch? Vorname, Geschlecht und Augenfarbe.`;
      }
      if (bubbleText && bubble) this._typewriter(bubbleText, bubble);

      // Populate images/data relevant to each step
      if (this.currentStep === 2) {
        this._renderUploadPhoto(activeScreen);
      }

      if (this.currentStep === STEP_OUTFIT) {
        this._renderOutfit(overlay);
      }

      if (this.currentStep === 3) {
        const img = /** @type {HTMLImageElement | null} */ (activeScreen.querySelector('.st-preview-card__img'));
        if (img) {
          img.src = this._previewSrc() || this.uploadedImageUrl || '';
        }
        // Decide starting character state
        if (this.previewImageUrl) {
          this._setCharacterState(activeScreen, 'done');
        } else if (this._previewGen.promise && !this._previewGen.done) {
          this._setCharacterState(activeScreen, 'loading');
          this._connectPreviewGeneration(overlay);
        } else if (this.uploadedImageBlob && this.eyeColor) {
          // Auto-start character generation in parallel with name/gender entry
          this._setCharacterState(activeScreen, 'loading');
          this._connectPreviewGeneration(overlay);
        } else {
          this._setCharacterState(activeScreen, 'idle');
        }
      }

      if (this.currentStep === 4) {
        const img = /** @type {HTMLImageElement | null} */ (activeScreen.querySelector('.st-preview-card__img'));
        if (img) {
          img.src = this.bookPreviewImageUrl || this._previewSrc() || '';
        }
        if (this.bookPreviewImageUrl) {
          this._setBookPreviewState(activeScreen, 'done');
        } else if (this._bookPreviewGen.promise && !this._bookPreviewGen.done) {
          this._setBookPreviewState(activeScreen, 'loading');
          this._connectBookPreviewGeneration(overlay);
        } else if (this.previewUniqid) {
          this._setBookPreviewState(activeScreen, 'loading');
          this._connectBookPreviewGeneration(overlay);
        } else {
          this._setBookPreviewState(activeScreen, 'idle');
        }
      }

      if (this.currentStep === 6) {
        this._updateDedicationField(overlay);
      }

      if (this.currentStep === 7) {
        this._updateSummary(overlay);
      }
    }

    this._validateStep(overlay);
    this._persistFlow();
  }

  /**
   * Show the uploaded photo on the upload screen (step 2). Without a photo the
   * example card explains the transformation; once one is picked it is replaced
   * by the actual photo and the CTA turns into "anderes Foto wählen".
   * @param {Element} screen
   */
  _renderUploadPhoto(screen) {
    const hasPhoto = !!this.uploadedImageUrl;
    screen.setAttribute('data-has-photo', hasPhoto ? 'true' : 'false');
    const img = /** @type {HTMLImageElement | null} */ (screen.querySelector('.st-photo-frame__img'));
    if (img && this.uploadedImageUrl) img.src = this.uploadedImageUrl;
    const title = screen.querySelector('.st-upload-cta__title');
    if (title) title.textContent = hasPhoto ? 'Anderes Foto wählen' : 'Foto hochladen';
  }

  /** Mascot line on the outfit step, with the name from step 1. */
  _outfitBubble() {
    const name = this.childName || 'dein Kind';
    return `Was soll ${name} im Buch tragen? 👕 Wähle das Outfit aus der Geschichte oder die Kleidung von deinem Foto.`;
  }

  /**
   * Fills the outfit step: the customer's own photo goes into the second card
   * (it is exactly the outfit they would get) and the saved choice is checked.
   * @param {Element} overlay
   */
  _renderOutfit(overlay) {
    const screen = overlay.querySelector('.st-screen--outfit');
    if (!screen) return;
    const photo = /** @type {HTMLImageElement | null} */ (screen.querySelector('.st-outfit__img--photo'));
    if (photo && this.uploadedImageUrl && photo.getAttribute('src') !== this.uploadedImageUrl) {
      photo.src = this.uploadedImageUrl;
    }
    const name = this.childName;
    const desc = screen.querySelector('[data-outfit="photo"] .st-outfit__desc');
    if (desc) desc.textContent = name ? `Die Kleidung, die ${name} auf deinem Foto trägt` : 'Die Kleidung von deinem Foto';
    this._setOutfit(overlay, this.outfitChoice);
  }

  /**
   * Single source of truth for the outfit choice: radios, card state, the tip
   * and the review summary all follow from here.
   * @param {Element} overlay
   * @param {'book' | 'photo'} choice
   */
  _setOutfit(overlay, choice) {
    this.outfitChoice = choice === 'photo' ? 'photo' : 'book';
    const screen = overlay.querySelector('.st-screen--outfit');
    if (screen) {
      screen.setAttribute('data-outfit', this.outfitChoice);
      screen.querySelectorAll('.st-outfit__option').forEach(/** @param {Element} opt */ (opt) => {
        const selected = opt.getAttribute('data-outfit') === this.outfitChoice;
        opt.setAttribute('data-selected', selected ? 'true' : 'false');
        const input = /** @type {HTMLInputElement | null} */ (opt.querySelector('.st-outfit__input'));
        if (input) input.checked = selected;
      });
      const tip = /** @type {HTMLElement | null} */ (screen.querySelector('.st-outfit-tip'));
      if (tip) tip.hidden = this.outfitChoice !== 'photo';
    }
    this._updateSummary(overlay);
    this._validateStep(overlay);
  }

  /**
   * "Weiter" ohne Häkchen: zur Checkbox scrollen und sie kurz rot markieren -
   * ohne das würde der Klick wirkungslos wirken.
   * @param {Element} overlay
   */
  _flagConsentMissing(overlay) {
    const consent = /** @type {HTMLElement | null} */ (overlay.querySelector('.st-screen--upload .st-consent'));
    if (!consent) return;
    consent.setAttribute('data-error', 'true');
    // Jeder weitere Tipp auf "Weiter" schuettelt erneut: Klasse ab, Reflow, wieder dran.
    consent.classList.remove('st-consent--shake');
    void consent.offsetWidth;
    consent.classList.add('st-consent--shake');
    const hint = /** @type {HTMLElement | null} */ (overlay.querySelector('.st-screen--upload .st-consent-hint'));
    if (hint) hint.hidden = false;
    const input = consent.querySelector('.st-consent__input');
    if (input) {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', 'st-consent-hint');
    }
    // Hinweis bleibt stehen, bis der Haken gesetzt ist - vorher verblasste er
    // nach 3s und war oft schon weg, bevor der Kunde hingesehen hatte.
    this._scrollInOverlay(hint || consent, 'center');
  }

  /** @param {Element} overlay */
  _clearConsentError(overlay) {
    const consent = overlay.querySelector('.st-screen--upload .st-consent');
    if (consent) {
      consent.setAttribute('data-error', 'false');
      consent.classList.remove('st-consent--shake');
      const input = consent.querySelector('.st-consent__input');
      if (input) input.removeAttribute('aria-invalid');
    }
    const hint = /** @type {HTMLElement | null} */ (overlay.querySelector('.st-screen--upload .st-consent-hint'));
    if (hint) hint.hidden = true;
  }

  /**
   * scrollIntoView() scrollt JEDEN scrollbaren Vorfahren mit - auch den
   * Dokument-Scroller, der trotz body{overflow:hidden} programmatisch noch
   * scrollbar bleibt. Auf iOS verschiebt Safari das position:fixed-Overlay dann
   * mit dem Dokument nach oben: die Footer-Leiste ("Zurueck"/"Weiter") wandert
   * scheinbar in die Bildschirmmitte und darunter blitzt die Produktseite
   * durch. Deshalb bewegen wir ausschliesslich den Scroll-Container INNERHALB
   * des Overlays und fassen den Dokument-Scroller nie an.
   * @param {Element | null} target
   * @param {'start' | 'center' | 'nearest'} [block]
   */
  _scrollInOverlay(target, block = 'start') {
    if (!target) return;
    const scroller = this._overlayScroller(target);
    if (!scroller) return;

    const targetRect = target.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const viewport = scroller.clientHeight;
    const current = scroller.scrollTop;
    // Position der Zielbox im Scroll-Inhalt (nicht im Viewport).
    const top = targetRect.top - scrollerRect.top + current;
    const max = Math.max(0, scroller.scrollHeight - viewport);

    let next;
    if (block === 'nearest') {
      const bottom = top + targetRect.height;
      if (top >= current && bottom <= current + viewport) return;
      next = top < current ? top - ST_SCROLL_GAP : bottom - viewport + ST_SCROLL_GAP;
    } else if (block === 'center') {
      next = top - (viewport - targetRect.height) / 2;
    } else {
      next = top - ST_SCROLL_GAP;
    }

    next = Math.max(0, Math.min(next, max));
    if (Math.abs(next - current) < 1) return;
    scroller.scrollTo({ top: next, behavior: 'smooth' });
  }

  /**
   * Naechster tatsaechlich scrollender Vorfahre innerhalb des Overlays. Auf dem
   * Review-Screen scrollt die Section selbst, auf allen anderen die .st-body -
   * deshalb wird gemessen statt nach Klassen gesucht.
   * @param {Element} target
   * @returns {HTMLElement | null}
   */
  _overlayScroller(target) {
    let node = /** @type {HTMLElement | null} */ (target.parentElement);
    while (node && !node.classList.contains('custom-overlay')) {
      const overflowY = getComputedStyle(node).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  /**
   * Nach dem Upload zur Foto-Vorschau scrollen: darunter liegen Upload-CTA und
   * Consent-Checkbox, der nächste nötige Schritt ist damit im Blick.
   * @param {Element} overlay
   */
  _scrollToPhotoPreview(overlay) {
    const target = overlay.querySelector('.st-screen--upload .st-photo-frame');
    if (!target) return;
    // Erst nach dem Layout-Update von data-has-photo scrollen.
    requestAnimationFrame(() => {
      this._scrollInOverlay(target, 'start');
    });
  }

  /** Alle Pflichtangaben aus Schritt 1 vorhanden und gueltig? */
  _detailsComplete() {
    const nameRegex = /^[\p{L}\s\-']+$/u;
    const nameValid = this.childName.length >= 1 && this.childName.length <= 20 && nameRegex.test(this.childName);
    return !!(nameValid && this.gender && this.eyeColor && !this._genderMismatch());
  }

  /**
   * Geschlecht, fuer das dieses Buch geschrieben ist (Produkt-Metafeld
   * custom.zielgeschlecht), auf die Werte der Buttons in Schritt 1 normiert.
   * Leer = fuer alle Kinder.
   * @returns {'' | 'Mädchen' | 'Junge'}
   */
  _targetGender() {
    const raw = (this.getAttribute('data-target-gender') || '').trim().toLowerCase();
    if (['mädchen', 'maedchen', 'female', 'girl'].includes(raw)) return 'Mädchen';
    if (['junge', 'male', 'boy'].includes(raw)) return 'Junge';
    return '';
  }

  /**
   * Passt das Geschlecht nicht zu diesem Buch? Harte Sperre: "Weiter" bleibt
   * zu, bis das andere Geschlecht oder das Alternativbuch gewaehlt ist.
   * @param {string} [gender]
   */
  _genderMismatch(gender = this.gender) {
    const target = this._targetGender();
    return !!(target && gender && gender !== target);
  }

  /**
   * Hinweis unter den Geschlechter-Buttons, nur fuer Buecher mit Zielgeschlecht.
   * Sichtbar per data-mismatch am Feld (_syncGenderHint).
   * @returns {string}
   */
  _genderHintHtml() {
    const target = this._targetGender();
    if (!target) return '';
    const girl = target === 'Mädchen';
    const title = _stEscape(this._getCurrentProductName());
    const altUrl = (this.getAttribute('data-alt-book-url') || '').trim();
    const altTitle = _stEscape((this.getAttribute('data-alt-book-title') || '').trim());
    const altColor = _stEscape((this.getAttribute('data-alt-book-color') || '').trim());
    const altImage = _stEscape((this.getAttribute('data-alt-book-image') || '').trim());
    const hero = girl
      ? 'Die Heldin der Geschichte ist ein Mädchen, deshalb können wir dieses Buch nicht für einen Jungen gestalten.'
      : 'Der Held der Geschichte ist ein Junge, deshalb können wir dieses Buch nicht für ein Mädchen gestalten.';
    // Das Alternativbuch erscheint in seiner eigenen Buchfarbe (wie auf seiner
    // Produktseite); ohne Farbe bleibt es bei der dieses Buchs.
    const alt = altUrl && altTitle
      ? `
        <div class="st-gender-hint__alt"${altColor ? ` style="--st-gender-alt: ${altColor};"` : ''}>
          <div class="st-gender-hint__book">
            ${altImage ? `<img class="st-gender-hint__cover" src="${altImage}" alt="" width="64" height="64" loading="lazy" decoding="async" />` : ''}
            <div class="st-gender-hint__book-body">
              <p class="st-gender-hint__eyebrow">Für ${girl ? 'Jungen' : 'Mädchen'} empfohlen</p>
              <p class="st-gender-hint__book-title">${altTitle}</p>
              <p class="st-gender-hint__book-sub">Gleicher Ablauf, kostenlose Vorschau</p>
            </div>
          </div>
          <a class="st-btn st-gender-hint__switch" href="${_stEscape(altUrl)}">Mit diesem Buch weitermachen ${ICON_ARROW_RIGHT}</a>
        </div>`
      : '';
    return `
      <div class="st-gender-hint" role="alert">
        <p class="st-gender-hint__title">„${title}“ ist für ${girl ? 'Mädchen' : 'Jungen'} geschrieben.</p>
        <p class="st-gender-hint__text">${hero}${alt ? '' : ' Schau dir gern unsere anderen Bücher an.'}</p>
        ${alt}
      </div>`;
  }

  /** @param {Element} overlay */
  _syncGenderHint(overlay) {
    const field = overlay.querySelector('.st-screen--details .st-field--gender');
    if (field) field.setAttribute('data-mismatch', this._genderMismatch() ? 'true' : 'false');
  }

  /**
   * Rabatt aufs zweite Buch in Prozent (Theme-Editor, siehe snippets/button.liquid).
   * 0 = Angebot aus.
   * @returns {number}
   */
  _nextBookPct() {
    const pct = parseInt(this.getAttribute('data-next-book-discount') || '0', 10);
    return pct > 0 && pct < 100 ? pct : 0;
  }

  /**
   * Rabatt aufs dritte Buch in Prozent. Nur zusammen mit dem Zweitbuch-Rabatt,
   * 0 = kein drittes Buch im Bundle.
   * @returns {number}
   */
  _thirdBookPct() {
    if (!this._nextBookPct()) return 0;
    const pct = parseInt(this.getAttribute('data-third-book-discount') || '0', 10);
    return pct > 0 && pct < 100 ? pct : 0;
  }

  /**
   * Rabatt fuer das Buch an Position `index` einer Bestellung (1-basiert).
   * @param {number} index
   * @returns {number}
   */
  _bookPctAt(index) {
    if (index === 2) return this._nextBookPct();
    if (index === 3) return this._thirdBookPct();
    return 0;
  }

  /**
   * Buecher fuer das Zweitbuch-Angebot, dieses zuerst (von Liquid als JSON
   * gerendert, snippets/next-book-entry.liquid).
   * @returns {Array<{ title: string, url: string, image: string, color: string, current: boolean }>}
   */
  _nextBooks() {
    if (this._nextBooksCache) return this._nextBooksCache;
    /** @type {Array<any>} */
    let list = [];
    try {
      const el = document.querySelector('script[data-st-next-books]');
      const parsed = el ? JSON.parse(el.textContent || '[]') : [];
      if (Array.isArray(parsed)) list = parsed;
    } catch {}
    this._nextBooksCache = list
      .filter((b) => b && typeof b.title === 'string' && typeof b.url === 'string')
      .map((b) => ({
        title: b.title,
        url: b.url,
        image: typeof b.image === 'string' ? b.image : '',
        color: typeof b.color === 'string' ? b.color : '',
        current: b.current === true,
      }));
    return this._nextBooksCache;
  }

  /**
   * Karte in der Bestelluebersicht: noch ein Buch, mit Rabatt. Sichtbar erst,
   * wenn feststeht, dass dies das erste Buch im Warenkorb ist (_renderNextBookState).
   * @returns {string}
   */
  _nextBookUpsellHtml() {
    const pct = this._nextBookPct();
    const books = this._nextBooks();
    if (!pct || !books.length) return '';
    const items = books.map((b, i) => `
      <li>
        <button type="button" class="st-nextbook__book" data-index="${i}"${b.color ? ` style="--st-nextbook-color: ${_stEscape(b.color)};"` : ''}>
          ${b.image ? `<img class="st-nextbook__cover" src="${_stEscape(b.image)}" alt="" width="56" height="56" loading="lazy" decoding="async" />` : ''}
          <span class="st-nextbook__body">
            <span class="st-nextbook__name">${_stEscape(b.title)}</span>
            <span class="st-nextbook__sub">${b.current ? 'Noch einmal, für ein weiteres Kind' : 'Ein neues Abenteuer'}</span>
          </span>
          <span class="st-nextbook__pill">-${pct} %</span>
        </button>
      </li>`).join('');
    return `
      <div class="st-nextbook" hidden>
        <p class="st-nextbook__title">🎁 Noch ein Buch für Geschwister oder Freunde?</p>
        <p class="st-nextbook__text">Dein zweites Buch ist <strong>${pct} % günstiger</strong>. Such dir einfach eins aus, dieses Buch legen wir dafür schon in den Warenkorb.</p>
        <ul class="st-nextbook__list" role="list">${items}</ul>
      </div>`;
  }

  /**
   * Liest die personalisierten Buecher im Warenkorb. Die Buch-Zeile des
   * laufenden Durchlaufs zaehlt nicht mit: sie liegt schon drin, wenn nur die
   * Extras gescheitert sind (_bookLineAdded), und traegt dann dessen _BuchID.
   * @param {Element} overlay
   */
  async _refreshCartBooks(overlay) {
    if (!this._nextBookPct()) return;
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      this._cartBooks = (cart.items || [])
        .filter((/** @type {any} */ item) => {
          const props = item.properties || {};
          if (!props.Name && !props._book_slug) return false;
          return !(this._bookLineAdded && this._pairId && props._BuchID === this._pairId);
        })
        .map((/** @type {any} */ item) => ({
          name: String((item.properties || {}).Name || ''),
          price: (Number(item.original_price) || 0) / 100,
        }));
    } catch {
      return;
    }
    if (overlay.isConnected) this._renderNextBookState(overlay);
  }

  /**
   * Bundle-Wahl der Produktseite (snippets/button.liquid: 1, 2 oder 3 Buecher).
   * Gelesen wird beim Rendern, damit Produktseiten- und Sticky-Button dieselbe
   * Auswahl sehen.
   * @returns {number}
   */
  _bundleSize() {
    const input = /** @type {HTMLInputElement | null} */ (
      document.querySelector('[data-st-bundle-pick] input:checked')
    );
    const size = input ? parseInt(input.value, 10) : 1;
    if (size === 3 && this._thirdBookPct()) return 3;
    return size === 2 || size === 3 ? 2 : 1;
  }

  /**
   * Welches Buch des Bundles gerade entsteht: { index, total }, z. B. 2 von 3.
   * Massgeblich sind die Buecher im Warenkorb - ein zweites Buch bekommt den
   * Rabatt auch ohne vorher gewaehltes Bundle (Angebot am Ende), ein drittes,
   * sobald es einen Drittbuch-Rabatt gibt. Solange der Warenkorb noch laedt,
   * zaehlt die Auswahl, damit "Buch 1/2" nicht erst spaeter erscheint.
   * null = normaler Ablauf.
   * @returns {{ index: number, total: number } | null}
   */
  _bundlePos() {
    if (!this._nextBookPct()) return null;
    const size = this._bundleSize();
    const inCart = Array.isArray(this._cartBooks) ? this._cartBooks.length : 0;
    if (inCart === 0) return size > 1 ? { index: 1, total: size } : null;
    if (inCart === 1) return { index: 2, total: Math.max(size, 2) };
    if (inCart === 2 && this._thirdBookPct()) return { index: 3, total: 3 };
    return null;
  }

  /**
   * Im Bundle steht nach diesem Buch noch eins an: die Uebersicht waehlt dann
   * das naechste Buch, statt zur Kasse zu fuehren.
   * @returns {boolean}
   */
  _bundleHasNext() {
    const pos = this._bundlePos();
    return !!pos && pos.index < pos.total && this._nextBooks().length > 0;
  }

  /** Setzt die Bundle-Auswahl der Produktseite, z. B. nach einem Buchwechsel. */
  _setBundleSize(/** @type {number} */ size) {
    const input = /** @type {HTMLInputElement | null} */ (
      document.querySelector(`[data-st-bundle-pick] input[value="${size}"]`)
    );
    if (input) input.checked = true;
  }

  /** Meldet die Bundle-Wahl auf der Produktseite an die Funnel-Auswertung. */
  _wireBundlePick() {
    if (_stBundlePickWired) return;
    const pick = document.querySelector('[data-st-bundle-pick]');
    if (!pick) return;
    _stBundlePickWired = true;
    pick.addEventListener('change', (event) => {
      const input = /** @type {HTMLInputElement} */ (event.target);
      if (!(input instanceof HTMLInputElement)) return;
      _stTrack('bundle_selected', {
        product_id: this.getAttribute('data-product-id') || undefined,
        meta: { enabled: input.value !== '1', size: input.value },
      });
    });
  }

  /**
   * "Buch 1/2" vor "Schritt x von y" in jedem Header.
   * @param {Element} overlay
   */
  _renderBundleCaption(overlay) {
    const pos = this._bundlePos();
    overlay.querySelectorAll('.st-stepper-caption__book').forEach((el) => {
      const pill = /** @type {HTMLElement} */ (el);
      pill.hidden = !pos;
      pill.textContent = pos ? `Buch ${pos.index}/${pos.total}` : '';
    });
  }

  /**
   * Kleiner Ueberblick in Schritt 1: was schon im Warenkorb liegt, welches
   * Buch gerade dran ist und was noch kommt. Bei drei Plaetzen bekommt der
   * aktive mehr Breite, die anderen zeigen nur das Noetigste.
   * @param {{ index: number, total: number }} pos
   * @returns {string}
   */
  _bundleStripHtml(pos) {
    const title = _stEscape(this.getAttribute('data-product-title') || this._getCurrentProductName() || 'Dieses Buch');
    const books = this._cartBooks || [];
    const compact = pos.total > 2;
    let html = '';
    for (let i = 1; i <= pos.total; i += 1) {
      const pct = this._bookPctAt(i);
      const badge = pct ? `<span class="st-bundle__badge">-${pct} %</span>` : '';
      let state = 'next';
      let mark = String(i);
      let label = `Buch ${i}`;
      let name = i === 2 && pos.index === 1 && !compact ? 'Wählst du am Ende' : '';
      if (i < pos.index) {
        const child = books[i - 1] && books[i - 1].name ? _stEscape(books[i - 1].name) : '';
        state = 'done';
        mark = ICON_CHECK_SM;
        label = compact ? (child || `Buch ${i}`) : 'Im Warenkorb';
        name = compact ? '' : (child ? `Für ${child}` : `Buch ${i}`);
      } else if (i === pos.index) {
        state = 'active';
        label = 'Jetzt dran';
        name = title;
      }
      html += `
        <div class="st-bundle__slot" data-state="${state}">
          <span class="st-bundle__mark" aria-hidden="true">${mark}</span>
          <span class="st-bundle__body">
            <span class="st-bundle__label">${label}</span>
            ${name ? `<span class="st-bundle__name">${name}</span>` : ''}
          </span>
          ${i >= pos.index ? badge : ''}
        </div>`;
    }
    return html;
  }

  /**
   * Erstes Buch ohne Bundle: Angebot in der Uebersicht. Im Bundle: Auswahl des
   * naechsten Buchs samt "Weiter zu Buch x", Ueberblick in Schritt 1 und
   * Rabattzeile in der Uebersicht. Mehr Buecher als Rabattstufen bekommen
   * nichts davon - jeder Admin-Rabatt gilt einmal pro Bestellung.
   * @param {Element} overlay
   */
  _renderNextBookState(overlay) {
    const pct = this._nextBookPct();
    const books = this._cartBooks;
    const pos = this._bundlePos();
    const isFirst = !!pct && Array.isArray(books) && books.length === 0;
    const pick = this._bundleHasNext();
    const nextIndex = pos ? pos.index + 1 : 2;
    const nextPct = this._bookPctAt(nextIndex) || pct;
    const ordinal = nextIndex === 3 ? 'drittes' : 'zweites';

    const upsell = /** @type {HTMLElement | null} */ (overlay.querySelector('.st-nextbook'));
    if (upsell) {
      // Im Bundle steht die Auswahl schon, bevor der Warenkorb gelesen ist.
      upsell.hidden = !((isFirst && !pos) || pick);
      upsell.toggleAttribute('data-pick', pick);
      const titleEl = upsell.querySelector('.st-nextbook__title');
      const textEl = upsell.querySelector('.st-nextbook__text');
      if (titleEl) {
        titleEl.textContent = pick ? `🎁 Welches Buch wird dein ${ordinal}?` : '🎁 Noch ein Buch für Geschwister oder Freunde?';
      }
      if (textEl) {
        textEl.innerHTML = pick
          ? `Buch ${nextIndex - 1} ist fertig. Das ${ordinal.slice(0, -1)} ist <strong>${nextPct} % günstiger</strong> - wähle es aus, dann geht's direkt weiter.`
          : `Dein zweites Buch ist <strong>${pct} % günstiger</strong>. Such dir einfach eins aus, dieses Buch legen wir dafür schon in den Warenkorb.`;
      }
      upsell.querySelectorAll('.st-nextbook__pill').forEach((el) => {
        el.textContent = `-${nextPct} %`;
      });
      upsell.querySelectorAll('.st-nextbook__book').forEach((el) => {
        const btn = /** @type {HTMLElement} */ (el);
        if (pick) btn.setAttribute('aria-pressed', String(Number(btn.dataset.index) === this._bundlePick));
        else btn.removeAttribute('aria-pressed');
      });
    }

    // Kauf-Button: im Bundle "Weiter zu Buch x", darunter der Ausweg.
    const buyBtn = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('.st-screen--review .st-btn--buy'));
    if (buyBtn && !this._cartSubmitted && !buyBtn.hasAttribute('aria-busy')) {
      buyBtn.innerHTML = pick ? `Weiter zu Buch ${nextIndex} ${ICON_ARROW_RIGHT}` : `${ICON_CART} Sicher zur Kasse`;
    }
    const solo = /** @type {HTMLElement | null} */ (overlay.querySelector('.st-screen--review .st-footer__solo'));
    if (solo) {
      solo.hidden = !pick;
      solo.textContent = nextIndex === 3 ? 'Nur 2 Bücher kaufen' : 'Nur dieses Buch kaufen';
    }

    const strip = /** @type {HTMLElement | null} */ (overlay.querySelector('.st-bundle'));
    if (strip) {
      strip.hidden = !pos;
      strip.dataset.count = pos ? String(pos.total) : '';
      strip.innerHTML = pos ? this._bundleStripHtml(pos) : '';
    }

    this._renderBundleCaption(overlay);
    this._renderBindingPrices(overlay);
    if (this.currentStep === 7) this._updateSummary(overlay);
  }

  /**
   * Preis einer Einband-Karte (Schritt 5). Beim zweiten Buch steht schon hier
   * der Preis nach Zweitbuch-Rabatt, gerechnet wie in der Uebersicht
   * (_nextBookDiscount), damit beide Schritte denselben Betrag nennen.
   * @param {number} price
   * @param {number} comparePrice
   * @returns {string}
   */
  _bindingPriceHtml(price, comparePrice) {
    const discount = this._nextBookDiscount(price);
    const now = Math.round((price - discount) * 100) / 100;
    const was = comparePrice > price ? comparePrice : price;
    let html = `<span class="st-binding__price-now">${this._fmt(now)}</span>`;
    if (was > now) {
      html += `<span class="st-binding__price-was">${this._fmt(was)}</span>`;
      // Same "Du sparst" pill as on the product page (snippets/price.liquid).
      const savings = Math.round((was - now) * 100) / 100;
      html += `<span class="st-binding__savings">Du sparst ${this._fmt(savings)}</span>`;
    }
    if (discount > 0) {
      const index = (this._cartBooks || []).length + 1;
      // Beim dritten Buch haengt der Betrag am guenstigsten Buch der ganzen
      // Bestellung, die Prozentzahl stimmt nur bei gleichen Preisen.
      html += index === 3
        ? '<span class="st-binding__next">inkl. Rabatt aufs 3. Buch</span>'
        : `<span class="st-binding__next">inkl. ${this._bookPctAt(index)} % Rabatt aufs ${index}. Buch</span>`;
    }
    return html;
  }

  /**
   * Schreibt die Preise der Einband-Karten neu, sobald der Warenkorb gelesen
   * ist (_refreshCartBooks laeuft asynchron nach dem Aufbau des Overlays).
   * @param {Element} overlay
   */
  _renderBindingPrices(overlay) {
    overlay.querySelectorAll('.st-screen--binding .st-binding').forEach((card) => {
      const priceEl = card.querySelector('.st-binding__price');
      if (!priceEl) return;
      const hardcover = card.getAttribute('data-variant') === 'hardcover';
      priceEl.innerHTML = hardcover
        ? this._bindingUpgradeHtml()
        : this._bindingPriceHtml(this.softcoverPrice || 29.99, this.softcoverComparePrice || 0);
    });
  }

  /**
   * Hardcover-Karte: nur der Aufpreis zum vorausgewaehlten Softcover, darunter
   * der Gesamtpreis. Beide Betraege nach Zweitbuch-Rabatt, wie _bindingPriceHtml,
   * damit Aufpreis + Softcover-Karte genau den Hardcover-Preis der Uebersicht ergeben.
   * @returns {string}
   */
  _bindingUpgradeHtml() {
    const hardcover = this.hardcoverPrice || 39.99;
    const softcover = this.softcoverPrice || 29.99;
    const hardNow = Math.round((hardcover - this._nextBookDiscount(hardcover)) * 100) / 100;
    const softNow = Math.round((softcover - this._nextBookDiscount(softcover)) * 100) / 100;
    const upgrade = Math.round((hardNow - softNow) * 100) / 100;
    if (upgrade <= 0) return this._bindingPriceHtml(hardcover, this.hardcoverComparePrice || 0);
    return `<span class="st-binding__price-now">+${this._fmt(upgrade)}</span>`
      + `<span class="st-binding__price-total">Gesamt ${this._fmt(hardNow)}</span>`;
  }

  /**
   * Rabatt fuer dieses Buch in Euro, so wie ihn die Kasse am Ende abzieht.
   *
   * Im Live-Shop nachgeprueft (ohne Shopify Plus): von den zwei "Kaufe X,
   * erhalte Y"-Rabatten greift immer nur der bessere, beide rabattieren das
   * guenstigste Buch und runden auf den Cent ab. Der 3er-Rabatt im Admin
   * traegt deshalb beide Stufen (20 + 30 = 50 % auf ein Buch). Fuers dritte
   * Buch steht hier, was zum schon gezeigten Rabatt des zweiten noch dazukommt.
   * @param {number} bookPrice
   * @returns {number}
   */
  _nextBookDiscount(bookPrice) {
    const books = this._cartBooks;
    if (!Array.isArray(books) || !books.length) return 0;
    const prices = books.map((b) => b.price || bookPrice);
    const off = (/** @type {number} */ base, /** @type {number} */ pct) => Math.floor(Math.round(base * 100) * pct / 100);
    const p2 = this._nextBookPct();
    if (books.length === 1) return off(Math.min(bookPrice, prices[0]), p2) / 100;
    const p3 = this._thirdBookPct();
    if (books.length !== 2 || !p3) return 0;
    const trio = off(Math.min(bookPrice, ...prices), p2 + p3);
    const pair = off(Math.min(prices[0], prices[1]), p2);
    return Math.max(0, trio - pair) / 100;
  }

  /**
   * Zweites Buch starten: dieses Buch (samt Extras) in den Warenkorb, dann
   * ohne Umweg zum naechsten. Dasselbe Buch startet hier frisch, ein anderes
   * oeffnet seine Seite mit offenem Overlay (_saveHandoff).
   * @param {number} index - Buch in _nextBooks()
   * @param {HTMLButtonElement} triggerBtn - zeigt den Spinner
   */
  async _startNextBook(index, triggerBtn) {
    const book = this._nextBooks()[index];
    if (!book) return;
    const bundle = this._bundleHasNext();
    const bundleSize = this._bundleSize();
    const overlay = triggerBtn.closest('.custom-overlay');
    // addToCart leert den State - Name und Preis dieses Buchs vorher sichern.
    const doneBook = {
      name: this.childName,
      price: this.bookCoverType === 'hardcover' ? (this.hardcoverPrice || 39.99) : (this.softcoverPrice || 29.99),
    };
    const added = await this.addToCart({
      redirect: false,
      openDrawer: false,
      container: /** @type {HTMLElement | null} */ (overlay),
      triggerBtn,
    });
    if (!added) return;

    _stTrack('next_book_started', {
      step: '7',
      product_id: this.getAttribute('data-product-id') || undefined,
      meta: { same_book: book.current, bundle },
    });

    const cartBooks = [...(this._cartBooks || []), doneBook];
    if (!book.current) {
      // addToCart hat den State schon geleert - uebergeben werden "Overlay
      // oeffnen", die Bundle-Groesse und die Buecher im Warenkorb, damit dort
      // sofort "Buch 2/3" steht.
      this._saveHandoff({ bundleSize, cartBooks });
      window.location.href = book.url;
      return;
    }
    // Sofort als naechstes Buch rendern; _refreshCartBooks bestaetigt das gleich.
    this._cartBooks = cartBooks;
    this._bundlePick = 0;
    this.showOverlay();
  }

  /**
   * Merkt sich die Angaben aus Schritt 1 fuer das Buch, zu dem der Kunde wechselt.
   * @param {{ bundleSize?: number, cartBooks?: Array<{ name: string, price: number }> }} [bundle]
   */
  _saveHandoff({ bundleSize = this._bundleSize(), cartBooks = undefined } = {}) {
    try {
      sessionStorage.setItem(STARTALES_HANDOFF_KEY, JSON.stringify({
        name: this.childName,
        gender: this.gender,
        eyeColor: this.eyeColor,
        bundleSize,
        cartBooks,
        ts: Date.now(),
      }));
    } catch {}
  }

  /**
   * Gegenstueck zu _saveHandoff: kommt der Kunde vom Wechsel-Button eines
   * anderen Buchs, oeffnet sich das Overlay hier sofort in Schritt 1, mit den
   * Angaben von dort. Gelesen wird einmal pro Seite, verbraucht sofort.
   */
  _consumeHandoff() {
    if (_stHandoffChecked) return;
    _stHandoffChecked = true;
    /** @type {any} */
    let data = null;
    try {
      const raw = sessionStorage.getItem(STARTALES_HANDOFF_KEY);
      if (!raw) return;
      sessionStorage.removeItem(STARTALES_HANDOFF_KEY);
      data = JSON.parse(raw);
    } catch {
      return;
    }
    if (!data || !(Date.now() - Number(data.ts) < STARTALES_HANDOFF_MAX_AGE_MS)) return;

    this.resetState();
    this.childName = typeof data.name === 'string' ? data.name.trim().slice(0, 20) : '';
    this.gender = data.gender === 'Mädchen' || data.gender === 'Junge' ? data.gender : '';
    this.eyeColor = typeof data.eyeColor === 'string' ? data.eyeColor : '';
    // Bundle weiterfuehren: dieselbe Auswahl auf dieser Produktseite und die
    // Buecher im Warenkorb vorlaeufig, bis _refreshCartBooks sie bestaetigt.
    const size = Number(data.bundleSize);
    if (size === 2 || size === 3) this._setBundleSize(size);
    if (Array.isArray(data.cartBooks)) {
      this._cartBooks = data.cartBooks
        .filter((/** @type {any} */ b) => b && typeof b === 'object')
        .map((/** @type {any} */ b) => ({ name: String(b.name || ''), price: Number(b.price) || 0 }));
    }

    // Das Overlay-CSS laedt nicht-blockierend (snippets/stylesheets.liquid);
    // vor dem load-Event koennte es noch fehlen und das Overlay blitzt ungestylt auf.
    const open = () => this.showOverlay({ keepState: true });
    if (document.readyState === 'complete') open();
    else window.addEventListener('load', open, { once: true });
  }

  /**
   * Markiert die erste fehlende Angabe aus Schritt 1 und springt sie an. Das
   * Markup dafuer lag laengst bereit - ausgeloest hat es nur nie jemand, also
   * blieb ein Klick auf den grauen "Weiter"-Button ohne jede Erklaerung.
   * @param {Element} overlay
   */
  _flagMissingDetails(overlay) {
    const screen = overlay.querySelector('.st-screen--details');
    if (!screen) return;
    const nameRegex = /^[\p{L}\s\-']+$/u;
    const nameValid = this.childName.length >= 1 && this.childName.length <= 20 && nameRegex.test(this.childName);

    // blocked: Geschlecht gewaehlt, passt aber nicht zum Buch. Der Hinweis
    // steht dann schon da - kein "Bitte waehle..."-Fehler, nur hinspringen.
    const fields = [
      { sel: '.st-field--name', missing: !nameValid, blocked: false },
      { sel: '.st-field--gender', missing: !this.gender, blocked: this._genderMismatch() },
      { sel: '.st-field--eyes', missing: !this.eyeColor, blocked: false },
    ];
    let first = null;
    fields.forEach(({ sel, missing, blocked }) => {
      const field = screen.querySelector(sel);
      if (!field) return;
      field.setAttribute('data-error', missing ? 'true' : 'false');
      if ((missing || blocked) && !first) first = field;
    });
    if (!first) return;
    this._scrollInOverlay(first, 'center');
    if (first.classList.contains('st-field--name')) {
      const input = /** @type {HTMLInputElement | null} */ (first.querySelector('.st-name-input'));
      if (input) input.focus({ preventScroll: true });
    }
  }

  /** @param {Element} overlay */
  _clearDetailErrors(overlay) {
    overlay.querySelectorAll('.st-screen--details .st-field[data-error="true"]').forEach(f => {
      f.setAttribute('data-error', 'false');
    });
  }

  _validateStep(overlay) {
    // Laeuft nach jeder Eingabe - der einfachste Punkt, an dem jede Aenderung
    // auch gespeichert wird.
    this._persistFlow();
    const activeScreen = overlay.querySelector('.st-screen--active');
    if (!activeScreen) return;
    const btn = /** @type {HTMLButtonElement | null} */ (activeScreen.querySelector('.st-btn--continue'));

    if (this.currentStep === 1) {
      if (!btn) return;
      const ready = this._detailsComplete();
      btn.disabled = false;
      btn.setAttribute('data-soft-disabled', ready ? 'false' : 'true');
      btn.setAttribute('aria-disabled', ready ? 'false' : 'true');
      if (ready) this._clearDetailErrors(overlay);
    } else if (this.currentStep === 2) {
      if (!btn) return;
      // Schritt 2 sperrt den Button nie, auch nicht optisch: der Click-Handler
      // fuehrt zu Upload bzw. Checkbox. Der graue Look wurde als "geht nicht"
      // gelesen.
      const ready = !!(this.uploadedImageBlob && this.photoConfirmed);
      btn.disabled = false;
      if (ready) this._clearConsentError(overlay);
    } else if (this.currentStep === STEP_OUTFIT) {
      if (!btn) return;
      // One option is always selected (the free one by default).
      btn.disabled = !this.outfitChoice;
    } else if (this.currentStep === 3) {
      if (!btn) return;
      const state = activeScreen.getAttribute('data-state') || 'idle';
      if (state === 'idle' || state === 'error') {
        // Idle/error = retry state: allow retry if prerequisites still available
        btn.disabled = !(this.uploadedImageBlob && this.eyeColor);
      } else if (state === 'loading') {
        btn.disabled = true;
      } else if (state === 'done') {
        btn.disabled = false;
      }
    } else if (this.currentStep === 4) {
      if (!btn) return;
      const state = activeScreen.getAttribute('data-state') || 'idle';
      btn.disabled = state !== 'done';
    } else if (this.currentStep === 5) {
      if (!btn) return;
      // Softcover is preselected (resetState), so this only guards odd states.
      btn.disabled = !this.bookCoverType;
    } else if (this.currentStep === 6) {
      if (!btn) return;
      // Both extras are optional, so the button stays clickable. A selected but
      // empty dedication is caught on click instead (see the extras continue
      // handler) - that way the hint appears when the customer tries to move on,
      // not the second the card is selected.
      btn.disabled = false;
    }
  }

  /**
   * Single source of truth for an extra's selected state: card, toggle button,
   * label and the review summary all follow from here.
   * @param {Element} overlay
   * @param {string} key - 'dedication' | 'matt'
   * @param {boolean} enabled
   */
  _setExtraSelected(overlay, key, enabled) {
    if (key === 'dedication') {
      this.dedicationEnabled = enabled;
      // Selecting or deselecting the card starts the field off clean again.
      this._dedicationErrorShown = false;
    } else if (key === 'matt') {
      this.mattCoverEnabled = enabled;
    } else {
      return;
    }

    const card = overlay.querySelector(`.st-screen--extras .st-extra[data-extra="${key}"]`);
    if (card) {
      card.setAttribute('data-selected', enabled ? 'true' : 'false');
      const toggle = card.querySelector('.st-extra__toggle');
      if (toggle) toggle.setAttribute('aria-pressed', enabled ? 'true' : 'false');
      const label = card.querySelector('.st-extra__toggle-label');
      if (label) label.textContent = enabled ? 'Hinzugefügt' : 'Hinzufügen';
    }

    if (key === 'dedication') this._updateDedicationField(overlay);
    this._updateSummary(overlay);
    this._validateStep(overlay);
  }

  /**
   * Keeps the dedication textarea's counter and hint in sync and flags the
   * empty-but-selected case that blocks the step.
   * @param {Element} overlay
   */
  _updateDedicationField(overlay) {
    const wrap = overlay.querySelector('.st-dedication');
    if (!wrap) return;
    const input = /** @type {HTMLTextAreaElement | null} */ (wrap.querySelector('.st-dedication__input'));
    const count = wrap.querySelector('.st-dedication__count');
    if (count && input) count.textContent = `${input.value.length}/200`;

    const hint = wrap.querySelector('.st-dedication__hint');
    if (hint) {
      const missing = this._dedicationErrorShown && this.dedicationEnabled && !this.dedicationText;
      hint.setAttribute('data-error', missing ? 'true' : 'false');
      hint.textContent = missing
        ? 'Bitte gib deine Widmung ein, um fortzufahren.'
        : 'Erscheint auf der ersten Seite deines Buches.';
    }
  }

  /**
   * True while the dedication textarea holds focus behind an on-screen keyboard.
   * iOS shrinks the visual viewport (caught by the resize handler), Android
   * resizes the layout viewport instead and leaves that measurement at zero -
   * hence the touch-device fallback, where a focused textarea always means a
   * keyboard. On desktop both checks stay false, so a focused field never
   * swallows a click.
   * @param {Element} overlay
   */
  _dedicationKeyboardOpen(overlay) {
    const field = overlay.querySelector('.st-dedication__input');
    if (!field || document.activeElement !== field) return false;
    if (this._keyboardOpen) return true;
    return !!(window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches);
  }

  // ---------------------------------------------------------------------------
  // Waiting screen (step 3): rotating social proof while the character generates
  // ---------------------------------------------------------------------------

  /**
   * Reviews shown during generation. The merchant supplies them as one review
   * per line (`Name | Zitat`); anything unparsable falls back to WAIT_REVIEWS.
   * @returns {Array<{ name: string, text: string }>}
   */
  _waitReviews() {
    if (this._waitReviewCache) return this._waitReviewCache;
    const raw = (this.getAttribute('data-wait-reviews') || '').trim();
    /** @type {Array<{ name: string, text: string }>} */
    const parsed = [];
    raw.split('\n').forEach(line => {
      const i = line.indexOf('|');
      if (i === -1) return;
      const name = line.slice(0, i).trim();
      const text = line.slice(i + 1).trim();
      if (name && text) parsed.push({ name, text });
    });
    this._waitReviewCache = parsed.length ? parsed : WAIT_REVIEWS;
    return this._waitReviewCache;
  }

  /**
   * @param {Element} screen
   * @param {number} index
   */
  _renderWaitReview(screen, index) {
    const reviews = this._waitReviews();
    const review = reviews[index % reviews.length];
    if (!review) return;
    const avatar = screen.querySelector('.st-wait__avatar');
    const nameEl = screen.querySelector('.st-wait__name');
    const quoteEl = screen.querySelector('.st-wait__quote');
    if (avatar) avatar.textContent = review.name.charAt(0).toUpperCase();
    if (nameEl) nameEl.textContent = review.name;
    if (quoteEl) quoteEl.innerHTML = `&bdquo;${_stEmphasis(review.text)}&ldquo;`;
  }

  /**
   * Auto-advances the review card while generation runs. Idempotent: returning
   * to step 3 mid-generation restarts the rotation instead of stacking timers.
   * @param {Element} screen
   */
  _startWaitReviewRotation(screen) {
    this._stopWaitReviewRotation();
    const card = screen.querySelector('.st-wait__review');
    if (!card) return;

    let i = 0;
    this._renderWaitReview(screen, i);
    const reviews = this._waitReviews();

    // Buchseiten laufen im selben Takt mit, eine Seite pro Bewertung.
    const page = /** @type {HTMLElement | null} */ (screen.querySelector('.st-wait__page'));
    const pages = this._waitPageSources();
    if (page) {
      page.hidden = pages.length === 0;
      if (pages.length) this._renderWaitPage(screen, 0);
    }
    if (reviews.length < 2 && pages.length < 2) return;

    /** @type {{ interval: ReturnType<typeof setInterval> | null, timeout: ReturnType<typeof setTimeout> | null }} */
    const rotation = { interval: null, timeout: null };
    rotation.interval = setInterval(() => {
      i += 1;
      // Fade out, swap, fade back in - the card keeps its height (min-height),
      // so the layout below it never jumps.
      if (reviews.length > 1) card.setAttribute('data-fading', 'true');
      if (page && pages.length > 1) page.setAttribute('data-fading', 'true');
      rotation.timeout = setTimeout(() => {
        if (reviews.length > 1) {
          this._renderWaitReview(screen, i);
          card.setAttribute('data-fading', 'false');
        }
        if (page && pages.length > 1) {
          this._renderWaitPage(screen, i);
          page.setAttribute('data-fading', 'false');
        }
      }, 260);
    }, 4000);
    this._waitRotation = rotation;
  }

  /**
   * Bis zu drei Seiten aus der "Blick ins Buch"-Section der Produktseite, in
   * Wartebild-Groesse. Die Seiten bleiben so an einer Stelle gepflegt (Theme-
   * Editor bzw. Metafeld); ohne Section bleibt der Bereich leer.
   * @returns {Array<{ src: string, alt: string }>}
   */
  _waitPageSources() {
    if (this._waitPageCache) return this._waitPageCache;
    /** @type {Array<{ src: string, alt: string }>} */
    const all = [];
    const seen = new Set();
    // Nur die Section der Seite, nicht eine Kopie in der Leseprobe des Overlays.
    document.querySelectorAll('.stl-bib img.stl-bib__img').forEach((el) => {
      if (el.closest('.custom-overlay')) return;
      const raw = el.getAttribute('src');
      if (!raw) return;
      let src = raw;
      try {
        const url = new URL(raw, window.location.href);
        url.searchParams.set('width', '600');
        src = url.toString();
      } catch { /* relative oder ungewoehnliche URL: unveraendert nutzen */ }
      if (seen.has(src)) return;
      seen.add(src);
      all.push({ src, alt: /** @type {HTMLImageElement} */ (el).alt || '' });
    });
    // Seite 1 ist meist die Titelseite - ab Seite 2, gleichmaessig verteilt.
    const pool = all.length > 1 ? all.slice(1) : all;
    const count = Math.min(3, pool.length);
    /** @type {Array<{ src: string, alt: string }>} */
    const picked = [];
    for (let n = 0; n < count; n++) {
      const entry = pool[Math.floor((n * pool.length) / count)];
      if (entry) picked.push(entry);
    }
    // Vorladen, damit beim Wechsel kein leerer Rahmen aufblitzt.
    picked.forEach((p) => { const img = new Image(); img.decoding = 'async'; img.src = p.src; });
    this._waitPageCache = picked;
    return picked;
  }

  /**
   * @param {Element} screen
   * @param {number} index
   */
  _renderWaitPage(screen, index) {
    const pages = this._waitPageSources();
    const entry = pages[index % pages.length];
    const img = /** @type {HTMLImageElement | null} */ (screen.querySelector('.st-wait__page-img'));
    if (!entry || !img) return;
    img.src = entry.src;
    img.alt = entry.alt;
  }

  _stopWaitReviewRotation() {
    if (!this._waitRotation) return;
    if (this._waitRotation.interval) clearInterval(this._waitRotation.interval);
    if (this._waitRotation.timeout) clearTimeout(this._waitRotation.timeout);
    this._waitRotation = null;
  }

  /**
   * Mark the screen ready once the finished preview can actually paint, which is
   * what starts the reveal animation. On error we still release it - an empty
   * frame that never appears would be worse than a broken image.
   * @param {Element} screen
   * @param {HTMLImageElement} img
   */
  _revealWhenPainted(screen, img) {
    // Beim Zurueckblaettern auf Schritt 3 steht das Bild laengst - dann ist es
    // kein Reveal mehr und der Burst bleibt aus. Der Zauber liegt im Einmaligen.
    const isFirstReveal = screen.getAttribute('data-reveal') !== 'ready';
    const reveal = () => {
      img.removeEventListener('load', reveal);
      img.removeEventListener('error', reveal);
      screen.setAttribute('data-reveal', 'ready');
      if (isFirstReveal && img.naturalWidth > 0) this._celebrateReveal(screen);
    };
    if (img.complete) {
      reveal();
      return;
    }
    img.addEventListener('load', reveal);
    img.addEventListener('error', reveal);
  }

  /**
   * Der Burst kommt bewusst NACH dem Buch, nicht mit ihm: erst landet das Cover
   * mit Schwung (900ms Pop), im Ueberschwingen zuenden die Sterne als Bestaetigung.
   * Gleichzeitig waere es Deko - nacheinander fuehlt es sich an wie ausgepackt.
   * @param {Element} screen
   */
  _celebrateReveal(screen) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    clearTimeout(this._celebrateTimer);
    this._celebrateTimer = setTimeout(() => {
      const photo = screen.querySelector('.st-preview-card__photo');
      // Bis hierhin kann der Nutzer laengst weitergeklickt haben.
      if (photo && photo.isConnected && screen.getAttribute('data-reveal') === 'ready') {
        _stStarBurst(photo);
      }
    }, 560);
  }

  /**
   * @param {Element} screen
   * @param {string} state
   * @param {{ name?: string, errorMessage?: string }} [options]
   */
  _setCharacterState(screen, state, { name, errorMessage } = {}) {
    screen.setAttribute('data-state', state);
    // Sonderfall-Fehler setzt _showOutfitRejected danach wieder; jeder andere
    // Zustandswechsel beendet ihn.
    screen.removeAttribute('data-error-code');
    // The reveal animation must not run against the old upload thumbnail or an
    // empty frame - `data-reveal` is set once the finished artwork can paint.
    if (state !== 'done') {
      screen.removeAttribute('data-reveal');
      clearTimeout(this._celebrateTimer);
    }
    // Only the loading state shows the waiting content - stop rotating otherwise.
    if (state !== 'loading') this._stopWaitReviewRotation();
    const eyebrow = screen.querySelector('.st-preview-card__eyebrow');
    const heading = screen.querySelector('.st-preview-card__heading');
    const meta = screen.querySelector('.st-preview-card__meta');
    const pctEl = screen.querySelector('.st-preview-card__pct');
    const fill = /** @type {HTMLElement | null} */ (screen.querySelector('.st-preview-card__fill'));
    const btn = /** @type {HTMLButtonElement | null} */ (screen.querySelector('.st-btn--continue'));

    if (state === 'idle') {
      if (eyebrow) eyebrow.textContent = 'Bereit zum Start';
      if (heading) heading.textContent = 'Dein Held wartet';
      if (meta) meta.textContent = 'Starte gleich …';
      if (fill) fill.style.width = '0%';
      if (pctEl) pctEl.textContent = '0%';
      if (btn) btn.innerHTML = 'Charakter erstellen';
    } else if (state === 'loading') {
      // Jede Generierung wird einzeln bewertet: sonst traegt der Daumen vom
      // ersten Versuch die Aussage ueber ein Bild, das der Kunde nie sah.
      this.characterRating = '';
      const rate = screen.querySelector('.st-rate');
      if (rate) {
        rate.setAttribute('data-answered', 'false');
        rate.removeAttribute('data-rating');
        const thanks = rate.querySelector('.st-rate__thanks');
        if (thanks) thanks.textContent = '';
      }
      if (eyebrow) eyebrow.textContent = 'Magie entsteht';
      if (heading) heading.textContent = 'Charakter wird erstellt…';
      if (meta) meta.textContent = 'Dauert ca. 1 Minute';
      if (fill) fill.style.width = '0%';
      if (pctEl) pctEl.textContent = '0%';
      if (btn) btn.innerHTML = '<span class="st-btn__spinner"></span>Magie entsteht…';
    } else if (state === 'error') {
      if (eyebrow) eyebrow.textContent = 'Hoppla!';
      if (heading) heading.textContent = 'Etwas hat nicht geklappt';
      if (meta) meta.textContent = errorMessage || 'Etwas ist schiefgelaufen, bitte versuchen Sie es nochmal.';
      if (fill) fill.style.width = '0%';
      if (pctEl) pctEl.textContent = '';
      if (btn) btn.innerHTML = 'Erneut versuchen';
    } else if (state === 'done') {
      if (eyebrow) eyebrow.textContent = 'Fertig';
      if (heading) heading.textContent = `Hallo, ${name || this.childName || '...'}!`;
      if (meta) meta.textContent = 'Charakter erstellt';
      if (fill) fill.style.width = '100%';
      if (pctEl) pctEl.textContent = '100%';
      if (btn) btn.innerHTML = `Weiter ${ICON_ARROW_RIGHT}`;
      const img = /** @type {HTMLImageElement | null} */ (screen.querySelector('.st-preview-card__img'));
      if (img && this.previewImageUrl) {
        const src = this._previewSrc();
        if (img.getAttribute('src') !== src) img.src = src;
        this._revealWhenPainted(screen, img);
      }
      // Keyed on the generation id, not a boolean: this state is re-applied
      // every time the user navigates back to step 3, which is not a new
      // preview. A regenerated preview gets a fresh uniqid and fires again.
      if (this.previewImageUrl && this._previewTrackedFor !== this.previewUniqid) {
        this._previewTrackedFor = this.previewUniqid;
        _stTrack('preview_generated', {
          step: String(this.currentStep),
          product_id: this.getAttribute('data-product-id') || undefined,
        });
      }
    }
    this._updateCharacterBubble(screen, state, name);
    this._updateRateLabel(screen);
    this._validateStep(screen.closest('.custom-overlay'));
  }

  /**
   * "Ja, das ist sie/er!" statt eines neutralen "passt": Die Frage ist, ob der
   * Kunde sein Kind wiedererkennt - die Antwort darf genauso konkret klingen.
   * Das Geschlecht kann sich noch aendern (zurueck auf Schritt 1), deshalb wird
   * die Beschriftung bei jedem Zustandswechsel neu gesetzt statt einmalig im
   * Markup. Ohne Auswahl bleibt die neutrale Fassung stehen.
   * @param {Element} screen
   */
  _updateRateLabel(screen) {
    const label = screen.querySelector('.st-rate__btn-label');
    if (!label) return;
    const pronoun = this.gender === 'Mädchen' ? 'sie' : this.gender ? 'er' : '';
    label.textContent = pronoun ? `Ja, das ist ${pronoun}!` : 'Ja, erkenne ich!';
  }

  /**
   * Sobald der Charakter steht, kommentiert das Maskottchen das Ergebnis - der
   * Schritt-Text ("Jetzt zaubern wir ...") stimmt dann nicht mehr. Alle anderen
   * Zustaende fallen auf den Text aus stepInfo zurueck, damit ein Zurueck auf
   * Schritt 3 oder ein Neustart wieder die Ausgangs-Ansprache zeigt.
   * @param {Element} screen
   * @param {string} state
   * @param {string} [name]
   */
  _updateCharacterBubble(screen, state, name) {
    const bubble = /** @type {HTMLElement | null} */ (screen.querySelector('.st-mascot__text'));
    if (!bubble) return;
    const stepText = (this.stepInfo[3] && this.stepInfo[3].bubble) || '';
    let text = stepText;
    if (state === 'done') {
      const hero = name || this.childName || '';
      const pronoun = this.gender === 'Mädchen' ? 'sie' : 'ihn';
      text = hero
        ? `Da ist ${hero}! 🎉 Erkennst du ${pronoun}?`
        : 'Da ist dein Held! 🎉 Erkennst du ihn?';
    }
    // textContent enthaelt auch waehrend des Tippens den vollen Satz (getippter
    // und noch verborgener Teil), der Vergleich verhindert also ein Neustarten
    // des Typewriters, wenn derselbe Zustand erneut gesetzt wird.
    if (bubble.textContent === text) return;
    this._typewriter(bubble, text);
  }

  /**
   * @param {Element} screen
   * @param {string} state
   */
  _setBookPreviewState(screen, state) {
    screen.setAttribute('data-state', state);
    const eyebrow = screen.querySelector('.st-preview-card__eyebrow');
    const heading = screen.querySelector('.st-preview-card__heading');
    const meta = screen.querySelector('.st-preview-card__meta');
    const pctEl = screen.querySelector('.st-preview-card__pct');
    const fill = /** @type {HTMLElement | null} */ (screen.querySelector('.st-preview-card__fill'));
    const btn = /** @type {HTMLButtonElement | null} */ (screen.querySelector('.st-btn--continue'));

    if (state === 'idle') {
      if (eyebrow) eyebrow.textContent = 'Bereit zum Start';
      if (heading) heading.textContent = 'Dein Buch wartet';
      if (meta) meta.textContent = 'Starte gleich …';
      if (fill) fill.style.width = '0%';
      if (pctEl) pctEl.textContent = '';
      if (btn) btn.innerHTML = `Weiter ${ICON_ARROW_RIGHT}`;
    } else if (state === 'loading') {
      if (eyebrow) eyebrow.textContent = 'Buch entsteht';
      if (heading) heading.textContent = 'Buchvorschau wird erstellt…';
      if (meta) meta.textContent = '≈ 30 Sekunden verbleibend';
      if (fill) fill.style.width = '0%';
      if (pctEl) pctEl.textContent = '0%';
      if (btn) btn.innerHTML = '<span class="st-btn__spinner"></span>Buch entsteht…';
    } else if (state === 'done') {
      if (eyebrow) eyebrow.textContent = 'Fertig';
      if (heading) heading.textContent = 'Deine Buchvorschau ist da!';
      if (meta) meta.textContent = 'Buchvorschau erstellt';
      if (fill) fill.style.width = '100%';
      if (pctEl) pctEl.textContent = '100%';
      if (btn) btn.innerHTML = `Weiter ${ICON_ARROW_RIGHT}`;
      const img = /** @type {HTMLImageElement | null} */ (screen.querySelector('.st-preview-card__img'));
      if (img && this.bookPreviewImageUrl) img.src = this.bookPreviewImageUrl;
      const resultImg = /** @type {HTMLImageElement | null} */ (screen.querySelector('.st-preview-result__img'));
      if (resultImg && this.bookPreviewImageUrl) resultImg.src = this.bookPreviewImageUrl;
      const resultTitle = screen.querySelector('.st-preview-result__title');
      if (resultTitle) {
        const productName = this._getCurrentProductName();
        const childName = this.childName || '';
        resultTitle.textContent = childName ? `${childName} in "${productName}"` : productName;
      }
    }
    this._validateStep(screen.closest('.custom-overlay'));
  }

  // ---------------------------------------------------------------------------
  // Character step handling (preview generation trigger)
  // ---------------------------------------------------------------------------

  _onCharacterContinueClick(overlay) {
    const screen = overlay.querySelector('.st-screen--character');
    if (!screen) return;
    const state = screen.getAttribute('data-state') || 'idle';

    if (state === 'idle' || state === 'error') {
      this._setCharacterState(screen, 'loading');
      this._connectPreviewGeneration(overlay);
    } else if (state === 'done') {
      this._submitPreviewDetails();
      this._next(overlay);
      this._saveCharacterToStorage();
    }
  }

  /**
   * Backend-Slug des Buchs (Produkt-Metafeld custom.book_slug). Ohne ihn waehlt
   * die API ihr Standardbuch - bei mehr als einem Buch mit Vorschau-Cover also
   * das falsche. Leer = Feld nicht mitschicken, nie einen geratenen Wert.
   * @returns {string | undefined}
   */
  _bookSlug() {
    return (this.getAttribute('data-book-slug') || '').trim() || undefined;
  }

  _submitPreviewDetails() {
    const uniqid = this.previewUniqid;
    if (!uniqid) return;
    const name = this.childName;
    const gender = this._mapGenderForApi(this.gender);
    // Idempotent against double-clicks: only submit once per (uniqid, name, gender) tuple
    const key = `${uniqid}|${name}|${gender}`;
    if (this._detailsSubmittedFor === key) return;
    this._detailsSubmittedFor = key;

    if (CustomAlertButton.devMode) {
      console.log('⚙️ Dev mode: skipping preview/details API call', { uniqid, name, gender });
      return;
    }

    fetch(`${STARTALES_API_BASE}/api/v1/preview/details`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shop': _stGetShop(),
        'X-Session-Id': _stGetSessionId(),
      },
      keepalive: true,
      body: JSON.stringify({ uniqid, name, gender, book: this._bookSlug() }),
    })
      .then(response => response.json())
      .then(data => {
        // The API re-renders the preview from the stored raw image (~250ms, no
        // new generation) and hands back the same URL with new content, so the
        // browser has to be told to refetch it.
        if (data && data.image_url) {
          this.previewImageUrl = data.image_url;
          this.previewDisplayUrl = `${data.image_url}${data.image_url.includes('?') ? '&' : '?'}t=${Date.now()}`;
          this._refreshPreviewImages();
        }
      })
      .catch(() => {});
  }

  /**
   * URL to render in the UI. Falls back to the canonical preview URL — the
   * cache-busted variant only exists after a re-render, and the canonical one
   * is what goes into the cart line item.
   */
  _previewSrc() {
    return this.previewDisplayUrl || this.previewImageUrl;
  }

  /** Repaint every visible preview image with the current display URL. */
  _refreshPreviewImages() {
    const overlay = document.querySelector('.custom-overlay');
    if (!overlay) return;
    const src = this._previewSrc();
    if (!src) return;
    overlay
      .querySelectorAll('.st-screen--character .st-preview-card__img, .st-summary__cover-img--character')
      .forEach(/** @param {Element} img */ (img) => { /** @type {HTMLImageElement} */ (img).src = src; });
  }

  /**
   * Perceived-progress curve for preview generation.
   *
   * Replaces the old ease-out cubic, which sprinted to ~70% and then froze at
   * 92% — the visible stall is what made people abandon. Instead we move
   * steadily (near-linear) up to 90% across the expected duration, then keep
   * creeping toward 99% asymptotically so the bar NEVER looks stuck even when
   * the backend runs long. Returns an integer percentage (8–99).
   *
   * @param {number} elapsed  Milliseconds since generation started.
   * @param {number} duration Expected generation duration in milliseconds.
   * @returns {number}
   */
  static _previewProgress(elapsed, duration) {
    const start = 8;
    const target = 90; // reached at the expected duration
    const cap = 99; // hard ceiling until the real result arrives
    let pct;
    if (elapsed < duration) {
      // Steady, honest climb — no deceleration where people are most impatient.
      pct = start + (target - start) * (elapsed / duration);
    } else {
      // Overtime: slow exponential creep so it always shows life.
      const over = elapsed - duration;
      pct = target + (cap - target) * (1 - Math.exp(-over / (duration * 0.5)));
    }
    return Math.max(start, Math.min(cap, Math.round(pct)));
  }

  _connectPreviewGeneration(overlay) {
    this._startPreviewGeneration();
    const screen = overlay.querySelector('.st-screen--character');
    if (!screen) return;
    const fill = /** @type {HTMLElement | null} */ (screen.querySelector('.st-preview-card__fill'));
    const meta = screen.querySelector('.st-preview-card__meta');
    const pctEl = screen.querySelector('.st-preview-card__pct');
    const gen = this._previewGen;
    // Passend zur Ansage "ca. 1 Minute": lieber frueher fertig als ein Balken,
    // der bei 90% haengen bleibt.
    const duration = 55000;

    // Social proof rotates for as long as the customer waits.
    this._startWaitReviewRotation(screen);

    let stopped = false;
    // The bar only moves a whole percent every few hundred ms - writing width and
    // the label on every frame forced a layout per frame and starved the mascot
    // typewriter running alongside it. Only touch the DOM when the value changed.
    let lastPct = -1;
    const animate = () => {
      if (stopped || gen.done) return;
      const elapsed = Date.now() - (gen.startTime || Date.now());
      const pct = CustomAlertButton._previewProgress(elapsed, duration);
      if (pct !== lastPct) {
        lastPct = pct;
        if (fill) fill.style.width = `${pct}%`;
        if (pctEl) pctEl.textContent = `${pct}%`;
      }
      requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);

    const rotatingMeta = [
      'Dauert ca. 1 Minute',
      'Gesichtszüge werden angepasst …',
      'Details werden verfeinert …',
      'Letzter Feinschliff …',
    ];
    let i = 0;
    const metaInterval = setInterval(() => {
      if (gen.done) { clearInterval(metaInterval); return; }
      i = (i + 1) % rotatingMeta.length;
      if (meta) meta.textContent = rotatingMeta[i];
    }, 4000);

    gen.promise?.then(() => {
      stopped = true;
      clearInterval(metaInterval);
      this._setCharacterState(screen, 'done');
    }).catch(err => {
      stopped = true;
      clearInterval(metaInterval);
      // A newer generation took over - its own handler will manage the UI.
      if (err && err.message === 'stale') return;
      console.error('Preview generation failed:', err);
      const fallback = 'Etwas ist schiefgelaufen, bitte versuchen Sie es nochmal.';
      const errorMessage = (err && err.userFacing && err.message) ? err.message : fallback;
      // Kleidung vom Foto nicht uebernehmbar: Foto und Daten sind in Ordnung,
      // nur das Outfit nicht - also die zwei Auswege anbieten statt "Erneut
      // versuchen", das mit demselben Foto wieder abgelehnt wuerde.
      if (err && err.code === 'PHOTO_OUTFIT_REJECTED' && err.outfit === 'photo') {
        this._showOutfitRejected(screen, errorMessage);
        return;
      }
      this._setCharacterState(screen, 'error', { errorMessage });
    });
  }

  /** @param {Element} overlay */
  _connectBookPreviewGeneration(overlay) {
    this._startBookPreviewGeneration();
    const screen = overlay.querySelector('.st-screen--book-preview');
    if (!screen) return;
    const fill = /** @type {HTMLElement | null} */ (screen.querySelector('.st-preview-card__fill'));
    const meta = screen.querySelector('.st-preview-card__meta');
    const pctEl = screen.querySelector('.st-preview-card__pct');
    const gen = this._bookPreviewGen;
    const duration = 70000;

    let stopped = false;
    // The bar only moves a whole percent every few hundred ms - writing width and
    // the label on every frame forced a layout per frame and starved the mascot
    // typewriter running alongside it. Only touch the DOM when the value changed.
    let lastPct = -1;
    const animate = () => {
      if (stopped || gen.done) return;
      const elapsed = Date.now() - (gen.startTime || Date.now());
      const pct = CustomAlertButton._previewProgress(elapsed, duration);
      if (pct !== lastPct) {
        lastPct = pct;
        if (fill) fill.style.width = `${pct}%`;
        if (pctEl) pctEl.textContent = `${pct}%`;
      }
      requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);

    const rotatingMeta = [
      '≈ 60 Sekunden verbleibend',
      'Szene wird komponiert …',
      'Charakter wird ins Buch gemalt …',
      'Letzter Feinschliff …',
    ];
    let i = 0;
    const metaInterval = setInterval(() => {
      if (gen.done) { clearInterval(metaInterval); return; }
      i = (i + 1) % rotatingMeta.length;
      if (meta) meta.textContent = rotatingMeta[i] || '';
    }, 4000);

    gen.promise?.then(() => {
      stopped = true;
      clearInterval(metaInterval);
      this._setBookPreviewState(screen, 'done');
    }).catch(err => {
      stopped = true;
      clearInterval(metaInterval);
      console.error('Book preview generation failed:', err);
      this._setBookPreviewState(screen, 'idle');
      if (meta) meta.textContent = err.message || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
    });
  }

  _startBookPreviewGeneration() {
    // Siehe _startPreviewGeneration: ein Fehlversuch darf den naechsten nicht
    // blockieren.
    if (this._bookPreviewGen.error) {
      this._bookPreviewGen = { promise: null, progress: 0, done: false, error: null, startTime: 0 };
    }
    if (this._bookPreviewGen.promise) return;
    if (this.bookPreviewImageUrl) return;
    if (!this.previewUniqid) return;

    /** @type {{ promise: Promise<string> | null, progress: number, done: boolean, error: string | null, startTime: number }} */
    const myGen = { promise: null, progress: 0, done: false, error: null, startTime: Date.now() };
    this._bookPreviewGen = myGen;

    myGen.promise = (async () => {
      try {
        let imageUrl;
        if (CustomAlertButton.devMode) {
          console.log('⚙️ Dev mode: skipping API, simulating 30s book preview...');
          await new Promise(r => setTimeout(r, 3000));
          imageUrl = this.previewImageUrl || 'https://placehold.co/600x800/e8d5b7/333?text=DEV+BOOK';
        } else {
          const response = await fetch(`${STARTALES_API_BASE}/api/v1/preview/book`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shop': _stGetShop(),
              'X-Session-Id': _stGetSessionId(),
            },
            body: JSON.stringify({ uniqid: this.previewUniqid, book: this._bookSlug() }),
          });
          const data = await response.json();
          if (!response.ok || !data.success) {
            throw new Error(data.error || 'Ein unerwarteter Fehler ist aufgetreten.');
          }
          imageUrl = data.image_url;
        }
        if (this._bookPreviewGen !== myGen) throw new Error('stale');
        this.bookPreviewImageUrl = imageUrl;
        myGen.done = true;
        myGen.progress = 100;
        return imageUrl;
      } catch (err) {
        if (this._bookPreviewGen === myGen) {
          myGen.done = true;
          myGen.error = (err instanceof Error ? err.message : null) || 'Ein unerwarteter Fehler ist aufgetreten.';
        }
        throw err;
      }
    })();
  }

  _startPreviewGeneration() {
    // Eine gescheiterte Generierung haelt sonst fuer immer ihre abgelehnte
    // Promise: der naechste Versuch stiege hier sofort wieder aus und der alte
    // Fehler wuerde erneut angezeigt. Betrifft jeden Einstieg gleichermassen -
    // "Erneut versuchen", Zurueckblaettern auf Schritt 3, Wiederherstellen.
    if (this._previewGen.error) this._resetPreviewGeneration();
    if (this._previewGen.promise) return;
    if (this.previewImageUrl) return;

    /** @type {{ promise: Promise<string> | null, progress: number, done: boolean, error: string | null, startTime: number }} */
    const myGen = { promise: null, progress: 0, done: false, error: null, startTime: Date.now() };
    this._previewGen = myGen;

    const uniqid = `preview_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    this.previewUniqid = uniqid;

    myGen.promise = (async () => {
      try {
        let imageUrl;
        if (CustomAlertButton.devMode) {
          console.log('⚙️ Dev mode: skipping API, simulating 30s preview...');
          await new Promise(r => setTimeout(r, 3000));
          imageUrl = this.uploadedImageUrl || 'https://placehold.co/600x800/e8d5b7/333?text=DEV+MODE';
        } else {
          const formData = new FormData();
          formData.append('image', /** @type {Blob} */ (this.uploadedImageBlob), `child-photo-${Date.now()}.jpg`);
          formData.append('gender', this._mapGenderForApi(this.gender));
          formData.append('eye_color', this._mapEyeColorForApi(this.eyeColor));
          formData.append('uniqid', uniqid);
          // Name and gender are collected in step 1, so the API can render the
          // name straight into the first preview - no re-render needed.
          if (this.childName) formData.append('name', this.childName);
          const book = this._bookSlug();
          if (book) formData.append('book', book);
          // 'book' = predefined outfit of the book, 'photo' = keep the clothes
          // from the uploaded photo (paid add-on, see addToCart).
          const outfit = this._outfitIsPhoto() ? 'photo' : 'book';
          formData.append('outfit', outfit);

          const send = () => fetch(`${STARTALES_API_BASE}/api/v1/preview`, {
            method: 'POST',
            headers: {
              'X-Shop': _stGetShop(),
              'X-Session-Id': _stGetSessionId(),
            },
            body: formData,
          });
          let response;
          try {
            response = await send();
          } catch (networkErr) {
            // iOS kappt laufende Requests, wenn der Kunde waehrend der Wartezeit
            // die App wechselt ("Load failed"). Statt eines Fehlers: warten, bis
            // die Seite wieder sichtbar ist, und einmal neu anfragen - mit
            // derselben uniqid, damit das Backend sie wiedererkennen kann.
            if (this._previewGen !== myGen) throw new Error('stale');
            _stTrack('preview_retry_after_network_error', {
              step: '3',
              product_id: this.getAttribute('data-product-id') || undefined,
              meta: { hidden: document.visibilityState === 'hidden' },
            });
            await _stWhenVisible();
            if (this._previewGen !== myGen) throw new Error('stale');
            response = await send();
          }
          let data = null;
          try { data = await response.json(); } catch { /* non-JSON response */ }
          if (!response.ok || !data || !data.success) {
            const code = (data && typeof data.code === 'string') ? data.code : '';
            const fallback = code === 'PHOTO_OUTFIT_REJECTED'
              ? OUTFIT_REJECTED_FALLBACK
              : 'Etwas ist schiefgelaufen, bitte versuchen Sie es nochmal.';
            const err = /** @type {Error & { userFacing?: boolean, code?: string, outfit?: string }} */ (
              new Error((data && data.error) || fallback)
            );
            err.userFacing = true;
            // `code` + gesendetes Outfit entscheiden in _connectPreviewGeneration
            // ueber eine eigene Behandlung. `reason` der API ist intern und wird
            // bewusst nicht uebernommen.
            err.code = code;
            err.outfit = outfit;
            throw err;
          }
          imageUrl = data.image_url;
        }
        // Abandon stale results if a newer generation has been started (e.g. gender changed)
        if (this._previewGen !== myGen) throw new Error('stale');
        this.previewImageUrl = imageUrl;
        this.previewDisplayUrl = '';
        myGen.done = true;
        myGen.progress = 100;
        return imageUrl;
      } catch (err) {
        if (this._previewGen === myGen) {
          myGen.done = true;
          myGen.error = (err instanceof Error ? err.message : null) || 'Ein unerwarteter Fehler ist aufgetreten.';
        }
        throw err;
      }
    })();
  }

  // ---------------------------------------------------------------------------
  // Upload handling
  // ---------------------------------------------------------------------------

  /**
   * Nimmt eine Datei aus einem Datei-Dialog entgegen und uebergibt sie an den
   * eigentlichen Handler. Jede Ablehnung und jeder Fehler wird sichtbar - vorher
   * wurden beide stillschweigend verworfen, was im Session-Replay aussah, als
   * reagiere der Upload-Kasten ueberhaupt nicht.
   *
   * @param {File | null | undefined} file
   * @param {Element} overlay
   * @param {(file: File) => Promise<void>} handler
   */
  async _acceptPhoto(file, overlay, handler) {
    this._clearUploadError(overlay);

    // Abgebrochener Datei-Dialog: kein Fehler, einfach nichts tun.
    if (!file) return;

    if (!this._isUsablePhoto(file)) {
      _stTrack('photo_rejected', {
        step: String(this.currentStep),
        product_id: this.getAttribute('data-product-id') || undefined,
        meta: { file_type: file.type || 'unknown', file_name_ext: (file.name.split('.').pop() || '').toLowerCase() },
      });
      this._showUploadError(overlay, 'Diese Datei ist kein Bild. Bitte wähle ein Foto (JPG, PNG, HEIC oder WebP).');
      return;
    }

    this._setUploadBusy(overlay, true);
    try {
      await handler(file);
    } catch (err) {
      console.error('Photo upload failed:', err);
      const userFacing = /** @type {any} */ (err)?.userFacing ? String(/** @type {Error} */ (err).message) : '';
      this._showUploadError(
        overlay,
        userFacing || 'Das Foto konnte nicht verarbeitet werden. Bitte versuche es noch einmal oder wähle ein anderes Bild.'
      );
      _stTrack('photo_upload_failed', {
        step: String(this.currentStep),
        product_id: this.getAttribute('data-product-id') || undefined,
        meta: { file_type: file.type || 'unknown' },
      });
    } finally {
      this._setUploadBusy(overlay, false);
    }
  }

  /**
   * Ob eine Datei als Foto durchgeht. Der MIME-Typ allein reicht nicht: manche
   * Android-Picker und Cloud-Anbieter liefern HEIC/JPEG mit leerem oder
   * generischem Typ ("", "application/octet-stream"), was vorher zum stillen
   * Verwerfen fuehrte. Dann entscheidet die Dateiendung.
   *
   * @param {File} file
   */
  _isUsablePhoto(file) {
    if (file.type && file.type.startsWith('image/')) return true;
    const ext = (file.name || '').split('.').pop();
    if (!ext) return false;
    return ['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'gif', 'bmp', 'tif', 'tiff', 'avif']
      .includes(ext.toLowerCase());
  }

  /**
   * Waehrend Komprimierung und Upload laeuft: der Kasten zeigt einen Spinner.
   * Ohne das ist ein grosses Foto auf einem langsamen Geraet mehrere Sekunden
   * lang nicht von "der Klick kam nicht an" zu unterscheiden.
   *
   * @param {Element} overlay
   * @param {boolean} busy
   */
  _setUploadBusy(overlay, busy) {
    const cta = /** @type {HTMLElement | null} */ (overlay.querySelector('.st-screen--upload .st-upload-cta'));
    if (cta) {
      cta.setAttribute('data-busy', busy ? 'true' : 'false');
      const chevron = /** @type {HTMLElement | null} */ (cta.querySelector('.st-upload-cta__chevron'));
      if (chevron) chevron.hidden = busy;
      let spinner = /** @type {HTMLElement | null} */ (cta.querySelector('.st-upload-cta__spinner'));
      if (busy && !spinner) {
        spinner = document.createElement('div');
        spinner.className = 'st-upload-cta__spinner';
        cta.appendChild(spinner);
      } else if (!busy && spinner) {
        spinner.remove();
      }
    }
    const restart = /** @type {HTMLElement | null} */ (overlay.querySelector('.st-action-btn--restart'));
    if (restart) restart.setAttribute('data-busy', busy ? 'true' : 'false');
    // Outfit-Ablehnung: waehrend das neue Foto vorbereitet wird, darf der
    // Buch-Outfit-Weg nicht parallel loslaufen.
    const reject = overlay.querySelector('.st-screen--character .st-outfit-reject');
    if (reject) {
      reject.setAttribute('data-busy', busy ? 'true' : 'false');
      const bookBtn = /** @type {HTMLButtonElement | null} */ (reject.querySelector('.st-outfit-reject__btn--book'));
      if (bookBtn) bookBtn.disabled = busy;
    }
  }

  /**
   * @param {Element} overlay
   * @param {string} message
   */
  _showUploadError(overlay, message) {
    // In den aktiven Screen schreiben: dieselbe Meldung kann aus Schritt 2
    // (erstes Foto) oder Schritt 3 ("Neues Foto") kommen.
    const screen = overlay.querySelector('.st-screen--active') || overlay;
    const box = /** @type {HTMLElement | null} */ (screen.querySelector('.st-upload-error'));
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
    this._scrollInOverlay(box, 'nearest');
  }

  /** @param {Element} overlay */
  _clearUploadError(overlay) {
    overlay.querySelectorAll('.st-upload-error').forEach(/** @param {Element} el */ (el) => {
      /** @type {HTMLElement} */ (el).hidden = true;
    });
  }

  async _handleUpload(file, overlay) {
    _stTrack('photo_upload_started', {
      step: '2',
      product_id: this.getAttribute('data-product-id') || undefined,
      meta: { file_size: file.size, file_type: file.type },
    });

    const { upload: fileToUpload, displayUrl } = await this._preparePhoto(file);

    this.uploadedImageBlob = fileToUpload;
    this._setUploadedImageUrl(displayUrl);

    _stTrack('photo_uploaded', {
      step: '2',
      product_id: this.getAttribute('data-product-id') || undefined,
      meta: { final_size: fileToUpload.size, compressed: fileToUpload !== file },
    });

    // Stay on step 2 — the customer still has to confirm the consent checkbox.
    const screen = overlay.querySelector('.st-screen--upload');
    if (screen) this._renderUploadPhoto(screen);
    this._validateStep(overlay);
    this._scrollToPhotoPreview(overlay);
  }

  /**
   * Reset character + book preview state so a fresh generation can run.
   */
  _resetPreviewGeneration() {
    this.previewImageUrl = '';
    this.previewDisplayUrl = '';
    this.previewUniqid = '';
    this._detailsSubmittedFor = '';
    this._previewGen = { promise: null, progress: 0, done: false, error: null, startTime: 0 };
    this.bookPreviewImageUrl = '';
    this._bookPreviewGen = { promise: null, progress: 0, done: false, error: null, startTime: 0 };
  }

  /**
   * "Nochmal" darf einmal benutzt werden. Solange der Versuch noch offen ist,
   * steht am Button nichts weiter - kein Zaehler, der Knappheit ankuendigt,
   * bevor der Kunde ueberhaupt etwas getan hat. Erst wenn der Versuch weg ist,
   * erklaert eine Zeile, warum der Button aus ist und wie es weitergeht.
   * @param {Element} overlay
   */
  _updateRetryState(overlay) {
    // Nach einer Wiederaufnahme aus dem Speicher fehlt das Foto (wird nicht
    // gespeichert) - "Nochmal" ginge dann ins Leere, nur "Neues Foto" hilft.
    const exhausted = this.retryCount >= this.retryMax || !this.uploadedImageBlob;
    const btn = /** @type {HTMLButtonElement | null} */ (overlay.querySelector('.st-action-btn--retry'));
    if (btn) btn.disabled = exhausted;

    const hint = /** @type {HTMLElement | null} */ (overlay.querySelector('.st-retry-hint'));
    if (hint) {
      hint.textContent = exhausted
        ? 'Noch nicht der Richtige? Mit einem neuen Foto startest du die Erstellung frisch.'
        : '';
      hint.hidden = !exhausted;
    }
  }

  /**
   * "Nochmal" and "Neues Foto" both start a fresh generation. With the outfit
   * step on, they first lead back there: a second attempt is the natural
   * moment to try the other outfit. Entering step 3 again auto-starts the
   * generation (see _updateScreen), so nothing else has to be kicked off here.
   * @param {Element} overlay
   */
  _restartCharacterPreview(overlay) {
    const screen = overlay.querySelector('.st-screen--character');
    if (!screen) return;
    this._resetPreviewGeneration();
    if (STARTALES_OUTFIT_ENABLED) {
      this._setCharacterState(screen, 'idle');
      this.currentStep = STEP_OUTFIT;
      this._updateScreen(overlay);
      return;
    }
    this._setCharacterState(screen, 'loading');
    this._connectPreviewGeneration(overlay);
  }

  /**
   * @param {File} file
   * @param {Element} overlay
   */
  async _handleRestartUpload(file, overlay) {
    const { upload: fileToUpload, displayUrl } = await this._preparePhoto(file);
    this.uploadedImageBlob = fileToUpload;
    this._setUploadedImageUrl(displayUrl);
    const screen = overlay.querySelector('.st-screen--character');
    const img = /** @type {HTMLImageElement | null} */ (screen ? screen.querySelector('.st-preview-card__img') : null);
    if (img) img.src = displayUrl;
    this.retryCount = 1;
    this._updateRetryState(overlay);
    this._restartCharacterPreview(overlay);
  }

  /**
   * Fehlerzustand fuer PHOTO_OUTFIT_REJECTED: Meldung der API plus zwei Wege
   * weiter (anderes Foto / Buch-Outfit). Der Footer-Button "Erneut versuchen"
   * ist per CSS ausgeblendet, er wuerde dasselbe Foto erneut ablehnen lassen.
   * @param {Element} screen
   * @param {string} message
   */
  _showOutfitRejected(screen, message) {
    this._setCharacterState(screen, 'error', { errorMessage: 'Outfit vom Foto nicht möglich' });
    screen.setAttribute('data-error-code', 'outfit-rejected');
    const box = /** @type {HTMLElement | null} */ (screen.querySelector('.st-outfit-reject'));
    if (!box) return;
    box.setAttribute('data-busy', 'false');
    const text = box.querySelector('.st-outfit-reject__text');
    if (text) text.textContent = message;
    const bookBtn = /** @type {HTMLButtonElement | null} */ (box.querySelector('.st-outfit-reject__btn--book'));
    if (bookBtn) bookBtn.disabled = false;
    this._scrollInOverlay(box, 'nearest');
    _stTrack('photo_outfit_rejected', {
      step: '3',
      product_id: this.getAttribute('data-product-id') || undefined,
    });
  }

  /**
   * "Outfit aus dem Buch nehmen": Outfit auf "book" (damit faellt auch das
   * Add-on "Outfit vom Foto" weg - es haengt erst in addToCart am Warenkorb und
   * richtet sich nach outfitChoice) und dieselbe Anfrage mit demselben Foto neu
   * senden. Neue uniqid, damit das Backend nicht die abgelehnte Generierung
   * wiedererkennt. Kein "Nochmal"-Versuch, retryCount bleibt.
   * @param {Element} overlay
   */
  _retryWithBookOutfit(overlay) {
    const screen = overlay.querySelector('.st-screen--character');
    // Doppelklick: der erste Klick hat den Zustand schon auf "loading" gesetzt.
    if (!screen || screen.getAttribute('data-error-code') !== 'outfit-rejected') return;
    if (!this.uploadedImageBlob) return;
    _stTrack('photo_outfit_rejected_choice', {
      step: '3',
      product_id: this.getAttribute('data-product-id') || undefined,
      meta: { choice: 'book_outfit' },
    });
    this._setOutfit(overlay, 'book');
    this._resetPreviewGeneration();
    this._setCharacterState(screen, 'loading');
    this._connectPreviewGeneration(overlay);
  }

  /**
   * "Anderes Foto hochladen": neues Foto ersetzt das abgelehnte, Outfit bleibt
   * "photo", und die Generierung startet direkt neu - ohne Umweg ueber den
   * Outfit-Schritt wie bei "Neues Foto", die Wahl steht ja schon fest.
   * @param {File} file
   * @param {Element} overlay
   */
  async _handleOutfitRejectedUpload(file, overlay) {
    const screen = overlay.querySelector('.st-screen--character');
    if (!screen || screen.getAttribute('data-error-code') !== 'outfit-rejected') return;
    const { upload: fileToUpload, displayUrl } = await this._preparePhoto(file);
    _stTrack('photo_outfit_rejected_choice', {
      step: '3',
      product_id: this.getAttribute('data-product-id') || undefined,
      meta: { choice: 'new_photo' },
    });
    this.uploadedImageBlob = fileToUpload;
    this._setUploadedImageUrl(displayUrl);
    const img = /** @type {HTMLImageElement | null} */ (screen.querySelector('.st-preview-card__img'));
    if (img) img.src = displayUrl;
    this.retryCount = 1;
    this._updateRetryState(overlay);
    this._resetPreviewGeneration();
    this._setCharacterState(screen, 'loading');
    this._connectPreviewGeneration(overlay);
  }

  /**
   * Setzt die Anzeige-URL des Fotos und gibt die vorherige frei. Ohne revoke
   * bleibt bei jedem erneuten Upload das komplette Bitmap im Speicher liegen -
   * auf alten Geraeten der schnellste Weg in den Tab-Absturz.
   * @param {string} url
   */
  _setUploadedImageUrl(url) {
    const previous = this.uploadedImageUrl;
    this.uploadedImageUrl = url;
    if (!previous || previous === url || !previous.startsWith('blob:')) return;
    // Erst im naechsten Task freigeben: die <img> zeigen in genau diesem Moment
    // noch auf die alte URL und werden gleich danach umgehaengt. Sofortiges
    // revoke laesst Chrome die tote URL nachladen -> ERR_FILE_NOT_FOUND.
    setTimeout(() => URL.revokeObjectURL(previous), 0);
  }

  /**
   * Dekodiert das gewaehlte Foto genau EINMAL und leitet beide Varianten davon ab:
   *
   * - Anzeige: frueher zeigte der 150-px-Rahmen die Originaldatei. Ein 12-MP-Handy-
   *   foto heisst dann ~48 MB Bitmap auf dem Main-Thread - fuer ein Thumbnail.
   *   Jetzt eine 900-px-Kopie.
   * - Upload: nur verkleinert, wenn die Datei ueber dem API-Limit liegt. Die
   *   Kompressionsstufen zeichnen jetzt aus derselben Dekodierung statt jede Stufe
   *   ihr Zwischenergebnis neu zu dekodieren.
   *
   * @param {File} file
   * @returns {Promise<{ upload: Blob, displayUrl: string }>}
   */
  async _preparePhoto(file) {
    const MAX_UPLOAD = 4.5 * 1024 * 1024;
    const source = await this._decodePhoto(file);

    // Dekodierung fehlgeschlagen (kaputte/exotische Datei): unveraendert weiter -
    // der Upload darf daran nicht scheitern.
    if (!source) return { upload: file, displayUrl: URL.createObjectURL(file) };

    const displayBlob = await this._drawToBlob(source, PHOTO_DISPLAY_MAX_PX, 0.82);

    let upload = /** @type {Blob} */ (file);
    if (file.size > MAX_UPLOAD) {
      const steps = [
        { maxDimension: 3000, quality: 0.8 },
        { maxDimension: 2400, quality: 0.7 },
        { maxDimension: 1800, quality: 0.6 },
        { maxDimension: 1200, quality: 0.5 },
      ];
      for (const { maxDimension, quality } of steps) {
        const next = await this._drawToBlob(source, maxDimension, quality);
        if (!next) break;
        upload = next;
        if (upload.size <= MAX_UPLOAD) break;
      }
    }

    URL.revokeObjectURL(source.src);
    return { upload, displayUrl: URL.createObjectURL(displayBlob || file) };
  }

  /**
   * Ueber <img> statt createImageBitmap: nur dieser Weg respektiert die
   * EXIF-Ausrichtung zuverlaessig, sonst kaeme jedes Hochkant-Foto gedreht an.
   * @param {File} file
   * @returns {Promise<HTMLImageElement | null>}
   */
  _decodePhoto(file) {
    return new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  }

  /**
   * @param {HTMLImageElement} source
   * @param {number} maxDimension
   * @param {number} quality
   * @returns {Promise<Blob | null>}
   */
  _drawToBlob(source, maxDimension, quality) {
    return new Promise((resolve) => {
      const longest = Math.max(source.naturalWidth || source.width, source.naturalHeight || source.height);
      const scale = Math.min(1, maxDimension / (longest || maxDimension));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round((source.naturalWidth || source.width) * scale));
      canvas.height = Math.max(1, Math.round((source.naturalHeight || source.height) * scale));
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(null); return; }
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        // Canvas sofort auf 0x0: sonst haelt der Browser das Backing-Bitmap bis
        // zur naechsten GC, und wir legen bis zu fuenf davon hintereinander an.
        canvas.width = 0;
        canvas.height = 0;
        resolve(blob);
      }, 'image/jpeg', quality);
    });
  }

  _mapGenderForApi(gender) {
    return gender === 'Mädchen' ? 'female' : 'male';
  }

  _mapEyeColorForApi(eyeColor) {
    const map = {
      'Dunkelbraun': 'dunkelbraun',
      'Braun': 'braun',
      'Hellbraun': 'hellbraun',
      'Grün': 'gruen',
      'Blau': 'blau',
    };
    return map[eyeColor] || 'braun';
  }

  // ---------------------------------------------------------------------------
  // Typewriter effect for mascot bubble
  // ---------------------------------------------------------------------------

  _cancelTypewriter() {
    if (this._typewriterRaf) {
      cancelAnimationFrame(this._typewriterRaf);
      this._typewriterRaf = 0;
    }
    if (this._typewriterTimeout) {
      clearTimeout(this._typewriterTimeout);
      this._typewriterTimeout = null;
    }
  }

  /**
   * Grapheme end offsets into `text`. charAt() would split the surrogate pairs of
   * the emoji in the bubble copy and flash a replacement glyph for one frame.
   * @param {string} text
   * @returns {number[]}
   */
  _graphemeOffsets(text) {
    if (!this._graphemeCache) this._graphemeCache = new Map();
    const hit = this._graphemeCache.get(text);
    if (hit) return hit;

    /** @type {number[]} */
    const offsets = [];
    const Segmenter = /** @type {any} */ (Intl).Segmenter;
    if (typeof Segmenter === 'function') {
      for (const part of new Segmenter('de', { granularity: 'grapheme' }).segment(text)) {
        offsets.push(part.index + part.segment.length);
      }
    } else {
      for (let i = 0; i < text.length; ) {
        const cp = text.codePointAt(i) || 0;
        i += cp > 0xffff ? 2 : 1;
        offsets.push(i);
      }
    }
    this._graphemeCache.set(text, offsets);
    return offsets;
  }

  /**
   * Legt die beiden Text-Knoten der Blase an: der vollstaendige Satz steht ab dem
   * ersten Frame im Layout, der noch nicht getippte Teil ist nur per
   * visibility:hidden unsichtbar. Damit umbricht der Text von Anfang an so wie am
   * Ende - die Blase ist also pro Screen genau so hoch, wie ihr eigener Text sie
   * braucht (zwei Zeilen bleiben zwei Zeilen), und waehrend des Tippens aendert
   * sich die Hoehe kein einziges Mal.
   * @param {HTMLElement} element
   * @returns {{ typed: Text, rest: Text }}
   */
  _bubbleTextNodes(element) {
    let typedSpan = element.querySelector('.st-mascot__text-typed');
    let restSpan = element.querySelector('.st-mascot__text-rest');
    if (!typedSpan || !restSpan) {
      element.textContent = '';
      typedSpan = document.createElement('span');
      typedSpan.className = 'st-mascot__text-typed';
      typedSpan.appendChild(document.createTextNode(''));
      restSpan = document.createElement('span');
      restSpan.className = 'st-mascot__text-rest';
      restSpan.appendChild(document.createTextNode(''));
      element.appendChild(typedSpan);
      element.appendChild(restSpan);
    }
    return {
      typed: /** @type {Text} */ (typedSpan.firstChild),
      rest: /** @type {Text} */ (restSpan.firstChild),
    };
  }

  /**
   * @param {HTMLElement} element
   * @param {string} text
   * @param {number} [speed] ms per character, before the total duration is capped
   */
  _typewriter(element, text, speed = 28) {
    this._cancelTypewriter();

    const offsets = this._graphemeOffsets(text);
    const total = offsets.length;
    const { typed, rest } = this._bubbleTextNodes(element);

    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!total || reduced || document.hidden) {
      typed.data = text;
      rest.data = '';
      element.classList.remove('st-mascot__text--typing');
      return;
    }

    // A setTimeout chain drifts hard on a busy main thread - step 3 runs the
    // progress rAF, the generation request and the image decode at the same time,
    // so every 28 ms tick can land at 100 ms+ and the bubble takes forever.
    // Drive the reveal from the clock instead: slow frames now skip ahead several
    // characters and the bubble always finishes within TYPEWRITER_MAX_MS.
    const duration = Math.min(total * speed, TYPEWRITER_MAX_MS);

    typed.data = '';
    rest.data = text;
    element.classList.add('st-mascot__text--typing');

    const start = performance.now();
    let shown = 0;
    /** @param {number} now */
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const target = Math.round(progress * total);
      if (target !== shown) {
        shown = target;
        const cut = offsets[shown - 1] || 0;
        // Die Summe beider Knoten bleibt konstant, der Umbruch also auch - der
        // Browser muss nur neu zeichnen, nicht neu umbrechen.
        typed.data = text.slice(0, cut);
        rest.data = text.slice(cut);
      }
      if (progress < 1) {
        this._typewriterRaf = requestAnimationFrame(step);
        return;
      }
      this._typewriterRaf = 0;
      this._typewriterTimeout = setTimeout(() => {
        element.classList.remove('st-mascot__text--typing');
        this._typewriterTimeout = null;
      }, 600);
    };
    this._typewriterRaf = requestAnimationFrame(step);
  }

  // ---------------------------------------------------------------------------
  // Lightbox (step 5 image preview)
  // ---------------------------------------------------------------------------

  /**
   * @param {string} src
   * @param {string} [alt]
   */
  _openLightbox(src, alt) {
    const existing = document.querySelector('.st-lightbox');
    if (existing) existing.remove();

    const opener = /** @type {HTMLElement | null} */ (document.activeElement);
    const lightbox = document.createElement('div');
    lightbox.className = 'st-lightbox st-lightbox--image';
    lightbox.setAttribute('role', 'dialog');
    lightbox.setAttribute('aria-modal', 'true');
    lightbox.setAttribute('aria-label', alt || 'Bildvorschau');
    lightbox.innerHTML = `
      <button type="button" class="st-lightbox__close" aria-label="Schließen">${ICON_CLOSE}</button>
      <div class="st-lightbox__inner">
        <img class="st-lightbox__img" src="${_stEscape(src)}" alt="${_stEscape(alt || '')}" />
      </div>
    `;

    const close = () => {
      lightbox.remove();
      document.removeEventListener('keydown', onKey, true);
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    };
    // Capture + stopPropagation: Escape schliesst nur die Lightbox, nicht
    // zusaetzlich den Schliessen-Dialog des Overlays darunter.
    /** @param {KeyboardEvent} e */
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    };

    // Ein Tipp irgendwohin schliesst - auch aufs Bild selbst. Vorher reagierte
    // nur der dunkle Rand; Taps aufs Bild waren in Clarity tote Klicks.
    lightbox.addEventListener('click', close);
    document.addEventListener('keydown', onKey, true);

    document.body.appendChild(lightbox);
    /** @type {HTMLElement | null} */ (lightbox.querySelector('.st-lightbox__close'))?.focus({ preventScroll: true });
  }

  /**
   * Die "Blick ins Buch"-Section der Seite - aber nur, wenn ihr JS gelaufen ist
   * und sich unter window.StarTalesBlickInsBuch angemeldet hat. Ohne diese
   * init()-Funktion waere eine Kopie ein totes Buch, das sich nicht blaettern
   * laesst; dann bleibt der Knopf auf der Uebersicht lieber weg.
   *
   * @returns {HTMLElement | null}
   */
  _bookPeekSource() {
    const api = /** @type {any} */ (window).StarTalesBlickInsBuch;
    if (!api || typeof api.init !== 'function') return null;
    const stage = document.querySelector('[data-stl-bib]');
    const section = stage ? stage.closest('.stl-bib') : null;
    return /** @type {HTMLElement | null} */ (section);
  }

  /**
   * Schlaegt die Leseprobe der Produktseite ueber der Uebersicht auf.
   *
   * Statt das Buch nachzubauen wird die Section geklont und vom Section-JS neu
   * initialisiert - Seitenbilder, Format und Blaetter-Animation bleiben so an
   * genau einer Stelle gepflegt (Theme-Editor).
   *
   * @param {Element} overlay
   */
  _openBookPeek(overlay) {
    const source = this._bookPeekSource();
    if (!source) return;

    const existing = overlay.querySelector('.st-bookpeek');
    if (existing) existing.remove();

    const modal = document.createElement('div');
    modal.className = 'st-bookpeek';
    modal.innerHTML = `
      <div class="st-bookpeek__box" role="dialog" aria-modal="true" aria-label="Blick ins Buch">
        <div class="st-bookpeek__head">
          <span class="st-bookpeek__title">Blick ins Buch</span>
          <button type="button" class="st-bookpeek__close" aria-label="Schließen">${ICON_CLOSE}</button>
        </div>
        <div class="st-bookpeek__body"></div>
        <p class="st-bookpeek__note">
          <span class="st-bookpeek__note-icon" aria-hidden="true">${ICON_INFO}</span>
          <span>Das ist nur eine Leseprobe. Im fertigen Buch stehen überall der Name deines Kindes und die Figur aus deinem Foto.</span>
        </p>
      </div>
    `;

    const clone = /** @type {HTMLElement} */ (source.cloneNode(true));
    clone.classList.add('stl-bib--peek');
    // Abstaende/Hintergrund der Section gehoeren zur Seite, nicht ins Modal.
    clone.removeAttribute('style');
    // Editor-Marker der Kopie entfernen, sonst zeigt der Theme-Editor auf ein
    // Element, das er nicht kennt.
    clone.removeAttribute('data-shopify-editor-section');
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((el) => {
      el.removeAttribute('data-shopify-editor-block');
    });
    // Startzustand ohne sichtbares Nachblaettern setzen; das Section-JS gibt
    // die Animation nach dem ersten Frame wieder frei.
    clone.querySelector('[data-stl-bib-book]')?.classList.add('stl-bib--no-anim');
    // Die Seiten stehen sofort im Blick - lazy waere hier nur ein Flackern.
    clone.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'eager'));

    modal.querySelector('.st-bookpeek__body')?.appendChild(clone);
    overlay.appendChild(modal);

    /** @type {any} */ (window).StarTalesBlickInsBuch.init(clone);

    const close = () => {
      const stage = /** @type {any} */ (clone.querySelector('[data-stl-bib]'));
      stage?.__stlBib?.destroy?.();
      modal.remove();
      document.removeEventListener('keydown', onKey);
    };
    /** @param {KeyboardEvent} e */
    const onKey = (e) => { if (e.key === 'Escape') close(); };

    modal.addEventListener('click', (e) => {
      const target = /** @type {Element} */ (e.target);
      if (target === modal || target.closest('.st-bookpeek__close')) close();
    });
    document.addEventListener('keydown', onKey);

    _stTrack('book_peek_opened', { step: String(this.currentStep) });
  }

  /**
   * True once the customer has invested something into the flow that closing
   * would throw away (photo, name, or simply having moved past step 1).
   */
  _hasProgress() {
    return this.currentStep > 1
      || !!this.uploadedImageBlob
      || !!this.uploadedImageUrl
      || !!this.childName;
  }

  // ---------------------------------------------------------------------------
  // Fortschritt ueber Neuladen hinweg (localStorage)
  // ---------------------------------------------------------------------------

  /**
   * Schluessel des gespeicherten Durchlaufs, je Buch. Unabhaengig vom alten,
   * abgeschalteten Charakter-Speicher (STARTALES_SESSION_SAVE_ENABLED).
   * @returns {string}
   */
  _flowStorageKey() {
    return `${STARTALES_FLOW_KEY_PREFIX}${this.getAttribute('data-product-id') || window.location.pathname}`;
  }

  /**
   * Sichert den Stand nach jeder Aenderung. Bewusst NICHT gespeichert wird das
   * Kinderfoto selbst - nur das fertige Charakterbild (Server-URL). Ohne Fortschritt
   * oder solange die "Weiter, wo du aufgehoert hast?"-Frage offen ist, wird nichts
   * geschrieben: ein frisch geoeffneter Flow wuerde sonst den alten Stand loeschen.
   */
  _persistFlow() {
    if (this._resumePending || this._flowCompleted || !this._hasProgress()) return;
    const data = {
      v: 1,
      savedAt: Date.now(),
      step: this.currentStep,
      childName: this.childName,
      gender: this.gender,
      eyeColor: this.eyeColor,
      outfitChoice: this.outfitChoice,
      previewImageUrl: /^https?:\/\//i.test(this.previewImageUrl || '') ? this.previewImageUrl : '',
      previewUniqid: this.previewUniqid,
      bookCoverType: this.bookCoverType,
      dedicationEnabled: this.dedicationEnabled,
      dedicationText: this.dedicationText,
      mattCoverEnabled: this.mattCoverEnabled,
      retryCount: this.retryCount,
      characterRating: this.characterRating,
      generating: !!(this._previewGen && this._previewGen.promise && !this._previewGen.done),
    };
    try {
      localStorage.setItem(this._flowStorageKey(), JSON.stringify(data));
    } catch { /* Privatmodus / Speicher voll: dann eben ohne Wiederaufnahme */ }
  }

  /** @returns {any} Gespeicherter Stand oder null (abgelaufen/kaputt/leer). */
  _readStoredFlow() {
    try {
      const raw = localStorage.getItem(this._flowStorageKey());
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data || data.v !== 1 || !data.savedAt || Date.now() - data.savedAt > STARTALES_FLOW_MAX_AGE_MS) {
        localStorage.removeItem(this._flowStorageKey());
        return null;
      }
      // Ein Stand ohne Vornamen ist nichts, wofuer sich die Frage lohnt.
      return data.childName ? data : null;
    } catch {
      return null;
    }
  }

  _clearStoredFlow() {
    try { localStorage.removeItem(this._flowStorageKey()); } catch { /* egal */ }
  }

  /**
   * Fragt beim Oeffnen eines frischen Flows, ob der gespeicherte Stand weiter-
   * gehen soll. Gleiche Optik wie der Schliessen-Dialog (.st-confirm).
   * @param {Element} overlay
   * @param {any} saved
   */
  _offerResume(overlay, saved) {
    this._resumePending = true;
    const modal = document.createElement('div');
    modal.className = 'st-confirm st-confirm--resume';
    const thumb = saved.previewImageUrl
      ? `<img class="st-confirm__thumb" src="${_stEscape(saved.previewImageUrl)}" alt="" />`
      : '';
    modal.innerHTML = `
      <div class="st-confirm__box" role="alertdialog" aria-modal="true" aria-labelledby="st-resume-title">
        ${thumb}
        <div class="st-confirm__title" id="st-resume-title">Weiter, wo du aufgehört hast?</div>
        <div class="st-confirm__text">Wir haben dein Buch für <strong>${_stEscape(saved.childName)}</strong> gespeichert.</div>
        <button type="button" class="st-btn st-btn--orange st-confirm__resume">Weitermachen</button>
        <button type="button" class="st-confirm__abort st-confirm__fresh">Neu beginnen</button>
      </div>
    `;
    const finish = () => {
      modal.remove();
      this._resumePending = false;
      document.removeEventListener('keydown', onKey, true);
    };
    /** @param {KeyboardEvent} e */
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Escape = Dialog weg, Stand bleibt fuers naechste Oeffnen erhalten.
      e.stopPropagation();
      finish();
    };
    modal.addEventListener('click', (e) => {
      const target = /** @type {Element} */ (e.target);
      if (target.closest('.st-confirm__resume')) {
        finish();
        _stTrack('flow_resumed', {
          step: String(saved.step),
          product_id: this.getAttribute('data-product-id') || undefined,
        });
        this._applyStoredFlow(overlay, saved);
        return;
      }
      if (target.closest('.st-confirm__fresh')) {
        finish();
        this._clearStoredFlow();
        _stSyncTriggerLabels(false);
        const name = /** @type {HTMLInputElement | null} */ (overlay.querySelector('#st-name-input'));
        if (name) name.focus({ preventScroll: true });
      }
    });
    document.addEventListener('keydown', onKey, true);
    overlay.appendChild(modal);
    /** @type {HTMLElement | null} */ (modal.querySelector('.st-confirm__resume'))?.focus();
  }

  /**
   * Uebernimmt den gespeicherten Stand und springt in den letzten Schritt, den
   * er noch tragen kann. Ohne fertigen Charakter fehlt das Foto (wird nicht
   * gespeichert): dann geht es auf Schritt 2 mit der Bitte um neuen Upload.
   * Eine beim Neuladen laufende Generierung laesst sich ohne Status-Endpunkt
   * im Backend nicht wieder aufnehmen - auch dann Schritt 2.
   * @param {Element} overlay
   * @param {any} saved
   */
  _applyStoredFlow(overlay, saved) {
    this.childName = String(saved.childName || '');
    this.gender = String(saved.gender || '');
    this.eyeColor = String(saved.eyeColor || '');
    this.outfitChoice = saved.outfitChoice === 'photo' ? 'photo' : 'book';
    this.previewImageUrl = String(saved.previewImageUrl || '');
    this.previewDisplayUrl = '';
    this.previewUniqid = String(saved.previewUniqid || '');
    if (STARTALES_BINDING_ENABLED) {
      this.bookCoverType = saved.bookCoverType === 'hardcover' ? 'hardcover' : 'softcover';
    }
    this.dedicationEnabled = !!saved.dedicationEnabled;
    this.dedicationText = String(saved.dedicationText || '');
    this.mattCoverEnabled = !!saved.mattCoverEnabled;
    if (Number.isFinite(saved.retryCount)) this.retryCount = saved.retryCount;
    this.characterRating = saved.characterRating === 'up' || saved.characterRating === 'down' ? saved.characterRating : '';

    const order = this._stepOrder();
    let step = order.includes(saved.step) ? saved.step : 1;
    const photoLost = !this.previewImageUrl && step >= 2;
    if (!this._detailsComplete()) step = 1;
    else if (photoLost) step = 2;
    // Mit fertigem Charakter war das Foto schon bestaetigt.
    this.photoConfirmed = !!this.previewImageUrl;
    this.currentStep = step;

    this._restoreStateToOverlay(overlay);
    if (photoLost && step === 2) {
      this._showUploadError(overlay, saved.generating
        ? 'Die Erstellung wurde unterbrochen. Lade dein Foto bitte noch einmal hoch - wir speichern es aus Datenschutzgründen nicht auf deinem Gerät.'
        : 'Lade dein Foto bitte noch einmal hoch - wir speichern es aus Datenschutzgründen nicht auf deinem Gerät.');
      // "nearest" laesst den Hinweis halb unter der Fussleiste stehen.
      this._scrollInOverlay(overlay.querySelector('.st-screen--active .st-upload-cta'), 'center');
    }
    this._persistFlow();
  }

  /**
   * Merkt sich diese Instanz als Halterin des angefangenen Durchlaufs und
   * beschriftet die Trigger-Buttons um. Wird aufgerufen, wenn der Kunde den
   * Flow schliesst, ohne ihn abzuschliessen.
   */
  _markFlowInProgress() {
    if (!this._hasProgress()) {
      this._clearFlowProgress();
      return;
    }
    _stFlowOwner = this;
    _stSyncTriggerLabels(true);
  }

  /**
   * Gegenstueck zu _markFlowInProgress: kein offener Durchlauf mehr, die
   * Buttons tragen wieder ihren Liquid-Text.
   */
  _clearFlowProgress() {
    if (_stFlowOwner === this) _stFlowOwner = null;
    if (!_stFlowOwner) _stSyncTriggerLabels(false);
  }

  /**
   * Close request coming from the X button or Escape. Pristine step 1 closes
   * straight away; anything further asks for confirmation first.
   * @param {Element} overlay
   * @param {() => void} [onClosed] Run when the overlay actually closes.
   */
  _requestClose(overlay, onClosed) {
    if (!this._hasProgress()) {
      this.hideOverlay(overlay);
      if (onClosed) onClosed();
      return;
    }
    this._openAbortConfirm(overlay, onClosed);
  }

  /**
   * "Personalisierung schliessen?" — blurred, dimmed backdrop over the flow.
   * Appended inside the overlay so it inherits the --st-* design tokens and
   * so the blur applies to the personalization screens behind it.
   * Der Zwischenstand geht dabei NICHT verloren: er bleibt im Speicher der
   * Instanz und wird beim naechsten Oeffnen wiederhergestellt. Erst ein
   * Neuladen der Seite startet wieder bei Schritt 1.
   * @param {Element} overlay
   * @param {() => void} [onClosed] Run when the customer confirms the close.
   */
  _openAbortConfirm(overlay, onClosed) {
    if (overlay.querySelector('.st-confirm')) return;

    // Ab "Buch veredeln" ist das Buch komplett: statt "Später weitermachen"
    // landet es direkt im Warenkorb - dort geht es auch nach einem Neuladen
    // oder auf einem anderen Tab nicht verloren.
    const canSaveToCart = this.currentStep === 6 || this.currentStep === 7;
    const modal = document.createElement('div');
    modal.className = 'st-confirm';
    modal.innerHTML = canSaveToCart ? `
      <div class="st-confirm__box" role="alertdialog" aria-modal="true" aria-label="Personalisierung schließen">
        <div class="st-confirm__title">Möchtest du wirklich schließen?</div>
        <div class="st-confirm__text">Leg dein Buch in den Warenkorb - so geht nichts verloren und du bestellst, wann du möchtest.</div>
        <p class="st-confirm__error" role="alert" hidden></p>
        <button type="button" class="st-btn st-btn--orange st-confirm__keep">Weiter personalisieren</button>
        <button type="button" class="st-confirm__save">${ICON_CART}<span>Im Warenkorb speichern</span></button>
      </div>
    ` : `
      <div class="st-confirm__box" role="alertdialog" aria-modal="true" aria-label="Personalisierung schließen">
        <div class="st-confirm__title">Möchtest du wirklich schließen?</div>
        <div class="st-confirm__text">${this.previewImageUrl
          ? 'Dein Zwischenstand wird auf diesem Gerät gespeichert.<br />Du kannst jederzeit genau hier weitermachen.'
          : 'Dein Zwischenstand wird auf diesem Gerät gespeichert.<br />Nach einem Neuladen lädst du nur dein Foto kurz neu hoch.'}</div>
        <button type="button" class="st-btn st-btn--orange st-confirm__keep">Weiter personalisieren</button>
        <button type="button" class="st-confirm__abort">Später weitermachen</button>
      </div>
    `;

    const dismiss = () => {
      modal.remove();
      document.removeEventListener('keydown', onKey);
    };
    /** @param {KeyboardEvent} e */
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };

    modal.addEventListener('click', (e) => {
      const target = /** @type {Element} */ (e.target);
      // Backdrop tap and "Weiter personalisieren" both mean: stay in the flow.
      if (e.target === modal || target.closest('.st-confirm__keep')) {
        dismiss();
        return;
      }
      if (target.closest('.st-confirm__abort')) {
        dismiss();
        // Kein resetState: der Fortschritt bleibt stehen. Beide Trigger-Buttons
        // heissen ab jetzt "Bestellung abschliessen" und oeffnen den Flow an
        // genau dieser Stelle wieder.
        this.hideOverlay(overlay);
        this._markFlowInProgress();
        if (onClosed) onClosed();
        return;
      }
      const saveBtn = /** @type {HTMLButtonElement | null} */ (target.closest('.st-confirm__save'));
      if (saveBtn) this._saveToCartAndClose(overlay, modal, saveBtn, dismiss, onClosed);
    });

    document.addEventListener('keydown', onKey);
    overlay.appendChild(modal);

    _stTrack('abort_prompted', {
      step: String(this.currentStep),
      product_id: this.getAttribute('data-product-id') || undefined,
    });

    const keep = /** @type {HTMLButtonElement | null} */ (modal.querySelector('.st-confirm__keep'));
    if (keep) keep.focus();
  }

  /**
   * "Im Warenkorb speichern" im Schliessen-Dialog (Schritt 6/7). Derselbe Weg
   * wie "Sicher zur Kasse" (addToCart mit allen Properties und Add-ons), nur
   * ohne Weiterleitung: Overlay zu, Bestaetigung als Toast.
   * @param {Element} overlay
   * @param {HTMLElement} modal
   * @param {HTMLButtonElement} saveBtn
   * @param {() => void} dismiss
   * @param {(() => void) | undefined} onClosed
   */
  async _saveToCartAndClose(overlay, modal, saveBtn, dismiss, onClosed) {
    // Liegt die Bestellung schon im Warenkorb, wuerde ein zweiter Aufruf ein
    // zweites Buch anlegen - dann nur schliessen und bestaetigen.
    if (this._cartSubmitted) {
      dismiss();
      this.hideOverlay(overlay);
      this._clearFlowProgress();
      this._showCartToast();
      if (onClosed) onClosed();
      return;
    }
    // Eine angewaehlte, leere Widmung fiele beim Hinzufuegen stillschweigend
    // weg. Wie beim "Weiter" in Schritt 6: zurueck ins Feld statt speichern.
    if (this.dedicationEnabled && !this.dedicationText) {
      dismiss();
      this._dedicationErrorShown = true;
      this._updateDedicationField(overlay);
      const field = /** @type {HTMLTextAreaElement | null} */ (overlay.querySelector('.st-dedication__input'));
      if (field && this.currentStep === 6) {
        this._scrollInOverlay(field, 'center');
        field.focus({ preventScroll: true });
      }
      return;
    }

    const errorEl = /** @type {HTMLElement | null} */ (modal.querySelector('.st-confirm__error'));
    if (errorEl) errorEl.hidden = true;
    _stTrack('save_to_cart_clicked', {
      step: String(this.currentStep),
      product_id: this.getAttribute('data-product-id') || undefined,
    });

    const ok = await this.addToCart({ redirect: false, closeOverlay: false, openDrawer: false, container: overlay, triggerBtn: saveBtn });
    if (ok === true) {
      dismiss();
      this.hideOverlay(overlay);
      this._clearFlowProgress();
      this._showCartToast();
      if (onClosed) onClosed();
      return;
    }
    // addToCart schreibt seinen Fehler in die Fehlerbox von Schritt 7. Steht der
    // Kunde gerade auf Schritt 6, waere sie unsichtbar - deshalb hier im Dialog.
    const box = overlay.querySelector('.st-screen--review .st-cart-error');
    const message = (box && box.querySelector('.st-cart-error__text')?.textContent) || 'Das hat leider nicht geklappt. Bitte versuche es noch einmal.';
    if (box) /** @type {HTMLElement} */ (box).hidden = true;
    if (errorEl && modal.isConnected) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  /**
   * Kurze Bestaetigung nach "Im Warenkorb speichern". Eine einzige Live-Region,
   * ein erneuter Aufruf ersetzt den alten Toast.
   */
  _showCartToast() {
    document.querySelector('.st-toast')?.remove();
    const toast = document.createElement('div');
    toast.className = 'st-toast';
    toast.setAttribute('role', 'status');
    toast.innerHTML = `
      <span class="st-toast__icon" aria-hidden="true">${ICON_CART}</span>
      <span class="st-toast__text">Gespeichert! Dein Buch liegt im Warenkorb.</span>
      <a class="st-toast__link" href="/cart">Ansehen</a>
    `;
    const link = toast.querySelector('.st-toast__link');
    const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (link && cartDrawer && typeof cartDrawer.open === 'function') {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        toast.remove();
        cartDrawer.open();
      });
    }
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('st-toast--visible'));
    setTimeout(() => {
      toast.classList.remove('st-toast--visible');
      setTimeout(() => toast.remove(), 300);
    }, 6000);
  }

  /**
   * Bewertung der fertigen Vorschau. Zwei Wege gleichzeitig, weil sie
   * unterschiedliche Fragen beantworten: `character_rated` geht ueber den
   * startales:track-Bus an die Analytics (wie oft sitzt die Aehnlichkeit?),
   * der Feedback-Endpoint bekommt dieselbe Bewertung am uniqid haengend
   * (welches Bild war es konkret?). Daumen runter oeffnet direkt die
   * Textabfrage - das ist der Moment, in dem der Kunde sagen will, was fehlt.
   * @param {'up' | 'down'} rating
   * @param {Element} overlay
   */
  _submitCharacterRating(rating, overlay) {
    const rate = overlay.querySelector('.st-screen--character .st-rate');
    if (!rate || rate.getAttribute('data-answered') === 'true') return;

    this.characterRating = rating;
    rate.setAttribute('data-answered', 'true');
    rate.setAttribute('data-rating', rating);

    const thanks = rate.querySelector('.st-rate__thanks');
    if (thanks) {
      thanks.textContent = rating === 'up'
        ? 'Danke! Weiter geht es zum Buch.'
        : 'Danke - sag uns kurz, was nicht passt.';
    }

    _stTrack('character_rated', {
      step: 3,
      product_id: this.getAttribute('data-product-id') || undefined,
      rating,
      // Erster Versuch oder nachgeneriert - ohne das laesst sich nicht trennen,
      // ob der zweite Anlauf ueberhaupt etwas verbessert.
      attempt: this.retryCount,
    });

    this._submitCharacterFeedback({ rating });

    if (rating === 'down') this._openFeedbackModal(overlay);
  }

  /**
   * Modal nach Daumen runter: Gruende zum Antippen (Mehrfachauswahl), damit
   * auch Kunden, die nichts tippen wollen, in zwei Taps sagen koennen, was
   * nicht passt. Freitext nur ueber "Etwas anderes". Geht an die Feedback-API
   * zur aktuellen Generierung (uniqid) und schliesst sich danach.
   * Haengt im Overlay, damit es die --st-* Tokens erbt.
   * @param {Element} [overlay]
   */
  _openFeedbackModal(overlay) {
    const existing = document.querySelector('.st-feedback-modal');
    if (existing) existing.remove();

    const opener = /** @type {HTMLElement | null} */ (document.activeElement);
    const modal = document.createElement('div');
    modal.className = 'st-feedback-modal';
    modal.innerHTML = `
      <div class="st-feedback-modal__box" role="dialog" aria-modal="true" aria-labelledby="st-feedback-title" aria-describedby="st-feedback-text">
        <button type="button" class="st-feedback-modal__close" aria-label="Schließen">${ICON_CLOSE}</button>
        <div class="st-feedback-modal__title" id="st-feedback-title">Was passt noch nicht?</div>
        <div class="st-feedback-modal__text" id="st-feedback-text">Tipp an, was nicht stimmt. Mehrere Antworten sind möglich.</div>
        <div class="st-feedback-modal__reasons" role="group" aria-labelledby="st-feedback-title">
          ${CHARACTER_FEEDBACK_REASONS.map(r => `<button type="button" class="st-feedback-modal__reason" data-reason="${r.id}" aria-pressed="false">${r.label}</button>`).join('')}
        </div>
        <div class="st-feedback-modal__other" hidden>
          <label class="st-feedback-modal__label" for="st-feedback-input">Was genau?</label>
          <textarea class="st-feedback-modal__input" id="st-feedback-input" rows="3" maxlength="1000" placeholder="Beschreib kurz, was dir auffällt …"></textarea>
        </div>
        <button type="button" class="st-btn st-btn--primary st-feedback-modal__submit" disabled>Absenden</button>
      </div>
    `;

    const close = () => {
      modal.remove();
      document.removeEventListener('keydown', onKey);
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    };
    /** @param {KeyboardEvent} e */
    const onKey = (e) => { if (e.key === 'Escape') close(); };

    const otherBox = /** @type {HTMLElement | null} */ (modal.querySelector('.st-feedback-modal__other'));
    const input = /** @type {HTMLTextAreaElement | null} */ (modal.querySelector('.st-feedback-modal__input'));
    const submit = /** @type {HTMLButtonElement | null} */ (modal.querySelector('.st-feedback-modal__submit'));
    /** @returns {string[]} */
    const selectedReasons = () => Array.from(modal.querySelectorAll('.st-feedback-modal__reason[aria-pressed="true"]'))
      .map(btn => btn.getAttribute('data-reason') || '')
      .filter(Boolean);

    modal.addEventListener('click', (e) => {
      const target = /** @type {Element} */ (e.target);
      if (e.target === modal || target.closest('.st-feedback-modal__close')) {
        close();
        return;
      }
      const reason = target.closest('.st-feedback-modal__reason');
      if (!reason) return;
      const pressed = reason.getAttribute('aria-pressed') !== 'true';
      reason.setAttribute('aria-pressed', String(pressed));
      if (reason.getAttribute('data-reason') === 'other' && otherBox) {
        otherBox.hidden = !pressed;
        if (pressed && input) input.focus();
      }
      if (submit) submit.disabled = selectedReasons().length === 0;
    });

    if (submit) {
      submit.addEventListener('click', () => {
        const reasons = selectedReasons();
        if (!reasons.length) return;
        // Freitext zaehlt nur, solange "Etwas anderes" angewaehlt ist - wer es
        // wieder abwaehlt, hat den Text verworfen.
        const message = reasons.includes('other') && input ? input.value.trim() : '';
        this._submitCharacterFeedback({ reasons, message });
        const thanks = overlay && overlay.querySelector('.st-screen--character .st-rate__thanks');
        if (thanks) thanks.textContent = 'Danke für dein Feedback!';
        close();
      });
    }

    document.addEventListener('keydown', onKey);
    (overlay || document.body).appendChild(modal);
    const firstReason = /** @type {HTMLButtonElement | null} */ (modal.querySelector('.st-feedback-modal__reason'));
    if (firstReason) firstReason.focus({ preventScroll: true });
  }

  /**
   * Feedback zur Charakter-Vorschau an die eigene API. Zwei Sorten laufen ueber
   * denselben Endpoint, unterschieden durch die gesetzten Felder:
   *   - Bewertung: `rating` gesetzt, sonst nichts (Daumen hoch/runter)
   *   - Feedback:  `reasons` (IDs aus CHARACTER_FEEDBACK_REASONS) plus optional
   *                `message`; `rating` traegt die zuvor abgegebene Bewertung
   *                mit, damit sich beides am uniqid zusammenfuehren laesst
   * `uniqid` ist der Schluessel zur Generierung, `attempt` sagt, ob es der erste
   * Anlauf war oder ein nachgenerierter - ohne das ist nicht zu sehen, ob ein
   * zweiter Versuch die Aehnlichkeit ueberhaupt verbessert.
   *
   * @param {{ message?: string, rating?: string, reasons?: string[] }} [payload]
   */
  _submitCharacterFeedback(payload = {}) {
    const uniqid = this.previewUniqid;
    const message = payload.message || '';
    const rating = payload.rating || this.characterRating || '';
    const reasons = payload.reasons || [];

    if (CustomAlertButton.devMode) {
      console.log('⚙️ Dev mode: skipping character feedback API call', { uniqid, rating, reasons, message });
      return;
    }

    fetch(`${STARTALES_API_BASE}/api/v1/preview/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shop': _stGetShop(),
        'X-Session-Id': _stGetSessionId(),
      },
      keepalive: true,
      body: JSON.stringify({
        uniqid,
        rating: rating || undefined,
        reasons: reasons.length ? reasons : undefined,
        message: message || undefined,
        attempt: this.retryCount,
        name: this.childName || undefined,
        gender: this._mapGenderForApi(this.gender) || undefined,
        image_url: this.previewImageUrl || undefined,
      }),
    }).catch(() => {});
  }

  /**
   * Volltext der Einwilligung. Das Checkbox-Label traegt nur die Kurzfassung,
   * die einzelnen Zusicherungen stehen hier - eine Antippdistanz entfernt und
   * ohne dass der Kunde den Flow verlaesst. Wer weiterliest, kommt von hier
   * direkt in die Datenschutzerklaerung.
   * @param {string} policyUrl
   */
  _openConsentDetailsLightbox(policyUrl) {
    const body = `
      <p>Mit dem Häkchen bestätigst du:</p>
      <ul>
        <li>Du bist mindestens 18 Jahre alt.</li>
        <li>Du bist berechtigt, das hochgeladene Foto zu verwenden.</li>
        <li>Zeigt das Foto ein Kind, bist du erziehungsberechtigt oder hast die Einwilligung der erziehungsberechtigten Person.</li>
        <li>Du hast die Datenschutzerklärung gelesen.</li>
      </ul>
      <p>Dein Foto wird verschlüsselt übertragen, ausschließlich zur Erstellung deines Buches verwendet und danach automatisch gelöscht.</p>
      <p><a href="${policyUrl}" class="st-consent-details__policy">Zur Datenschutzerklärung</a></p>
    `;

    const lightbox = this._openHtmlLightbox('Deine Einwilligung', body);

    // Aus dem Volltext weiter in die Shopify-Policy: ersetzt die Lightbox,
    // damit nicht zwei Overlays uebereinander liegen.
    const policyLink = lightbox.querySelector('.st-consent-details__policy');
    if (policyLink) {
      policyLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._openPolicyLightbox(policyUrl, 'Datenschutz');
      });
    }
  }

  /**
   * Lightbox that renders inline HTML (e.g. a Shopify policy body) - same
   * chrome as the image lightbox. Falls back to opening the URL in a new tab
   * if no inline content is available (Shopify policy pages refuse to be
   * iframed because of X-Frame-Options).
   * @param {string} url
   * @param {string} [title]
   */
  _openPolicyLightbox(url, title) {
    const tpl = /** @type {HTMLTemplateElement | null} */ (document.getElementById('st-privacy-policy-content'));
    const html = tpl ? tpl.innerHTML.trim() : '';

    if (!html) {
      window.open(url, '_blank', 'noopener');
      return;
    }

    this._openHtmlLightbox(title, html);
  }

  /**
   * Gemeinsame Lightbox-Huelle fuer Fliesstext (Policy, Einwilligungs-Volltext).
   * @param {string | undefined} title
   * @param {string} html
   * @returns {HTMLElement} die eingehaengte Lightbox
   */
  _openHtmlLightbox(title, html) {
    const existing = document.querySelector('.st-lightbox');
    if (existing) existing.remove();

    const escape = (/** @type {string} */ s) => s.replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c
    ));

    const lightbox = document.createElement('div');
    lightbox.className = 'st-lightbox st-lightbox--policy';
    lightbox.innerHTML = `
      <button type="button" class="st-lightbox__close" aria-label="Schließen">${ICON_CLOSE}</button>
      <div class="st-lightbox__inner st-lightbox__inner--policy">
        ${title ? `<div class="st-lightbox__policy-header">${escape(title)}</div>` : ''}
        <div class="st-lightbox__policy-body rte">${html}</div>
      </div>
    `;

    const close = () => {
      lightbox.remove();
      document.removeEventListener('keydown', onKey);
    };
    /** @param {KeyboardEvent} e */
    const onKey = (e) => { if (e.key === 'Escape') close(); };

    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox || /** @type {Element} */ (e.target).closest('.st-lightbox__close')) {
        close();
      }
    });
    document.addEventListener('keydown', onKey);

    document.body.appendChild(lightbox);
    return lightbox;
  }

  // ---------------------------------------------------------------------------
  // Summary (step 5)
  // ---------------------------------------------------------------------------

  _updateSummary(overlay) {
    const screen = overlay.querySelector('.st-screen--review');
    if (!screen) return;

    const productTitle = this._getCurrentProductName();

    const characterImg = /** @type {HTMLImageElement | null} */ (screen.querySelector('.st-summary__cover-img--character'));
    if (characterImg) {
      characterImg.src = this._previewSrc() || '';
      characterImg.alt = productTitle;
      characterImg.style.display = this.previewImageUrl ? '' : 'none';
    }

    const titleEl = screen.querySelector('.st-summary__title');
    if (titleEl) titleEl.textContent = productTitle;

    const nameEl = screen.querySelector('.st-summary__name');
    if (nameEl) nameEl.textContent = this.childName || '-';

    const genderEl = screen.querySelector('.st-summary__gender');
    if (genderEl) genderEl.textContent = this.gender || '-';

    const eyeEl = screen.querySelector('.st-summary__eye');
    if (eyeEl) eyeEl.textContent = this.eyeColor || '-';

    // Mirrors the price fallback below, so label and price can never disagree.
    const bindingDisplay = this.bookCoverType === 'hardcover' ? 'Hardcover' : 'Softcover';
    const bindingEl = screen.querySelector('.st-summary__binding');
    if (bindingEl) bindingEl.textContent = bindingDisplay;
    const bindingSub = screen.querySelector('.st-summary__binding-sub');
    if (bindingSub) bindingSub.textContent = bindingDisplay;

    const productLabel = screen.querySelector('.st-summary__product-label');
    if (productLabel) productLabel.textContent = productTitle;

    const bookPrice = this.bookCoverType === 'hardcover' ? (this.hardcoverPrice || 39.99) : (this.softcoverPrice || 29.99);
    const bookCompare = this.bookCoverType === 'hardcover' ? (this.hardcoverComparePrice || 0) : (this.softcoverComparePrice || 0);
    const bookPriceEl = screen.querySelector('.st-summary__book-price');
    const bookCompareEl = /** @type {HTMLElement | null} */ (screen.querySelector('.st-summary__book-compare'));
    if (bookPriceEl) bookPriceEl.textContent = this._fmt(bookPrice);
    if (bookCompareEl) {
      if (bookCompare > bookPrice) {
        bookCompareEl.textContent = this._fmt(bookCompare);
        bookCompareEl.style.display = '';
      } else {
        bookCompareEl.style.display = 'none';
      }
    }

    const outfitEl = screen.querySelector('.st-summary__outfit');
    if (outfitEl) outfitEl.textContent = this._outfitIsPhoto() ? 'Von deinem Foto' : 'Buch-Outfit';
    const outfitLine = /** @type {HTMLElement | null} */ (screen.querySelector('.st-summary__outfit-line'));
    if (outfitLine) outfitLine.style.display = this._outfitIsPhoto() ? '' : 'none';

    // Extras picked on step 6 show up as their own lines here.
    const dedicationLine = /** @type {HTMLElement | null} */ (screen.querySelector('.st-summary__dedication-line'));
    if (dedicationLine) dedicationLine.style.display = this.dedicationEnabled ? '' : 'none';
    const mattLine = /** @type {HTMLElement | null} */ (screen.querySelector('.st-summary__matt-line'));
    if (mattLine) mattLine.style.display = this.mattCoverEnabled ? '' : 'none';

    // Zweitbuch-Rabatt: zieht Shopify an der Kasse automatisch ab; hier steht
    // derselbe Betrag, damit die Summe nicht erst an der Kasse sinkt.
    const nextDiscount = this._nextBookDiscount(bookPrice);
    const nextLine = /** @type {HTMLElement | null} */ (screen.querySelector('.st-summary__next-line'));
    if (nextLine) nextLine.style.display = nextDiscount > 0 ? '' : 'none';
    const nextPriceEl = screen.querySelector('.st-summary__next-price');
    if (nextPriceEl) nextPriceEl.textContent = `-${this._fmt(nextDiscount)}`;
    const nextSubEl = screen.querySelector('.st-summary__next-sub');
    const bookIndex = (this._cartBooks || []).length + 1;
    if (nextSubEl) {
      nextSubEl.textContent = bookIndex === 3
        ? 'Wird an der Kasse abgezogen'
        : `${this._bookPctAt(bookIndex)} %, wird an der Kasse abgezogen`;
    }
    const nextLabelEl = screen.querySelector('.st-summary__next-line .st-line__label');
    if (nextLabelEl) nextLabelEl.textContent = `Rabatt ${bookIndex}. Buch`;

    // Total + savings
    let total = bookPrice - nextDiscount;
    if (this._outfitIsPhoto()) total += this.outfitPrice || OUTFIT_PHOTO_PRICE_FALLBACK;
    if (this.dedicationEnabled) total += (this.dedicationPrice || 3.99);
    if (this.mattCoverEnabled) total += (this.mattPrice || 1.99);
    const savings = (bookCompare > bookPrice ? bookCompare - bookPrice : 0) + nextDiscount;
    const totalEl = screen.querySelector('.st-total__amount');
    if (totalEl) totalEl.textContent = this._fmt(total);

    const saveEl = /** @type {HTMLElement | null} */ (screen.querySelector('.st-total__save'));
    if (saveEl) {
      if (savings > 0) {
        saveEl.textContent = `Du sparst ${this._fmt(savings)}`;
        saveEl.style.display = '';
      } else {
        saveEl.style.display = 'none';
      }
    }
  }

  _fmt(n) {
    return `€${n.toFixed(2).replace('.', ',')}`;
  }

  // ---------------------------------------------------------------------------
  // Cart integration
  // ---------------------------------------------------------------------------

  async _selectVariantByBookType(productForm, productUrl) {
    if (!this.bookCoverType) return;
    try {
      const response = await fetch(`${productUrl}.js`);
      if (!response.ok) return;
      const product = await response.json();
      const variants = product.variants || [];
      const target = variants.find(v => {
        const title = (v.title || '').toLowerCase();
        const options = (v.options || []).map(o => o.toLowerCase());
        return [title, ...options].join(' ').includes(this.bookCoverType);
      });
      if (target) {
        const input = /** @type {HTMLInputElement | null} */ (productForm.querySelector('input[name="id"]'));
        if (input) input.value = target.id.toString();
      }
    } catch (err) {
      console.warn('Error selecting variant by book type:', err);
    }
  }

  /**
   * @param {{ redirect?: boolean, closeOverlay?: boolean, openDrawer?: boolean, container?: HTMLElement | null, triggerBtn?: HTMLButtonElement | null }} [opts]
   * @returns {Promise<boolean>} true, wenn ohne Weiterleitung alles im Warenkorb liegt.
   */
  async addToCart({ redirect = true, closeOverlay = false, openDrawer = true, container = null, triggerBtn = null } = {}) {
    // Guard against double-submit / button spamming while the request is in flight.
    if (this._submittingOrder) return false;
    this._submittingOrder = true;

    const btn = /** @type {HTMLButtonElement | null} */ (triggerBtn);
    const _originalBtnHtml = btn ? btn.innerHTML : '';
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      btn.innerHTML = `<span class="st-btn__spinner"></span>${redirect ? 'Wird geladen…' : 'Wird hinzugefügt…'}`;
    }
    // Only reset the button if we did NOT navigate away (on redirect the page
    // unloads, so keeping the spinner visible until then is intentional).
    let didRedirect = false;
    const restoreBtn = () => {
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.innerHTML = _originalBtnHtml;
      }
    };
    // Merken, damit ein bfcache-Rueckkehrer den Spinner nicht ewig sieht
    // (siehe _releaseSubmitGuard).
    this._restoreSubmitBtn = restoreBtn;
    this._hideCartError(container);

    try {
      const productFormComponent = /** @type {any} */ (document.querySelector('product-form-component'));
      const productForm = /** @type {HTMLFormElement | null} */ (productFormComponent?.querySelector('form'));

      if (!productForm) {
        console.error('No product form found');
        this._showCartError(
          container,
          'Der Kauf konnte nicht gestartet werden. Bitte lade die Seite neu und versuche es noch einmal.'
        );
        return;
      }

      const productUrl = productFormComponent?.dataset?.productUrl || '';
      await this._selectVariantByBookType(productForm, productUrl);

      productForm.querySelectorAll('input[name^="properties["]').forEach(input => {
        if (input.getAttribute('data-custom-overlay') === 'true') input.remove();
      });

      const addHidden = (name, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.setAttribute('data-custom-overlay', 'true');
        productForm.appendChild(input);
      };

      // Ein Retry nach einem fehlgeschlagenen Add-on-Call muss dieselbe _BuchID
      // senden wie die bereits abgeschickte Buch-Zeile - sonst findet die
      // Produktion das Paar nicht mehr zusammen.
      if (!this._bookLineAdded || !this._pairId) {
        this._pairId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      }
      const pairId = this._pairId;

      if (this.childName) addHidden('properties[Name]', this.childName);
      if (this.bookCoverType) {
        addHidden('properties[_Buchtyp]', this.bookCoverType === 'hardcover' ? 'Hardcover' : 'Softcover');
      }
      if (this.gender) addHidden('properties[_Geschlecht]', this.gender);
      if (this.eyeColor) addHidden('properties[_Augenfarbe]', this.eyeColor);
      // Eindeutige Buch-Zuordnung fuer die Produktion; das Backend liest
      // "book_slug" (fuehrende Unterstriche ignoriert) vor Titel/SKU-Matching.
      const bookSlug = this._bookSlug();
      if (bookSlug) addHidden('properties[_book_slug]', bookSlug);
      // Nur echte, remote erreichbare URLs. `uploadedImageUrl` ist eine
      // blob:-URL aus URL.createObjectURL - die lebt nur in diesem Tab und waere
      // in der Bestellung fuer die Produktion wertlos.
      const orderImageUrl = this.previewImageUrl || this.uploadedImageUrl;
      if (orderImageUrl && /^https?:\/\//i.test(orderImageUrl)) {
        addHidden('properties[_Bild]', orderImageUrl);
      } else if (orderImageUrl) {
        console.warn('Skipping non-remote preview URL for line item property:', orderImageUrl);
      }
      // _BuchID pairs the book with its separate add-on lines (dedication + matt cover).
      // Attach it whenever any add-on is present so each add-on can be matched back
      // to the correct book. The matt finish is modelled exactly like the dedication:
      // its own line item carrying this same _BuchID — no property on the book line.
      // Outfit der Vorschau steht immer an der Buch-Zeile, auch ohne Add-on:
      // die Produktion muss wissen, welche Kleidung gedruckt wird.
      if (STARTALES_OUTFIT_ENABLED) {
        addHidden('properties[_Outfit]', this._outfitIsPhoto() ? 'Foto' : 'Buch');
      }
      // Eigenes Outfit = eigenes Add-on-Produkt (Handle outfit-vom-foto), wie
      // Widmung und Matt-Cover ueber _BuchID an das Buch gekoppelt.
      const outfitVariantId = parseInt(this.getAttribute('data-outfit-variant-id') || '', 10) || 0;
      const hasOutfitAddon = this._outfitIsPhoto() && !!outfitVariantId;
      if (this._outfitIsPhoto() && !outfitVariantId) {
        console.warn('Outfit add-on product "outfit-vom-foto" missing - photo outfit is not charged.');
      }
      const hasDedication = this.dedicationEnabled && this.dedicationText;
      // Werden die Extras bewusst ausgelassen, braucht die Buch-Zeile auch keine
      // _BuchID - sie wuerde auf nichts zeigen.
      const hasAddon = !this._skipAddons && (hasDedication || this.mattCoverEnabled || hasOutfitAddon);
      if (hasAddon) {
        addHidden('properties[_BuchID]', pairId);
      }

      const variantIdInput = /** @type {HTMLInputElement | null} */ (productForm.querySelector('input[name="id"]'));
      const variantId = variantIdInput?.value;
      if (!variantId) {
        console.error('No variant id in product form');
        this._showCartError(
          container,
          'Die gewählte Ausführung ist gerade nicht verfügbar. Bitte lade die Seite neu und versuche es noch einmal.'
        );
        return;
      }

      const properties = {};
      productForm.querySelectorAll('input[data-custom-overlay="true"]').forEach(el => {
        const input = /** @type {HTMLInputElement} */ (el);
        properties[input.name.replace('properties[', '').replace(']', '')] = input.value;
      });

      const bookItem = { id: parseInt(variantId), quantity: 1, properties };

      // Matt & Kratzfest cover — separate add-on line, paired to its book via _BuchID.
      // The product's name/title/SKU contains both "matt" and "kratz"/"scratch" so
      // fulfillment can match it; absence of this line means a glossy finish.
      const MATT_COVER_VARIANT_ID = 58081680195909;
      const DEDICATION_VARIANT_ID = 57036415009093;

      const addonItems = [];
      if (!this._skipAddons) {
        if (hasDedication) {
          addonItems.push({
            id: DEDICATION_VARIANT_ID,
            quantity: 1,
            properties: { 'Widmungs Text': this.dedicationText, '_BuchID': pairId },
          });
        }
        if (this.mattCoverEnabled && MATT_COVER_VARIANT_ID) {
          addonItems.push({
            id: MATT_COVER_VARIANT_ID,
            quantity: 1,
            properties: { '_BuchID': pairId },
          });
        }
        if (hasOutfitAddon) {
          addonItems.push({
            id: outfitVariantId,
            quantity: 1,
            properties: { '_BuchID': pairId },
          });
        }
      }

      try {
        // Buch und Extras gehen in ZWEI Requests. /cart/add.js ist atomar: lag
        // eine der fest verdrahteten Add-on-Varianten nicht kaufbar vor
        // (ausverkauft, aus dem Verkaufskanal genommen, ersetzt), riss sie vorher
        // den kompletten Kauf mit - inklusive Buch, obwohl daran nichts fehlte.
        if (!this._bookLineAdded) {
          const bookResult = await this._cartAdd([bookItem]);
          if (!bookResult.ok) {
            console.error('Failed to add book to cart:', bookResult.detail);
            // Ohne dieses Event ist ein gescheiterter Kauf in keiner Statistik
            // sichtbar - genau das hat die Diagnose vorher so schwer gemacht.
            _stTrack('cart_add_failed', {
              step: '7',
              product_id: this.getAttribute('data-product-id') || undefined,
              meta: { status: bookResult.status, variant_id: variantId },
            });
            this._showCartError(
              container,
              bookResult.message || 'Dein Buch konnte nicht in den Warenkorb gelegt werden. Bitte versuche es noch einmal.'
            );
            return;
          }
          this._bookLineAdded = true;
        }

        // Die Extras separat. Scheitern sie, liegt das Buch trotzdem im Warenkorb -
        // der Kunde entscheidet dann selbst, ob er es erneut versucht oder ohne
        // Extras zur Kasse geht. Stillschweigend weiterleiten waere falsch: die
        // Widmung existiert ausschliesslich auf dieser Zeile.
        if (addonItems.length > 0) {
          const addonResult = await this._cartAdd(addonItems);
          if (!addonResult.ok) {
            console.error('Failed to add extras to cart:', addonResult.detail);
            _stTrack('addon_add_failed', {
              step: '7',
              product_id: this.getAttribute('data-product-id') || undefined,
              meta: {
                dedication: !!hasDedication,
                matt_cover: this.mattCoverEnabled,
                outfit: this.outfitChoice,
                status: addonResult.status,
              },
            });
            this._showCartError(
              container,
              'Dein Buch liegt im Warenkorb, aber die gewählten Extras konnten nicht hinzugefügt werden.',
              { allowSkip: true }
            );
            return;
          }
        }

        const bookPrice = this.bookCoverType === 'hardcover'
          ? (this.hardcoverPrice || 39.99)
          : (this.softcoverPrice || 29.99);
        const totalValue = bookPrice
          + (this.dedicationEnabled ? (this.dedicationPrice || 3.99) : 0)
          + (this.mattCoverEnabled ? (this.mattPrice || 1.99) : 0)
          + (hasOutfitAddon ? (this.outfitPrice || OUTFIT_PHOTO_PRICE_FALLBACK) : 0);
        _stTrack('step_completed', {
          step: '7',
          screen: STEP_SLUGS[7],
          product_id: this.getAttribute('data-product-id') || undefined,
        });
        _stTrack('add_to_cart', {
          step: '7',
          product_id: this.getAttribute('data-product-id') || undefined,
          variant_id: variantId,
          value: totalValue,
          meta: {
            binding: this.bookCoverType,
            dedication: this.dedicationEnabled,
            matt_cover: this.mattCoverEnabled,
            outfit: this.outfitChoice,
            gender: this.gender,
            eye_color: this.eyeColor,
          },
        });

        productForm.querySelectorAll('input[data-custom-overlay="true"]').forEach(i => i.remove());

        // Das Buch liegt im Warenkorb: ein gespeicherter Stand wuerde beim
        // naechsten Besuch sonst ein zweites, identisches Buch anbieten.
        this._flowCompleted = true;
        this._clearStoredFlow();

        if (redirect) {
          didRedirect = true;
          // Ab hier liegt die komplette Bestellung im Warenkorb: jeder weitere
          // Klick darf nur noch zur Kasse fuehren, nicht erneut hinzufuegen.
          this._cartSubmitted = true;
          window.location.href = '/checkout';
          // Sicherheitsnetz: bleibt die Navigation aus (blockiert, abgebrochen),
          // haengt der Spinner sonst endlos. Der Button wird dann zum reinen
          // Checkout-Link - ein zweites Buch entsteht dabei nicht.
          setTimeout(() => {
            if (!this._cartSubmitted) return;
            this._submittingOrder = false;
            this._restoreSubmitBtn = null;
            this._markCheckoutPending(container);
          }, 10000);
          return;
        }

        try {
          const cartResponse = await fetch('/cart.js');
          const cartData = await cartResponse.json();
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              source: 'custom-alert-button',
              data: { source: 'custom-alert-button', itemCount: cartData.item_count },
            },
          }));
        } catch (e) {
          console.warn('cart:update dispatch failed', e);
        }

        const _pid = this.getAttribute('data-product-id');
        try {
          if (_pid) {
            localStorage.removeItem(`startales_char_${_pid}`);
            localStorage.removeItem(`startales_chars_${_pid}`);
          }
        } catch {}
        this.resetState();
        this._savedChars = [];
        if (closeOverlay && container) {
          this.hideOverlay(container, true);
        }

        const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
        if (openDrawer && cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
        return true;
      } catch (err) {
        console.error('Error in cart add:', err);
        this._showCartError(container, 'Beim Hinzufügen zum Warenkorb ist etwas schiefgelaufen.');
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      this._showCartError(container, 'Beim Hinzufügen zum Warenkorb ist etwas schiefgelaufen. Bitte versuche es noch einmal.');
    } finally {
      // Keep the spinner up while the browser navigates to checkout; otherwise
      // re-enable the button so the user can retry.
      if (!didRedirect) {
        this._restoreSubmitBtn = null;
        restoreBtn();
        this._submittingOrder = false;
      }
    }
  }

  /**
   * Ein einzelner /cart/add.js-Aufruf. Wirft nie, sondern meldet das Ergebnis
   * zurueck - inklusive der Shopify-Fehlerbeschreibung, sofern es eine gibt.
   * @param {Array<Record<string, any>>} items
   * @returns {Promise<{ ok: boolean, status: number, message: string, detail: string }>}
   */
  async _cartAdd(items) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (response.ok) return { ok: true, status: response.status, message: '', detail: '' };

      const detail = await response.text();
      let message = '';
      try {
        const parsed = JSON.parse(detail);
        // Shopify liefert die kundentaugliche Meldung als `description`
        // ("... ist nicht mehr verfuegbar"), `message` ist der technische Titel.
        message = parsed?.description || parsed?.message || '';
      } catch { /* kein JSON - dann bleibt die generische Meldung */ }
      return { ok: false, status: response.status, message, detail };
    } catch (err) {
      // Netzwerkfehler (offline, abgebrochene Verbindung).
      return {
        ok: false,
        status: 0,
        message: 'Keine Verbindung zum Shop. Bitte prüfe deine Internetverbindung und versuche es noch einmal.',
        detail: err instanceof Error ? err.message : String(err),
      };
    }
  }

  // ---------------------------------------------------------------------------
  // Product page helpers
  // ---------------------------------------------------------------------------

  _getCurrentProductName() {
    try {
      const analyticsTitle = /** @type {any} */ (window).ShopifyAnalytics?.meta?.product?.title;
      if (analyticsTitle && analyticsTitle.trim()) return analyticsTitle.trim();

      const attrTitle = this.getAttribute('data-product-title');
      if (attrTitle && attrTitle.trim()) return attrTitle.trim();

      const selectors = [
        '.product__title',
        'h1.title',
        '.product-single__title',
        '.product__info-container h1',
        'h1[itemprop="name"]',
        'h1.product_name',
      ];
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (el && el.textContent?.trim()) return el.textContent.trim();
      }
      return document.title.split('|')[0].split('–')[0].trim() || 'Personalisiertes Buch';
    } catch {
      return 'Personalisiertes Buch';
    }
  }

  // ---------------------------------------------------------------------------
  // State restoration (when reopening modal with in-progress state)
  // ---------------------------------------------------------------------------

  _restoreStateToOverlay(overlay) {
    // Eye color
    if (this.eyeColor) {
      overlay.querySelectorAll('.st-eye-option').forEach(opt => {
        const match = opt.getAttribute('data-color-label') === this.eyeColor;
        opt.setAttribute('data-selected', match ? 'true' : 'false');
      });
    }

    // Consent
    const consent = overlay.querySelector('.st-consent');
    if (consent) {
      consent.setAttribute('data-checked', this.photoConfirmed ? 'true' : 'false');
      const input = /** @type {HTMLInputElement | null} */ (consent.querySelector('.st-consent__input'));
      if (input) input.checked = this.photoConfirmed;
    }

    // Name
    const nameInput = /** @type {HTMLInputElement | null} */ (overlay.querySelector('.st-name-input'));
    if (nameInput && this.childName) nameInput.value = this.childName;

    // Gender
    overlay.querySelectorAll('.st-gender').forEach(opt => {
      opt.setAttribute('data-selected', opt.getAttribute('data-value') === this.gender ? 'true' : 'false');
    });
    this._syncGenderHint(overlay);

    // Binding
    overlay.querySelectorAll('.st-screen--binding .st-binding').forEach(/** @param {Element} card */ (card) => {
      const selected = card.getAttribute('data-variant') === this.bookCoverType ? 'true' : 'false';
      card.setAttribute('data-selected', selected);
      card.setAttribute('aria-pressed', selected);
    });

    // Extras (step 6)
    const dedicationInput = /** @type {HTMLTextAreaElement | null} */ (overlay.querySelector('.st-dedication__input'));
    if (dedicationInput && this.dedicationText) dedicationInput.value = this.dedicationText;
    this._setExtraSelected(overlay, 'dedication', this.dedicationEnabled);
    this._setExtraSelected(overlay, 'matt', this.mattCoverEnabled);

    // Outfit (step 2.5)
    this._setOutfit(overlay, this.outfitChoice);

    this._updateScreen(overlay);
  }

  // ---------------------------------------------------------------------------
  // Reset + teardown
  // ---------------------------------------------------------------------------

  resetState() {
    this.currentStep = 1;
    this.childName = '';
    this.gender = '';
    this.eyeColor = '';
    this.photoConfirmed = false;
    this.uploadedImageUrl = '';
    this.uploadedImageBlob = null;
    this.previewImageUrl = '';
    this.previewDisplayUrl = '';
    this.previewUniqid = '';
    this.bookPreviewImageUrl = '';
    this._detailsSubmittedFor = '';
    this.retryCount = 1;
    this.retryMax = 2;
    // '' | 'up' | 'down' - Bewertung der aktuell gezeigten Vorschau.
    this.characterRating = '';
    this.bookCoverType = STARTALES_BINDING_ENABLED ? 'softcover' : 'hardcover';
    this.coverFinish = 'glaenzend';
    this.outfitChoice = 'book';
    this.dedicationEnabled = false;
    this.dedicationText = '';
    this._dedicationErrorShown = false;
    this.mattCoverEnabled = false;
    this._previewGen = { promise: null, progress: 0, done: false, error: null, startTime: 0 };
    this._bookPreviewGen = { promise: null, progress: 0, done: false, error: null, startTime: 0 };
    this._lastViewedStep = 0;
    // Ein neuer Durchlauf ist ein neues Buch: der Kauf-Zustand des vorherigen
    // darf nicht stehen bleiben, sonst waere "Jetzt kaufen" hier gesperrt.
    this._submittingOrder = false;
    this._restoreSubmitBtn = null;
    this._bookLineAdded = false;
    this._cartSubmitted = false;
    this._skipAddons = false;
    this._pairId = '';
    this._flowCompleted = false;
    // Nach dem Zuruecksetzen gibt es nichts mehr fortzusetzen - die
    // Trigger-Buttons duerfen nicht weiter "Bestellung abschliessen" sagen.
    this._clearFlowProgress();
  }

  // ---------------------------------------------------------------------------
  // Character storage (localStorage + customer sync)
  // ---------------------------------------------------------------------------

  _saveCharacterToStorage() {
    if (!STARTALES_SESSION_SAVE_ENABLED) return;
    const productId = this.getAttribute('data-product-id');
    if (!productId || !this.previewImageUrl || !this.childName || !this.gender) return;
    try {
      const data = {
        v: 1,
        name: this.childName,
        gender: this.gender,
        eyeColor: this.eyeColor,
        previewImageUrl: this.previewImageUrl,
        previewUniqid: this.previewUniqid,
        currentStep: this.currentStep,
        bookCoverType: this.bookCoverType,
        coverFinish: this.coverFinish,
        outfit: this.outfitChoice,
        savedAt: Date.now(),
      };
      const chars = this._loadCharactersFromStorage(productId);
      const idx = chars.findIndex(c => c.previewUniqid === data.previewUniqid);
      if (idx >= 0) {
        chars[idx] = data;
      } else {
        chars.unshift(data);
        if (chars.length > 5) chars.length = 5;
      }
      this._savedChars = chars;
      localStorage.setItem(`startales_chars_${productId}`, JSON.stringify(chars));
      localStorage.setItem(`startales_char_${productId}`, JSON.stringify(data));
      this._syncToCustomer(data);
    } catch {}
  }

  /** @param {string} productId */
  _loadCharacterFromStorage(productId) {
    try {
      const raw = localStorage.getItem(`startales_char_${productId}`);
      if (!raw) return null;
      const d = JSON.parse(raw);
      return d?.v === 1 ? d : null;
    } catch { return null; }
  }

  /** @param {string} productId */
  _loadCharactersFromStorage(productId) {
    try {
      const raw = localStorage.getItem(`startales_chars_${productId}`);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          return arr.filter(/** @param {any} c */ (c) => c?.v === 1 && c.previewImageUrl && c.name && c.gender);
        }
      }
      const single = this._loadCharacterFromStorage(productId);
      return single ? [single] : [];
    } catch { return []; }
  }

  _tryRestoreFromStorage() {
    if (!STARTALES_SESSION_SAVE_ENABLED) return;
    const productId = this.getAttribute('data-product-id');
    if (!productId) return;
    // Ein Charakter mit unpassendem Geschlecht wuerde direkt hinter Schritt 1
    // landen und die Sperre dort umgehen.
    const chars = this._loadCharactersFromStorage(productId)
      .filter(/** @param {any} c */ (c) => !this._genderMismatch(c.gender));
    if (chars.length > 0) {
      this._savedChars = chars;
      this._applyRestoredState(chars[0]);
    } else {
      this._tryLoadFromCustomer();
    }
  }

  /**
   * @param {Record<string, any>} saved
   * @param {{ render?: boolean }} [opts]
   */
  _applyRestoredState(saved, { render = true } = {}) {
    this.childName = saved.name;
    this.gender = saved.gender;
    this.eyeColor = saved.eyeColor || '';
    this.previewImageUrl = saved.previewImageUrl;
    this.previewDisplayUrl = '';
    this.previewUniqid = saved.previewUniqid || '';
    const savedStep = saved.currentStep || 5;
    this.currentStep = savedStep === 6 ? 5 : savedStep;
    this.bookCoverType = saved.bookCoverType || 'softcover';
    // When the cover choice is disabled, force hardcover and skip a restored binding step.
    if (!STARTALES_BINDING_ENABLED) {
      this.bookCoverType = 'hardcover';
      if (this.currentStep === 5) this.currentStep = 7;
    }
    this.coverFinish = saved.coverFinish || 'glaenzend';
    // The restored preview was generated with this outfit - keep them together.
    this.outfitChoice = saved.outfit === 'photo' ? 'photo' : 'book';
    this._previewGen = { promise: null, progress: 0, done: true, error: null, startTime: 0 };
    if (render) this._renderSavedState();
  }

  _renderSavedState() {
    const btnText = /** @type {HTMLElement | null} */ (this.querySelector('.custom-alert-button__text'));
    if (btnText) {
      if (!btnText.dataset['originalText']) btnText.dataset['originalText'] = btnText.textContent || '';
      btnText.textContent = 'Buch konfigurieren';
    }

    const container = /** @type {HTMLElement | null} */ (this.closest('.custom-alert-container'));
    if (!container) return;

    container.querySelectorAll('.st-character-resume').forEach(el => el.remove());

    const heading = /** @type {HTMLElement | null} */ (container.querySelector('.custom-alert-container__heading'));
    if (heading) heading.style.display = 'none';
    const footer = /** @type {HTMLElement | null} */ (container.querySelector('.custom-alert-container__footer'));
    if (footer) footer.style.display = 'none';

    const chars = this._savedChars || [];
    /** @param {string} s */
    const esc = (s) => s.replace(/[<>"&]/g, (/** @type {string} */ c) =>
      (/** @type {Record<string,string>} */ ({'<': '&lt;', '>': '&gt;', '"': '&quot;', '&': '&amp;'})[c] || c));

    const wrap = document.createElement('div');
    wrap.className = 'st-character-resume';

    let html = '';
    chars.forEach((char, i) => {
      html += `
        <div class="st-character-resume__item" data-index="${i}" data-selected="${i === 0 ? 'true' : 'false'}">
          <div class="st-character-resume__thumb">
            <img class="st-character-resume__img" src="${esc(char.previewImageUrl)}" alt="Charakter" />
          </div>
          <span class="st-character-resume__name">${esc(char.name)}</span>
        </div>
      `;
    });

    html += `
      <button type="button" class="st-character-resume__new">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>
        <span>Neuer Charakter</span>
      </button>
    `;

    wrap.innerHTML = html;

    wrap.querySelectorAll('.st-character-resume__item').forEach(item => {
      item.addEventListener('click', () => {
        const idx = parseInt(item.getAttribute('data-index') || '0', 10);
        this._selectCharacter(idx, wrap);
      });
    });

    const newBtn = wrap.querySelector('.st-character-resume__new');
    if (newBtn) {
      newBtn.addEventListener('click', () => this._clearAllAndStartFresh());
    }

    container.insertBefore(wrap, this);
  }

  /**
   * @param {number} idx
   * @param {Element} wrap
   */
  _selectCharacter(idx, wrap) {
    const chars = this._savedChars || [];
    const char = chars[idx];
    if (!char) return;
    this._applyRestoredState(char, { render: false });
    wrap.querySelectorAll('.st-character-resume__item').forEach((item, i) => {
      item.setAttribute('data-selected', i === idx ? 'true' : 'false');
    });
  }

  /** @param {number} idx */
  _removeCharacter(idx) {
    const productId = this.getAttribute('data-product-id');
    const chars = this._savedChars || [];
    chars.splice(idx, 1);
    this._savedChars = chars;
    try {
      if (productId) {
        if (chars.length > 0) {
          localStorage.setItem(`startales_chars_${productId}`, JSON.stringify(chars));
          localStorage.setItem(`startales_char_${productId}`, JSON.stringify(chars[0]));
        } else {
          localStorage.removeItem(`startales_chars_${productId}`);
          localStorage.removeItem(`startales_char_${productId}`);
        }
      }
    } catch {}

    const container = /** @type {HTMLElement | null} */ (this.closest('.custom-alert-container'));
    if (chars.length === 0) {
      this.resetState();
      if (container) {
        container.querySelectorAll('.st-character-resume').forEach(el => el.remove());
        const heading = /** @type {HTMLElement | null} */ (container.querySelector('.custom-alert-container__heading'));
        if (heading) heading.style.display = '';
        const footer = /** @type {HTMLElement | null} */ (container.querySelector('.custom-alert-container__footer'));
        if (footer) footer.style.display = '';
      }
      const btnText = /** @type {HTMLElement | null} */ (this.querySelector('.custom-alert-button__text'));
      if (btnText) btnText.textContent = btnText.dataset['originalText'] || btnText.textContent || '';
    } else {
      const first = chars[0];
      if (first) this._applyRestoredState(first, { render: false });
      this._renderSavedState();
    }
  }

  _clearAllAndStartFresh() {
    this.resetState();
    this.showOverlay();
  }

  /** @param {Record<string, any>} data */
  async _syncToCustomer(data) {
    const customerId = /** @type {any} */ (window).STARTALES_CUSTOMER_ID;
    if (!customerId) return;
    fetch(`${STARTALES_API_BASE}/api/v1/customer-character`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shop': _stGetShop() },
      body: JSON.stringify({ customerId: String(customerId), shop: _stGetShop(), characterData: data }),
      keepalive: true,
    }).catch(() => {});
  }

  async _tryLoadFromCustomer() {
    const customerId = /** @type {any} */ (window).STARTALES_CUSTOMER_ID;
    if (!customerId) return;
    try {
      const res = await fetch(
        `${STARTALES_API_BASE}/api/v1/customer-character?customer_id=${customerId}&shop=${encodeURIComponent(_stGetShop())}`
      );
      if (!res.ok) return;
      const json = await res.json();
      const data = json.characterData;
      if (!data?.previewImageUrl || !data?.name || !data?.gender) return;
      // Kundencharaktere gelten fuer jedes Buch - nicht fuer eins, das nicht passt.
      if (this._genderMismatch(data.gender)) return;
      const productId = this.getAttribute('data-product-id');
      if (productId) {
        try {
          this._savedChars = [data];
          localStorage.setItem(`startales_chars_${productId}`, JSON.stringify([data]));
          localStorage.setItem(`startales_char_${productId}`, JSON.stringify(data));
        } catch {}
      }
      this._applyRestoredState(data);
    } catch {}
  }

  hideOverlay(overlay, scrollToTop = false) {
    this._resumePending = false;
    _stTrack('flow_closed', {
      product_id: this.getAttribute('data-product-id') || undefined,
      step: String(this.currentStep),
      meta: { converted: !!scrollToTop, last_step: this.currentStep },
    });

    if (this._vvResizeHandler && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this._vvResizeHandler);
      this._vvResizeHandler = null;
    }

    // The overlay is removed from the DOM below; the rotation restarts on reopen
    // if generation is still running (see _updateScreens for step 3).
    this._stopWaitReviewRotation();
    this._cancelTypewriter();

    // Unlock scroll — no position:fixed was used so scroll position is already
    // correct; no scrollTo needed (prevents the jump-to-top flash).
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';

    if (scrollToTop) {
      window.scrollTo(0, 0);
    }

    overlay.classList.remove('custom-overlay--wave-open');
    overlay.classList.add('custom-overlay--wave-exit');

    setTimeout(() => {
      overlay.remove();
      if (this._savedChars && this._savedChars.length > 0) {
        this._renderSavedState();
      }
    }, 220);
  }
}

if (!customElements.get('custom-alert-button')) {
  customElements.define('custom-alert-button', CustomAlertButton);
}

// ---------------------------------------------------------------------------
// Meta Web Pixel bridge
// ---------------------------------------------------------------------------
// When the personalization flow is started ("Personalisierung starten"), emit a
// Shopify custom event. The Meta pixel runs inside Shopify's Web Pixel sandbox —
// there is NO global window.fbq in the theme, so we must NOT call fbq() here.
// The only correct path out of the theme is Shopify.analytics.publish(...),
// which the Web Pixel picks up and forwards to Meta.
(function () {
  // Guard: publish at most once per page view, even if the button is clicked
  // multiple times (or re-rendered).
  var published = false;

  // Event delegation on document so it also works if the button is loaded /
  // replaced dynamically. The whole <custom-alert-button> element carries the
  // click handler that opens the overlay, so matching it (or its trigger
  // button) is the accurate "flow started" signal.
  document.addEventListener('click', function (event) {
    if (published) return;

    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var trigger = target.closest('custom-alert-button, .custom-alert-button__trigger');
    if (!trigger) return;

    // Defensive: make sure the Shopify analytics publish API exists before use.
    if (
      !window.Shopify ||
      !window.Shopify.analytics ||
      typeof window.Shopify.analytics.publish !== 'function'
    ) {
      return;
    }

    try {
      window.Shopify.analytics.publish('start_personalization');
      published = true;
    } catch (e) {
      // Never break the personalization flow because of analytics; allow a
      // retry on a later click if publish threw.
    }
  }, true); // capture phase: fires even if an inner handler stops propagation
})();
